<template>
  <div :id="component_id" class="">
    <div class="w-full h-full" :class="!web_socket_try_again && !error && !show_loader?'flex':'hidden'">
      <!-- Left Column -->
      <div ref="leftColumn" class="px-[18px] py-2 overflow-auto flex-shrink-0" :class="left_column_classes" :style="(messenger_container_height>0?`height: ${messenger_container_height}px;`:'') + 'scrollbar-gutter: stable;'">
        <div id="messenger_left_panel" class="h-full">
          <div id="internal_messages" class="flex flex-col items-start justify-start w-full h-full">
            <div v-if="internal_messaging_state !== 'NewTacChat'" id="internal_messages_threads" class="w-full h-full">
              <p v-show="internal_message_errors" class="text-left w-full">
                {{ internal_message_errors }}
              </p>
              <template v-if="!internal_message_errors && !internal_message_processing && !web_socket_processing">
                <message-thread v-for="(messageThread, idx) in internal_messages" :key="`message_thread_${messageThread.threadId}`"
                                class="hover:bg-black/5"
                                :thread-type="messageType==='All' ? (messageThread._displayType || 'Messages') : messageType"
                                :current-user-guid="current_user_guid" :users-by-guid="ent_user_names_by_guid"
                                :message-thread="messageThread" :avatar-colors="internal_message_avatar_colors" @selectThread="openThread(messageThread.threadId)" :unread-messages="unreads_by_thread_id[messageThread.threadId] || null"
                                :last-message="thread_preview_by_thread_id[messageThread.threadId] || messageThread.lastMessagePreview || null"
                                :last-message-at="thread_last_message_at_by_thread_id[messageThread.threadId] || messageThread.lastMessageAt || null" />
                <p v-if="['All','Messages','Channels','Threads','Support'].indexOf(messageType)>=0 && !$FUSarrayHasEntries(internal_messages)">
                  <template v-if="messageType==='All'">
                    <template v-if="filter_channels">{{ $t('messaging.window.empty.activityFound') }}</template>
                    <template v-else>{{ $t('messaging.window.empty.activityYet') }}</template>
                  </template>
                  <template v-if="messageType==='Messages'">
                    <template v-if="filter_channels">{{ $t('messaging.window.empty.dmsFound') }}</template>
                    <template v-else>{{ $t('messaging.window.empty.dmsYet') }}</template>
                  </template>
                  <template v-if="messageType==='Channels'">
                    <template v-if="filter_channels">{{ $t('messaging.window.empty.channelsFound') }}</template>
                    <template v-else>{{ $t('messaging.window.empty.channelsCreated') }}</template>
                  </template>
                  <template v-if="messageType==='Threads'">
                    <template v-if="filter_channels">{{ $t('messaging.window.empty.threadsFound') }}</template>
                    <template v-else>{{ $t('messaging.window.empty.threadsYet') }}</template>
                  </template>
                  <template v-if="messageType==='Support'">
                    <template v-if="filter_channels">{{ $t('messaging.window.empty.supportFound') }}</template>
                    <template v-else>{{ $t('messaging.window.empty.supportYet') }}</template>
                  </template>
                </p>
              </template>
            </div>
            <div v-else id="new_tac_chat_picker" class="w-full h-full text-left flex flex-col">
              <!-- Search input — styled to match the To: field on the New Message screen -->
              <div class="flex items-center mb-3 border border-border-subtle rounded-lg px-3 py-2 transition-[border-color,box-shadow] focus-within:border-[#0855F9] focus-within:shadow-[0_0_0_3px_var(--shadow-focus-input)]">
                <i class="ri-search-line text-sm text-text-subtle mr-2 shrink-0" />
                <input
                  v-model="tac_search_query"
                  type="text"
                  :placeholder="$t('messaging.window.tac.searchPlaceholder')"
                  class="flex-1 border-none outline-none bg-transparent text-sm text-text-default"
                  autocomplete="off"
                />
                <i v-if="tac_search_query" class="ri-close-circle-line text-sm text-text-subtle cursor-pointer shrink-0" @click="tac_search_query = ''" />
              </div>
              <!-- Every team is closed (see tac_picker_disabled): nothing here can be started, so
                   surface the hours + urgent phone line. When only SOME teams are closed this stays
                   hidden and those rows are dimmed individually instead.
                   Styling matches the "Chat Unavailable" alert in the Figma design (error/rose tokens). -->
              <div v-if="tac_picker_disabled" class="mb-3 rounded-lg border border-[var(--border-error)] bg-[var(--background-error)] p-3">
                <p class="text-sm font-semibold text-text-default mb-1">{{ $t('messaging.window.tac.unavailableTitle') }}</p>
                <p class="text-sm text-text-default mb-2">{{ $t('messaging.window.tac.unavailableBody', { hours: tac_support_hours_display }) }}</p>
                <p class="text-sm text-text-default mb-1">{{ $t('messaging.window.tac.urgentPrefix') }}</p>
                <!-- Both TAC lines (US + Canada), matching the bullet pair Rey sends in chat — see
                     tac_support_phone_lines. Both are always shown: nothing tells us whether this user
                     or their store is US or Canadian, so the numbers are labelled rather than guessed
                     between. list-inside keeps the markers aligned inside the alert box. -->
                <ul class="text-sm text-text-default list-disc list-inside">
                  <li v-for="line in tac_support_phone_lines" :key="line.href">
                    <span class="font-semibold">{{ line.label }}:</span>
                    <a :href="line.href" class="text-text-default">{{ line.display }}</a>
                  </li>
                </ul>
              </div>
              <p v-if="tac_teams_error" class="text-danger text-sm mb-2">{{ tac_teams_error }}</p>
              <!-- Starting-chat state: takes over the picker body so team rows aren't clickable
                   during the in-flight POST /messaging/tac/start request. -->
              <div v-if="start_tac_chat_processing" class="text-center w-full pt-8 px-4 tac-starting-overlay">
                <div class="fusion-loader loader-md inline-block mb-3"></div>
                <p class="text-sm text-text-default">
                  <i18n-t v-if="selected_team_display_name" keypath="messaging.window.tac.startingChatWithTeam" scope="global" tag="span">
                    <template #team><span class="font-semibold">{{ selected_team_display_name }}</span></template>
                  </i18n-t>
                  <template v-else>{{ $t('messaging.window.tac.startingChat') }}</template>
                </p>
                <p class="text-xs text-text-subtle mt-2">{{ $t('messaging.window.tac.startingHint') }}</p>
              </div>
              <div v-else-if="tac_teams_loading" class="text-center w-full pt-3">
                <div class="fusion-loader loader-sm inline-block"></div>
              </div>
              <template v-else>
                <!-- Recent section -->
                <div v-if="$FUSarrayHasEntries(filtered_recent_tac_teams)" class="shrink-0">
                  <div class="pt-4 shrink-0">
                    <div class="border-b border-border-separator pb-1">
                      <span class="text-sm text-text-subtle">{{ $t('messaging.window.tac.resultCount', filtered_recent_tac_teams.length) }} &middot; {{ $t('messaging.window.tac.recent') }}</span>
                    </div>
                  </div>
                  <!-- Teams are staffed independently, so availability is per row (tacTeamAvailable):
                       a closed team dims out, ignores clicks and says so, while its neighbours stay
                       selectable. Recent rows come from thread history, so they can also outlive the
                       team itself — a discontinued team dims the same way but says so differently
                       (tacTeamRowBadge), since that one never reopens. -->
                  <div v-for="(option, idx) in filtered_recent_tac_teams" :key="`recent_tac_${option.key}_${option.startedAt || ''}`"
                       class="flex items-center gap-3 px-2"
                       :class="[tacTeamAvailable(option.key) ? 'cursor-pointer hover:bg-black/5' : 'opacity-50 cursor-not-allowed', selected_tac_team_key === option.key ? 'bg-black/5' : '']"
                       @click="tacTeamAvailable(option.key) ? selectTacTeam(option.key) : null">
                    <img :src="tac_chat_logo" alt="" class="w-7 h-7 rounded-full flex-shrink-0" />
                    <div class="flex items-center min-w-0 flex-1 py-4"
                         :class="{'border-b border-border-separator': idx < filtered_recent_tac_teams.length - 1}">
                      <div class="flex flex-col min-w-0 flex-1">
                        <span class="text-sm text-text-default">{{ option.name }}</span>
                        <span v-if="option.subtitle" class="text-xs text-text-subtle truncate">{{ option.subtitle }}</span>
                      </div>
                      <span v-if="!tacTeamAvailable(option.key)" class="text-xs text-text-subtle ml-2 flex-shrink-0">{{ tacTeamRowBadge(option.key) }}</span>
                      <i v-show="selected_tac_team_key === option.key" class="ri-check-line text-text-link ml-2 flex-shrink-0" />
                    </div>
                  </div>
                </div>
                <!-- All section -->
                <div class="flex-1 flex flex-col">
                  <div class="pt-4 shrink-0">
                    <div class="border-b border-border-separator pb-1">
                      <span class="text-sm text-text-subtle">{{ $t('messaging.window.tac.resultCount', filtered_all_tac_teams.length) }}{{ (!tac_search_query_trimmed || filtered_all_tac_teams.length === all_tac_team_options.length) ? ' · ' + $t('common.all') : '' }}</span>
                    </div>
                  </div>
                  <div v-for="(option, idx) in filtered_all_tac_teams" :key="`all_tac_${option.key}`"
                       class="flex items-center gap-3 px-2"
                       :class="[tacTeamAvailable(option.key) ? 'cursor-pointer hover:bg-black/5' : 'opacity-50 cursor-not-allowed', selected_tac_team_key === option.key ? 'bg-black/5' : '']"
                       @click="tacTeamAvailable(option.key) ? selectTacTeam(option.key) : null">
                    <img :src="tac_chat_logo" alt="" class="w-7 h-7 rounded-full flex-shrink-0" />
                    <div class="flex items-center min-w-0 flex-1 py-4"
                         :class="{'border-b border-border-separator': idx < filtered_all_tac_teams.length - 1}">
                      <div class="flex flex-col min-w-0 flex-1">
                        <span class="text-sm text-text-default">{{ option.name }}</span>
                        <span v-if="option.subtitle" class="text-xs text-text-subtle truncate">{{ option.subtitle }}</span>
                      </div>
                      <span v-if="!tacTeamAvailable(option.key)" class="text-xs text-text-subtle ml-2 flex-shrink-0">{{ tacTeamRowBadge(option.key) }}</span>
                      <i v-show="selected_tac_team_key === option.key" class="ri-check-line text-text-link ml-2 flex-shrink-0" />
                    </div>
                  </div>
                  <!-- No results: search returned nothing -->
                  <div v-if="!$FUSarrayHasEntries(filtered_all_tac_teams) && tac_search_query_trimmed"
                       class="w-full flex-1 flex flex-col items-center justify-center px-6 text-center">
                    <p class="text-text-placeholder text-base font-semibold leading-snug mb-2">{{ $t('messaging.search.noResultsFor', { query: tac_search_query_trimmed }) }}</p>
                    <p class="text-text-placeholder text-base leading-snug max-w-xs">{{ $t('messaging.search.noResultsHint') }}</p>
                  </div>
                  <p v-else-if="!$FUSarrayHasEntries(filtered_all_tac_teams)" class="text-sm text-text-subtle mt-3">
                    {{ $t('messaging.window.tac.noSupportTeams') }}
                  </p>
                </div>
              </template>
            </div>
          </div>
          <div v-if="internal_messaging_state !== 'NewTacChat'"
               class="absolute bottom-2.5 right-2.5 cursor-pointer" style="border-radius: var(--Border-radius-rounded-button, 12px); background: var(--button-primary-default); display: flex; height: 40px; padding: var(--gap-16); justify-content: center; align-items: center;"
               :class="['All','Messages','Channels','Threads','Support'].indexOf(messageType)>=0 && !is_read_only?'inline-flex':'invisible'" @click.stop="composeNew()">
            <img :src="quill_pen_icon" alt="" class="w-5 h-5 brightness-0 invert mr-1" />
            <div class="text-center justify-center text-white text-base font-medium leading-5 tracking-normal">{{ $t('messaging.window.compose') }}</div>
          </div>
          <div v-else
               class="h-10 px-4 py-3 rounded-xl outline outline-1 outline-offset-[-1px] outline-brand-outline inline-flex justify-center items-center gap-1 absolute bottom-2.5 right-2.5"
               :class="selected_tac_team_key && !start_tac_chat_processing && selected_tac_team_available ? 'bg-btn-primary hover:bg-btn-primary/80 cursor-pointer' : 'bg-btn-primary/40 cursor-not-allowed'"
               @click="startTacChat()">
            <template v-if="start_tac_chat_processing">
              <div class="fusion-loader loader-sm inline-block mr-1" style="filter: brightness(0) invert(1);"></div>
              <div class="text-center justify-center text-white text-sm font-medium leading-4 tracking-tight">{{ $t('messaging.window.tac.starting') }}</div>
            </template>
            <template v-else>
              <div class="text-center justify-center text-white text-sm font-medium leading-4 tracking-tight">{{ $t('messaging.window.tac.startChat') }}</div>
              <i class="ri-arrow-right-line text-white ml-1" />
            </template>
          </div>
          <div v-show="compose_new" class="compose-menu absolute" @click.stop>
            <div class="compose-menu-item flex items-center gap-4 px-4 py-2 rounded-md cursor-pointer mx-2" @click="composeNew('NewMessage')">
              <i class="ri-chat-3-line text-sm leading-none text-text-default shrink-0" />
              <span class="text-text-default text-sm tracking-[0.1px]">{{ $t('messaging.window.menu.newMessage') }}</span>
            </div>
            <div v-if="has_tac_access" class="compose-menu-item flex items-center gap-4 px-4 py-2 rounded-md cursor-pointer mx-2" @click="composeNew('NewTacChat')">
              <i class="ri-customer-service-2-line text-sm leading-none text-text-default shrink-0" />
              <span class="text-text-default text-sm tracking-[0.1px]">{{ $t('messaging.window.menu.startTacSupport') }}</span>
            </div>
            <div class="compose-menu-item flex items-center gap-4 px-4 py-2 rounded-md cursor-pointer mx-2" @click="composeNew('NewChannel')">
              <i class="ri-team-line text-sm leading-none text-text-default shrink-0" />
              <span class="text-text-default text-sm tracking-[0.1px]">{{ $t('messaging.window.menu.createChannel') }}</span>
            </div>
            <div v-if="is_autovision_platform && is_inventory_vehicle_page" class="my-1 border-t border-border-separator"></div>
            <div v-if="is_autovision_platform && is_inventory_vehicle_page" class="compose-menu-item flex items-center gap-4 px-4 py-2 rounded-md cursor-pointer mx-2" @click="composeNewThread()">
              <i class="ri-hashtag text-sm leading-none text-text-default shrink-0" />
              <div class="flex flex-col min-w-0">
                <span class="text-text-default text-sm tracking-[0.1px] truncate">{{ $t('messaging.window.menu.startVehicleThread') }}</span>
                <span class="compose-thread-subtext">{{ vehicle_thread_preview_name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Right Column -->
      <div ref="rightColumn" class="flex-grow p-0" :class="(active_panel==='messages' || creating_new_thread?'block':'hidden')" style="min-width: 0" :style="messenger_container_height>0?`height: ${messenger_container_height}px`:''">
        <div class="content-container flex-col h-full overflow-hidden" :class="creating_new_thread?'hidden':'flex'">
          <!-- Top Cell -->
          <div ref="messageList" class="middle-cell bg-surface-1" :class="active_thread_is_system_ai?'ai-thread-cell':'thread-cell'">
            <div>
              <div v-if="selected_thread && !messages_error">
                <!-- The Scheduled tasks thread says what it is, how many tasks feed it, and offers to
                     clear its unread results. Above the list rather than inside it: it describes the
                     thread, not any message in it. -->
                <scheduled-thread-header v-if="active_thread_is_scheduled"
                                         :active-count="scheduled_active_task_count"
                                         :total-count="scheduled_total_task_count">
                  <!-- The older results, folded into one row. Inside the header, which already sits above
                       the oldest message on screen — where they belong in an oldest-first list. Absent
                       until there is enough to fold. -->
                  <scheduled-earlier-results v-if="earlier_row"
                                             :count="earlier_row.count"
                                             :from-day="earlier_row.fromDay || ''"
                                             :to-day="earlier_row.toDay || ''"
                                             :expanded="earlier_row.expanded"
                                             :hidden-beyond-peek="earlier_row.hiddenBeyondPeek"
                                             @toggle="earlier_expanded = !earlier_expanded"
                                             @openHistory="openScheduledResultsInbox" />
                </scheduled-thread-header>
                <!-- A morning brief greets its reader and says its findings ran before they arrived.
                     Mutually exclusive with the header above by thread, not by branch — no thread carries
                     both flags. -->
                <brief-thread-header v-if="active_thread_is_brief"
                                     :brief-date="active_thread_brief_date" />
                <!-- Message List -->
                <div class="message-list p-2" :class="[{'typing-fade':is_typing}, {'message-list-ai':active_thread_is_system_ai}]">
                  <template v-for="message in messages_rendered">
                    <div v-if="separator_before_id[message.id]" :key="`scheduled_day_${message.id}`"
                         class="w-full text-center text-text-subtle text-xs py-2">
                      {{ scheduledDayLabel(separator_before_id[message.id]) }}
                    </div>
                    <div v-if="false && last_seen_message && message.threadMsgId===last_seen_message.threadMsgId+1" class="relative flex items-center justify-between border-t border-warning mt-3 pb-3">
                      <span class="inline-block text-xs font-bold leading-none whitespace-nowrap rounded-full px-2 py-1 border border-warning text-warning ml-auto absolute bg-surface-1" style="right: 6px">{{ $t('messaging.window.newMessagesBadge') }}</span>
                    </div>
                    <div :id="`message_tracking_${message.id}`">
                      <message-item :ref="`message_item_${message.id}`" :key="`message_item_${message.id}`" :message="message" :current-user-guid="current_user_guid" :sender-index="sender_id_idx.indexOf(message.senderId)>=0?sender_id_idx.indexOf(message.senderId):null"
                                    :sender-name="ent_users_by_guid[message.senderId]?(ent_users_by_guid[message.senderId].friendlyName || ent_users_by_guid[message.senderId].fullName):$FUSresolveGhostUser(message.senderId).friendlyName" :sender-icon="ent_users_by_guid[message.senderId]?'':$FUSresolveGhostUser(message.senderId).emoji" :thread-is-system-ai="active_thread_is_system_ai" :thread-is-scheduled="active_thread_is_scheduled" :thread-is-brief="active_thread_is_brief"
                                    :streaming-display-buffer="streaming_display_buffers[message.id]" :reply-streaming-display-buffer="(!active_thread_is_system_ai && message_replies_by_id[message.id])?streaming_display_buffers[message_replies_by_id[message.id].id]:null"
                                    :message-reply="message_replies_by_id[message.id]" :reply-published="message_replies_published_by_message_id[message.id]" :can-use-av-rey-assist="can_use_av_rey_assist" :message-cancelled="cancelled_message_ids.indexOf(message.inReplyTo)>=0"
                                    :users-by-guid="ent_user_names_by_guid"                                     :is-streaming-message="streaming_message_id===message.id" :is-verifying="verifying_messages[message.id]" :tool-citations="messages_tool_citations[message.id]" :message-send-ready="message_send_ready" :dealer-roofs="dealer_roofs_with_messenger"
                                    :message-feedback-status="message_feedback_status[message.id]" :reply-feedback-status="message_feedback_status.hasOwnProperty(message_replies_by_id[message.id])?message_feedback_status[message_replies_by_id[message.id]]:null"
                                    :message-comment="message_feedback_comments[message.id] || ''" :is-latest-ai-message="message.id === latest_ai_message_id"
                                    @publishMessage="publishMessage" @replyToMessage="replyToMessage" @sendMessage="sendMessage" @submitFeedback="submitFeedback" @submitComment="submitComment"
                                    @updateStreamingDisplay="updateStreamingMessageDisplay" @streamingComplete="onStreamingComplete($event)" @speakMessage="handleSpeakMessage"
                                    @adjustScheduledTask="adjustScheduledTask" @forkScheduledResult="forkScheduledResult" @openScheduledThread="openScheduledThread"
                                    @chatAboutFinding="seedComposerPrompt" />
                    </div>
                    <template v-if="$FUSarrayHasEntries(status_updates_for_msg[message.id])">
                      <div :ref="`status_updates_container_${message.id}`" class="flex items-center justify-between py-1 cursor-pointer" @click="active_status_updates_for_msg=(active_status_updates_for_msg===message.id?null:message.id)">
                        <i v-if="cancelled_message_ids.indexOf(message.id)>=0" class="ri-forbid-line ri-lg mr-2" />
                        <img v-else-if="message_loader_icon" :src="completed_status_msg_ids[message.id]?message_loaded_icon:message_loader_icon" :alt="$t('messaging.window.messageLoaderAlt')" class="message-loader mr-2" :class="{'animate-spin':!completed_status_msg_ids[message.id]}" style="width: 20px" />
                        <p class="mb-0 mr-auto" :class="{'text-text-subtle':cancelled_message_ids.indexOf(message.id)>=0}">
                          <i v-if="cancelled_message_ids.indexOf(message.id)>=0">{{ $t('messaging.window.cancelledRequest') }}</i>
                          <i v-else>{{ status_updates_for_msg[message.id][status_updates_for_msg[message.id].length-1].content }}</i>
                        </p>
                        <i class="ri-pulse-ai-fill ri-lg ml-2" :class="completed_status_msg_ids[message.id]?'text-brand':'pulse-blink'" />
                      </div>
                      <table class="w-full border-collapse [&_th]:p-1 [&_td]:p-1 border border-border-separator my-1" :class="active_status_updates_for_msg===message.id?'':'hidden'">
                        <thead>
                          <tr>
                            <th class="border-0 bg-border-separator" style="width:70px">{{ $t('messaging.window.debug.time') }}</th>
                            <th class="border-0 bg-border-separator">{{ $t('messaging.window.debug.pulse') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="statusMessage in status_updates_for_msg[message.id].slice().reverse()">
                            <td class="border-0">
                              {{ statusMessage.sentAt?new Date(statusMessage.sentAt).toLocaleTimeString(getActiveLocaleTag(), {hour12: false}):$t('messaging.window.timeNotAvailable') }}
                            </td>
                            <td class="border-0">
                              {{ statusMessage.content }}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                      <div v-if="$FUSarrayHasEntries(tool_executions_for_msg[message.id])" class="mt-1 mb-1">
                        <div class="text-xs font-semibold text-text-subtle mb-1">{{ $t('messaging.window.debug.toolExecutions') }}</div>
                        <div v-for="(toolExec, idx) in tool_executions_for_msg[message.id]" :key="toolExec.id || idx"
                             class="border border-border-separator rounded mb-1 overflow-hidden">
                          <div class="flex items-center justify-between px-2 py-1 cursor-pointer hover:bg-border-separator"
                               @click="expanded_tool_executions[message.id + '_' + idx] = !expanded_tool_executions[message.id + '_' + idx]">
                            <div class="flex items-center gap-2">
                              <i class="ri-checkbox-blank-circle-fill ri-xs"
                                 :class="toolExec.isError ? 'text-red-500' : 'text-green-500'"></i>
                              <span class="text-sm font-medium">{{ toolExec.friendlyName }}</span>
                              <span v-if="toolExec.toolName && toolExec.toolName !== toolExec.friendlyName" class="text-xs text-text-subtle">{{ toolExec.toolName }}</span>
                              <span class="text-xs text-text-subtle">{{ toolExec.durationMs }}ms</span>
                            </div>
                            <i class="ri-arrow-down-s-line ri-lg transition-transform"
                               :class="expanded_tool_executions[message.id + '_' + idx] ? 'rotate-180' : ''"></i>
                          </div>
                          <div v-if="expanded_tool_executions[message.id + '_' + idx]" class="border-t border-border-separator">
                            <div v-if="toolExec.traceId" class="p-2 border-b border-border-separator">
                              <div class="text-xs font-semibold text-text-subtle mb-1">{{ $t('messaging.window.debug.traceId') }}</div>
                              <code class="text-xs break-all">{{ toolExec.traceId }}</code>
                            </div>
                            <div v-if="toolExec.arguments" class="p-2">
                              <div class="text-xs font-semibold text-text-subtle mb-1">{{ $t('messaging.window.debug.arguments') }}</div>
                              <pre class="text-xs overflow-auto p-2 rounded max-h-40" style="background: var(--background-surface, #f5f5f5); color: var(--text-primary, #333);"><code>{{ formatToolJson(toolExec.arguments) }}</code></pre>
                            </div>
                            <div v-if="toolExec.result" class="p-2 border-t border-border-separator">
                              <div class="text-xs font-semibold text-text-subtle mb-1">{{ $t('messaging.window.debug.result') }}</div>
                              <pre class="text-xs overflow-auto p-2 rounded max-h-60" style="background: var(--background-surface, #f5f5f5); color: var(--text-primary, #333);"><code>{{ formatToolJson(toolExec.result) }}</code></pre>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </template>
                  <p id="message-list-end" class="mb-0">
                    {{ $FUSarrayHasEntries(messages) || messages_processing || loading_connection ?'':$t('messaging.window.empty.noConversations') }}
                  </p>
                </div>
              </div>
              <p v-if="messages_error" class="text-center text-danger my-5">{{ messages_error }}</p>
            </div>
          </div>
          <!-- Bottom Cell -->
          <!-- The Scheduled tasks thread has no composer. It carries result cards only: nothing posted
               there is answered, and a working input would invite a question that goes nowhere — the
               owner would type, send, and watch it vanish into a thread Rey does not read.
               To ask something, they open a topic; to change what a task says, the adjust control on
               the card itself already anchors the next message to that task.
               The WHOLE cell goes, not just the input: the scroll-down button inside it is positioned
               against this cell's own top edge, so leaving an empty cell behind would leave the button
               floating over the last card.

               pending_feedback_task_id is the second exception, for the same reason the first one is:
               the adjust control lives ON a result card, so the only place it can be clicked is the one
               thread this cell was hidden in. Without it the click did nothing visible — no banner, no
               input, and $refs.messengerInput not there to focus — while still anchoring the next
               message to that task, so the anchor left with the user and quietly turned their next
               question in some other topic into feedback. The NPM-13 banner below has always been
               written to render here; this is what lets it. -->
          <!-- On the Rey chat the cell's top edge carries the same faint rule as the navbar's bottom
               edge (FusionBody), so the conversation reads as scrolling beneath both. pt-2 keeps the
               rule off the composer's own outline. Gated on page type rather than on the thread being
               Rey's, to match the navbar rule: this window also shows in InternalMessaging. -->
          <div v-if="selected_thread && !messages_error && (!active_thread_is_scheduled || scheduled_answer_pending || pending_feedback_task_id)" class="bottom-cell px-2 relative"
               :class="{'pt-2 border-t border-border-separator-subtle':page_type === 'Spark'}">
            <p v-show="show_scroll_down_btn" class="mb-0 absolute text-center" style="top: -38px; left: 50%; transform: translate(-50%, 0); ">
              <i class="ri-arrow-down-circle-line text-text-default/70 ri-2x cursor-pointer z-10" @click="scrollToLastMessageIfClose(true)" />
            </p>
            <!-- NPM-13: what the next message will do, stated before it is sent. Without this the person
                 clicks "adjust", types, and has no way to know whether they are talking to Rey or filing
                 feedback — and the two produce very different results. -->
            <div v-if="pending_feedback_task_id" class="flex items-center gap-2 mb-2 px-3 py-2 text-sm bg-brand-subtle border border-brand-outline rounded-md">
              <i class="ri-edit-line" />
              <span class="flex-1 min-w-0">{{ $t('messaging.scheduledResult.adjusting') }}</span>
              <button class="text-xs px-2 py-1 rounded text-text-subtle" @click="cancelScheduledAdjustment">
                {{ $t('messaging.scheduledResult.cancelAdjustment') }}
              </button>
            </div>
            <!-- What this box is FOR, when it appears in the Scheduled tasks thread: answering the
                 question above it, not asking a new one. The spec calls for the composer there to be
                 "scoped to the result above it and labeled for answering rather than for open enquiry",
                 and without saying so it looks like the ordinary input that Phase 3 removed. -->
            <div v-if="scheduled_answer_pending" class="flex items-center gap-2 mb-2 px-3 py-2 text-sm bg-brand-subtle border border-brand-outline rounded-md">
              <i class="ri-question-answer-line" />
              <span class="flex-1 min-w-0">{{ $t('messaging.scheduledAnswer.answering') }}</span>
            </div>
            <!-- NPM-17: the offer to automate a repeated question, placed where the questions are asked.
                 It only makes sense on the Rey thread, and it renders nothing unless the API has one.
                 Keyed on the store so switching rooftops re-asks for that rooftop's offer. -->
            <schedule-suggestion-chip v-if="active_thread_is_system_ai" :key="`${component_id}_schedule_suggestion_${context_cif}`"
                                      :dealer-id="context_cif || ''" class="mb-2" />
            <messenger-input ref="messengerInput" :key="`${component_id}_message_input`" :message-send-ready="message_send_ready" :users-info="users_info" :typing-users="users_typing" :thread-id="selected_thread" :participant-guids="active_thread_participant_ids" :selected-dealer-cif="context_cif" :selected-group-id="context_group ? context_group.id : null"
                             :input-config="input_config" :message-user="reply_to_message" :clear-messenger-input="clear_messenger_input" :emoji-index-obj="emoji_index" :processing-message="input_placeholder" :is-system-ai="active_thread_is_system_ai" :message-streaming="ai_response_pending" :topic-name="active_thread_topic_name"
                             @commitInput="sendMessage" @userTyping="handleTyping" @inputFocus="handleInputFocus" @updateContext="updateDealerContext" @updateGroupContext="updateGroupContext" @resetCurrentThread="resetCurrentThread()"
                             @voiceBargeIn="handleVoiceBargeIn" @voiceTranscribed="handleVoiceTranscribed" @ttsEnded="handleTtsEnded" @cancelMessage="cancelMessage()" />
          </div>
        </div>
        <create-thread v-if="creating_new_thread" :key="`${component_key}_create_new_thread`" class="content-container flex flex-col h-full" :user-access-info="user_access_info" :mode="create_thread_mode"
                       :users-info="users_info" :can-use-av-rey-assist="can_use_av_rey_assist" :emoji-index-obj="emoji_index" :is-loading="is_creating_thread" :error-message="thread_creation_error"
                       @createThread="createThread" @addToChannel="addParticipantsToChannel" @dismissError="clearThreadCreationError" />
      </div>
    </div>
    <div v-show="show_loader" class="text-center w-full pt-5">
      <div class="fusion-loader loader-lg inline-block"></div>
    </div>
    <p v-if="!loading_connection && (error || web_socket_try_again)" class="my-5 text-center">
      <span v-show="error" class="text-danger">{{ error }}</span>
      <span v-show="!error">{{ $t('messaging.window.notConnected') }}</span>
      <br /><br />
      <span v-show="web_socket_try_again" class="text-text-link cursor-pointer" @click="webSocketTryAgain()">{{ $t('messaging.window.tryAgain') }}</span>
    </p>

    <!-- Recent-cap confirmation, raised when the FIRST SEND of a draft would create a conversation
         Recent has no room for. It lives here rather than in the conversation list because this is
         where a conversation is actually created — the list is not even mounted while the user is
         typing in the chat. Same dialog component the list uses for the unpin case. -->
    <rey-conversation-cap-dialog :is-open="Boolean(pending_cap_create_victim)"
                                 mode="create"
                                 :victim-title="cap_create_victim_title"
                                 :processing="is_resolving_cap_create"
                                 :error="cap_create_error"
                                 @confirm="confirmCapCreate"
                                 @cancel="cancelCapCreate" />
  </div>
</template>

<style scoped>
.middle-cell {
  flex: 1;
  overflow-y: auto;
}

.message-list :deep(.emoji-message) {
  width: 24px;
  height: 24px;
  display: inline-block;
  background-size: 6100%;
  background-image: url('@images/emojis_32.png');
  vertical-align: bottom;
}

.compose-menu {
  bottom: 60px;
  right: 8px;
  background-color: var(--background-layer-01);
  box-shadow: 0px 2px 4px -2px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  border: 1px solid var(--border-separator);
  padding: 8px 0;
  min-width: 244px;
  max-width: 308px;
  z-index: 20;
}
.compose-menu-item:hover {
  background-color: var(--background-layer-02);
}
.compose-thread-subtext {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text-label);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 133%;
  letter-spacing: 0.16px;
  font-feature-settings: 'liga' off, 'clig' off;
}

.tac-team-row:hover {
  background-color: var(--background-layer-02);
}

.tac-team-row-selected {
  background-color: var(--background-layer-02);
  outline: 1px solid var(--border-default);
}
</style>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import ImageContainer from '@components/UI/ImageContainer.vue';
import MessengerInput from '@components/Messaging/MessengerInput.vue';
import ScheduledThreadHeader from '@components/Scheduled/ScheduledThreadHeader.vue';
import BriefThreadHeader from '@components/Scheduled/BriefThreadHeader.vue';
import ScheduledEarlierResults from '@components/Scheduled/ScheduledEarlierResults.vue';
import { buildScheduledRows } from '../../utils/scheduledThreadRows';
import ScheduleSuggestionChip from '@components/Scheduled/ScheduleSuggestionChip.vue';
import CreateThread from '@components/Messaging/CreateThread.vue';
import MessengerAvatar from '@components/Messaging/MessengerAvatar.vue';
import MessageThread from "@components/Messaging/MessageThread.vue";
import MessageItem from "@components/Messaging/MessageItem.vue";
import ToolCitations from "@components/UI/ToolCitations.vue";
import ReyConversationCapDialog from '@components/Rey/ReyConversationCapDialog.vue';
import { oldestUnpinnedRow, wouldExceedRecentCap } from '../../utils/reyConversationCap';
import { ALL_ROOFTOPS_GROUP_ID } from '../../utils/dealerGroups';
import vueFilePond from 'vue-filepond';
import { Picker, EmojiIndex } from "emoji-mart-vue-fast";
import data from "emoji-mart-vue-fast/data/all.json";
const emojiIndex = new EmojiIndex(data);
import "emoji-mart-vue-fast/css/emoji-mart.css";
import emojiSpritesheet from "@images/emojis_32.png";
import MessageLoaderIconBlue from '@images/loader_blue.svg';
import MessageLoaderIconGray from '@images/loader_gray.svg';
import MessageLoadedIcon from '@images/message_loaded.svg';
import QuillPenIcon from '@images/quill_pen_line.svg';
import TacChatLogo from '@images/tac_chat_logo.svg';
import ContactsSearchEmpty from '@images/contacts_search_empty.svg';
import { readTable, buildTablesWorkbook } from '../../utils/tableExport';
import { getActiveLocaleTag } from '../../i18n';
import 'filepond/dist/filepond.min.css';
const FilePond = vueFilePond();

export default {
  props: {
    setThreadId: {
      type: String,
      required: false,
      default: null,
    },
    setNewMessage: {
      type: String,
      required: false,
      default: null,
    },
    setVoiceInitiated: {
      type: Boolean,
      required: false,
      default: false,
    },
    setConfiguration: {
      type: Object,
      required: false,
      default: null,
    },
    messageType: {
      type: String,
      required: false,
      default: null,
    },
  },
  async created() {
    this.context_cif = this.current_cif || null;
    // A fresh Spark panel has no draft — a draft is this component's own selected_thread — so a true
    // left in the store is a predecessor's that was unmounted mid-draft (an auth round-trip, the
    // welcome splash). Left set, it kept getThreads from landing and turned this panel's first send
    // into a new conversation while the user was looking at a different one.
    if (this.page_type === 'Spark')
      this.spark_draft_active = false;
    if (this.setThreadId === this.placeholder_thread_id) {
      this.active_panel = 'messages';
      this.messages_processing = true;
    }
    else {
      if (this.setThreadId) {
        this.active_panel = 'messages';
        this.messages_processing = Boolean(this.setThreadId);
      }
      else
        this.active_panel = null;

      this.loading = true;
      this.ensureSocketConnected();
      this.set_thread_id = this.setThreadId || null;
      this.set_new_message = this.setNewMessage || null;
      await this.fetchUsersForPopup();
      this.send_last_message_seen_debounced = this.$FUSdebounce(this.sendLastMessageSeen, 750);
      this.loading = false;
    }
  },
  mounted() {
    if (this.setThreadId !== this.placeholder_thread_id) {
      const self = this;
      this.waiting_for_web_socket = true;
      this.startup_fetch_timer_id = setTimeout(() => {
        self.startup_fetch_timer_id = null;
        self.fetchThreadsWhenReady(0, self.getThreads);
      }, 500);
      this.resize_observer = new ResizeObserver(this.updateMessagesColumnHeight);
      this.resize_observer.observe(this.$el);
      //"Create a watcher that calls the messagesIntersected function when messages scroll into view.
      this.message_intersection_observer = new IntersectionObserver(this.messagesIntersected, {
        root: this.$refs.messageList,
        rootMargin: '0px',
        threshold: .75,
      });
      window.addEventListener("resize", this.updateMessagesColumnHeight);
      window.addEventListener("visibilitychange", this.visibilityChanged);
      window.event_bus.$on('CONTEXT-FOR-MESSAGE-RESPONSE', this.handleContextForMessage);
      window.event_bus.$on('WS-MESSAGE', this.handleWebSocketMessage);
      window.event_bus.$on('WS-CONNECTION-ERROR', this.handleConnectionError);
      window.event_bus.$on('WS-RECONNECTED', this.handleSocketReconnected);
      window.event_bus.$on('CLOSE-OVERLAYS', this.closeComposeMenu);
      // SCOPING RULE for the MESSENGER-* handlers below: these are global bus events, and in the
      // toolbar layout MessengerWindow is mounted TWICE (the Spark/AI body and the InternalMessaging
      // body, both kept alive by v-show), so every one of them fires on BOTH instances. They all act
      // on `selected_thread`, which on the Spark instance is the user's Rey AI thread — so an
      // unguarded handler applies an Internal Messaging action to the AI conversation. Each handler
      // therefore returns early unless page_type === 'InternalMessaging'. Only the emitters live on
      // Internal Messaging surfaces (Navbar's more-options menu, ChatDetails, EnterpriseContactDetails),
      // so the Spark instance has no legitimate reason to act on any of them.
      window.event_bus.$on('MESSENGER-DELETE-THREAD', this.deleteThread);
      window.event_bus.$on('MESSENGER-LEAVE-THREAD', this.leaveThread);
      window.event_bus.$on('MESSENGER-REMOVE-PARTICIPANT', this.removeParticipant);
      window.event_bus.$on('MESSENGER-OPEN-THREAD', this.openThreadFromEvent);
      window.event_bus.$on('MESSENGER-RESTORE-CHAT-DETAILS', this.restoreChatDetails);
      window.event_bus.$on('MESSENGER-RESET-AI-THREAD', this.resetAiThread);
      // Spark-scoped counterparts of the MESSENGER-* events above. Same dual-mount problem,
      // opposite guard: both handlers return early unless page_type === 'Spark', because the
      // Rey conversation list is a Spark surface and the InternalMessaging instance acting on
      // these would swap ITS open thread for one of the user's Rey conversations.
      window.event_bus.$on('SPARK-NEW-CONVERSATION', this.startDraftConversation);
      window.event_bus.$on('SPARK-OPEN-CONVERSATION', this.openSparkConversation);
      window.event_bus.$on('SPARK-CONVERSATION-DELETED', this.handleSparkConversationDeleted);
      // Emitted by OptionsMenu after a successful settings PATCH that changed Ux.Personality.
      // Same double-mount caveat as the MESSENGER-* handlers above, but the inverse guard: this one
      // belongs to the AI conversation, so it returns early unless the thread is the Rey thread.
      window.event_bus.$on('REY-PERSONALITY-CHANGED', this.handleReyPersonalityChanged);
      this.updateMessagesColumnHeight();
      //preload emoji images
      const img = new Image();
      img.src = emojiSpritesheet;
    }
    if (this.$refs.messageList)
      this.$refs.messageList.addEventListener('scroll', this.messageListScrollEventHandler);
    this.$el.addEventListener('click', this.handleCopyCodeClick);
    this.$el.addEventListener('click', this.handleTableDownloadClick);
    this.$el.addEventListener('keydown', this.handleTableDownloadKeydown);
    this.$el.addEventListener('keyup', this.handleTableDownloadKeyup);
    this.startSupportClock();
  },
  beforeUnmount() {
    // First, so every await already in flight sees it on resume — see is_unmounted.
    this.is_unmounted = true;
    if (this.startup_fetch_timer_id) {
      clearTimeout(this.startup_fetch_timer_id);
      this.startup_fetch_timer_id = null;
    }
    // WS-5: end active AI conversation on component teardown (store guard handles no-op when not active)
    this.mainStore.closeAiChat('user_closed');

    // Note: the WebSocket is a store-owned singleton (ConnectSocket/CleanupSocket in main.js) and is
    // intentionally NOT torn down here — it is shared across mounts/flows and reconnects on its own.
    this.stopSupportClock();
    this.resize_observer?.disconnect();
    this.message_intersection_observer?.disconnect();
    window.removeEventListener("visibilitychange", this.visibilityChanged);
    document.removeEventListener('click', this.closeComposeMenu);
    window.event_bus.$off('CONTEXT-FOR-MESSAGE-RESPONSE', this.handleContextForMessage);
    window.event_bus.$off('WS-MESSAGE', this.handleWebSocketMessage);
    window.event_bus.$off('WS-CONNECTION-ERROR', this.handleConnectionError);
    window.event_bus.$off('WS-RECONNECTED', this.handleSocketReconnected);
    window.event_bus.$off('CLOSE-OVERLAYS', this.closeComposeMenu);
    window.event_bus.$off('MESSENGER-DELETE-THREAD', this.deleteThread);
    window.event_bus.$off('MESSENGER-LEAVE-THREAD', this.leaveThread);
    window.event_bus.$off('MESSENGER-REMOVE-PARTICIPANT', this.removeParticipant);
    window.event_bus.$off('MESSENGER-OPEN-THREAD', this.openThreadFromEvent);
    window.event_bus.$off('MESSENGER-RESTORE-CHAT-DETAILS', this.restoreChatDetails);
    window.event_bus.$off('MESSENGER-RESET-AI-THREAD', this.resetAiThread);
    window.event_bus.$off('SPARK-NEW-CONVERSATION', this.startDraftConversation);
    window.event_bus.$off('SPARK-OPEN-CONVERSATION', this.openSparkConversation);
    window.event_bus.$off('SPARK-CONVERSATION-DELETED', this.handleSparkConversationDeleted);
    window.event_bus.$off('REY-PERSONALITY-CHANGED', this.handleReyPersonalityChanged);

    // Cleanup streaming buffers
    Object.keys(this.streaming_display_buffers).forEach(messageId => {
      this.handleStreamingComplete(messageId);
    });
    
    if (this.$refs.messageList)
      this.$refs.messageList.removeEventListener('scroll', this.messageListScrollEventHandler);
    if (this.$el) {
      this.$el.removeEventListener('click', this.handleCopyCodeClick);
      this.$el.removeEventListener('click', this.handleTableDownloadClick);
      this.$el.removeEventListener('keydown', this.handleTableDownloadKeydown);
      this.$el.removeEventListener('keyup', this.handleTableDownloadKeyup);
    }
  },
  components: {
    'emoji-picker': Picker,
    'image-container': ImageContainer,
    'messenger-input': MessengerInput,
    'scheduled-thread-header': ScheduledThreadHeader,
    'brief-thread-header': BriefThreadHeader,
    'scheduled-earlier-results': ScheduledEarlierResults,
    'schedule-suggestion-chip': ScheduleSuggestionChip,
    'create-thread': CreateThread,
    'messenger-avatar': MessengerAvatar,
    'message-thread': MessageThread,
    'message-item': MessageItem,
    'tool-citations': ToolCitations,
    'rey-conversation-cap-dialog': ReyConversationCapDialog,
    'file-pond': FilePond,
  },
  data() {
    return {
      selected_thread: null,
      selected_thread_users_list: [],
      is_removing_participant: false,
      is_adding_participants: false,
      active_status_updates_for_msg: null,
      status_updates_for_msg: {},
      // Turns whose status row is finished (check circle, solid pulse), keyed like status_updates_for_msg.
      // Reactive so the finished look survives the row being re-rendered after a thread switch.
      completed_status_msg_ids: {},
      tool_executions_for_msg: {},
      expanded_tool_executions: {},
      ai_response_pending: false,
      cancelled_message_ids: [],
      last_seen_message: null,
      typing_already_sent: false,
      typing_clear_timeout_id: null,
      clear_messenger_input: 0,
      typing_clear_timeout_timer: 5000,
      messenger_container_height: 0,
      loading: false,
      waiting_for_web_socket: false,
      messages_processing: false,
      error: '',
      channel_threads_error: '',
      user_threads_error: '',
      object_threads_error: '',
      messages_error: '',
      thread_creation_error: '',
      active_panel: null,
      set_thread_id: null,
      set_new_message: null,
      placeholder_thread_id: '00000000-0000-0000-0000-000000000000',
      // Which New Topic draft is open, bumped by every startDraftConversation. Every draft shares the
      // placeholder id and spark_draft_active, so without this "the user started ANOTHER New Topic
      // while the first one's question was being created" is indistinguishable from "the user is
      // still on it" — and materializeDraftConversation would pull them out of the blank second draft
      // into the first one's conversation.
      draft_generation: 0,
      // The draft_generation of the New Topic whose first send is creating its conversation right
      // now, or null. Holds the composer shut for that New Topic only (draft_send_pending) — see
      // sendMessage for why message_sending cannot do this job.
      sending_draft_generation: null,
      // The thread a New Topic draft has just become, set by materializeDraftConversation right before
      // it switches the view there. The selected_thread watcher keeps the dealer scope picked on the
      // draft for that one switch, instead of resetting it as it does for a move to another
      // conversation. Cleared by the watcher's next run, whichever thread that run is for.
      keep_scope_for_thread_id: null,
      // Guards for the deleted-Rey-thread self-heal (see healSystemAiThread). heal_in_flight keeps
      // concurrent triggers (join detection + a server push) to a single re-resolve;
      // unhealable_thread_ids records ids the server gave a definitive bad answer for, so a
      // backend that keeps handing back a dead thread can't spin the heal.
      heal_in_flight: false,
      unhealable_thread_ids: [],
      // The thread the in-flight heal is FOR. heal_in_flight alone collapsed every heal request into
      // the running one — including a request for a DIFFERENT dead thread, which was then told "a
      // heal owns this" by a heal that was never going to recover it. Scoping the collapse to one
      // thread lets that request start a heal of its own instead. See abandonStaleHeal.
      heal_thread_id: null,
      reply_to_message: null,
      // NPM-13: set while the next message is an adjustment to a scheduled task rather than a question.
      // Cleared as soon as it is sent, so it can never leak onto an unrelated later message.
      pending_feedback_task_id: null,
      // Guards the fork against a double click. It creates a real conversation, and the Recent cap
      // makes an accidental second one costly rather than merely untidy — the user would be asked to
      // give up an old conversation to make room for a duplicate of one they just made.
      forking_result: false,
      // Whether the Scheduled tasks thread's folded older results are open. Reset when the open
      // thread changes, so opening a different conversation and coming back does not find it
      // half-unfolded from last time.
      earlier_expanded: false,
      // The thread the scheduled header's task list was fetched for. Its counts are null, not zero,
      // until the tasks have been fetched — a zero would read as "nothing is running" during the gap.
      // A thread id rather than a flag: a same-device user switch empties the shared list, and a flag
      // left set would show the incoming user "0 of 0" until their own fetch landed.
      scheduled_tasks_loaded_for: null,
      messages: [],
      users_typing: [],
      new_channel_name: "",
      new_channel_participants: [],
      new_group_participants: [],
      // True while the New Thread participant picker is open for a vehicle thread. Routes the picker
      // into 'Threads' mode and the confirm handler into createVehicleThread (private, invite-only).
      pending_vehicle_thread: false,
      // Snapshot of the inventory-vehicle page context (wingman_init) captured when the user opened
      // the picker, so creation uses the page they started from.
      pending_vehicle_thread_context: null,
      message: '',
      create_thread_message: '',
      message_sending: false,
      dm_users_selected: [],
      resize_observer: null,
      message_intersection_observer: null,
      send_last_message_seen_debounced: null,
      // Debounced list refetch for a message on a thread this client has never seen — a morning brief
      // created by its own delivery run. Built on first use by refreshObjectThreadsSoon().
      refresh_object_threads_debounced: null,
      // The deferred first getThreads that mounted() schedules, kept so beforeUnmount can cancel it.
      startup_fetch_timer_id: null,
      // Set in beforeUnmount. A panel can be unmounted while its startup is still in flight — the
      // welcome splash replaces the chat window the moment the gate resolves, usually before this
      // instance has landed anywhere — and without a way to tell, that orphan went on to run the
      // landing rules against the store the replacement panel then reads.
      is_unmounted: false,
      thread_id_to_last_seen_thread_msg_id: {},
      thread_preview_by_thread_id: {},
      thread_last_message_at_by_thread_id: {},
      message_feedback_status: {},
      // Parallel map to message_feedback_status. Keyed by messageId, value is the user's
      // free-text comment for a thumbs-down vote. Stored separately so the existing prop
      // contract on MessageItem (messageFeedbackStatus stays Boolean|String) isn't disturbed.
      message_feedback_comments: {},
      streaming_display_buffers: {},
      messages_tool_citations: {},
      streaming_message_id: null,
      verifying_messages: {},
      preamble_spinner_timeout: null,
      show_scroll_down_btn: false,
      compose_new: false,
      input_placeholder: null,
      context_cif: null,
      // Selected dealer group ({ id, name }) or null. Supersedes context_cif in message_context;
      // '__ALL__' is the all-rooftops virtual group. Cleared when a single dealer is picked or
      // the host page switches dealers (current_cif watcher).
      context_group: null,
      tac_teams_list: [],
      tac_teams_loading: false,
      tac_teams_error: '',
      tac_search_query: '',
      tac_chat_logo: TacChatLogo,
      contacts_search_empty_icon: ContactsSearchEmpty,
      selected_tac_team_key: null,
      start_tac_chat_processing: false,
      // TAC Support hours + phone copy. All three come from the GetTacTeams response now
      // (supportHours / supportPhoneUs / supportPhoneCanada, sourced from CarlStreet.Core.FusionConstants
      // and TacAvailabilityService) so the banner states the window the SERVER gated on and can't
      // contradict it. The literals below are only the pre-response fallback — first paint, or a server
      // that predates these fields.
      //
      // Both numbers are shown with country labels: nothing in the session tells us whether the user or
      // their store is US or Canadian, so guessing would send half our dealers to the wrong country.
      //
      // Format mirrors FusionConstants ("(800) NNN-NNNN"). Display only — tac_support_phone_lines
      // strips to digits for the tel: href, so never interpolate these into a URL directly.
      //
      // now_tick is bumped by a 60s interval; it now only drives the legacy client-side window used when
      // a team's `available` flag is missing (see tacTeamAvailable).
      tac_support_phone: '(800) 767-0080',
      tac_support_phone_canada: '(800) 661-8600',
      tac_support_hours_server: '',
      // Set from the GetTacTeams response (supportBypassActive): true when the backend is bypassing
      // the support-hours restriction for this user (persisted AutoWithDebug admin flag + non-prod).
      // Keeps the picker enabled for staging testers. NOT tied to the #WITH-DEBUG message command.
      tac_support_bypass_active: false,
      now_tick: Date.now(),
      support_clock_interval: null,
      // Voice conversation state
      voice_mode_active: false,  // True when user initiated via voice
      last_voice_message_id: null,  // Track which message was from voice
      preamble_tts_started: false,  // Track if we've started preamble TTS for current response
      sent_last_message_from_this_tab: false,  // True when this tab sent the most recent message (prevents cross-tab TTS)
      last_sent_thread_msg_id: null,  // threadMsgId of the last message sent from this tab
      pending_context_request: null,  // { resolve, reject, timeout }
      thread_context_timeout_ms: 2500,  // Timeout in milliseconds for context request
      // WS-5 analytics: conversation lifecycle is tracked in the store (openAiChat / closeAiChat / incrementAiChatMessageCount)
      // Recent-cap confirmation on the CREATE path — see materializeDraftConversation. The victim is
      // the conversation that would be deleted to make room, or null when nothing is being asked.
      pending_cap_create_victim: null,
      is_resolving_cap_create: false,
      cap_create_error: false,
      // resolve() of the promise the send is parked on while that dialog is up. Held in data rather
      // than closed over so both button handlers can reach it.
      cap_create_decision: null,
    };
  },
  computed: {
    ...mapStores(useMainStore),

    // [NPM-71] Busy state for the navbar's three thread actions. The state lives in the
    // store so Navbar can render it, but the transitions stay here: this component knows
    // when the whole user-visible operation is done, while the store action only knows
    // when its HTTP call resolved. Read-write computeds because the project forbids
    // assigning to this.mainStore.X directly.
    is_resetting_ai_thread: {
      get() { return this.mainStore.is_resetting_ai_thread; },
      set(value) { this.mainStore.is_resetting_ai_thread = value; }
    },
    // Named from the snapshot the dialog was opened with, not looked up again: the victim row is
    // dropped from the cached list on success while the dialog is still closing.
    cap_create_victim_title() {
      return this.pending_cap_create_victim ? this.sparkConversationLabel(this.pending_cap_create_victim) : '';
    },
    /**
     * The Rey thread the HOST handed this panel as its default — not a conversation anyone asked for.
     *
     * Both hosts resolve it the same way, from <c>GET threads/system/Fusion/ai</c>: FusionToolbar
     * builds spark_body_config with it, and the WingCommander extension pins the same id into the Rey
     * blade iframe's URL (/app/fusion/spark?thread_id=). So it arrives as setThreadId looking exactly
     * like a deep link, and nothing on it tells the two apart — which matters, because "the host had
     * to name some thread" must not outrank "put me back where I was".
     *
     * So the host says which it is. On the toolbar entry the pin is FusionToolbar's own, and on a
     * blade whose host asked for Rey conversations (host_supports_rey_conversations — the extension's
     * ?rey_conversations=1) the ?thread_id= is only ever that landing resolve; either way it is the
     * default, and it is the pin itself. Anywhere else — a direct visit to the Spark route, or an
     * extension that predates the flag and still re-pins its id over any switch made here — a
     * ?thread_id= is honoured as the deep link it is, so there is no default.
     */
    host_default_thread_id() {
      if (this.host_supports_rey_conversations)
        return this.setThreadId || this.ai_thread_obj?.id || null;
      return this.ai_thread_obj?.id || null;
    },
    host_supports_rey_conversations() {
      return this.mainStore.host_supports_rey_conversations;
    },
    ai_thread_obj() {
      return this.mainStore.ai_thread_obj;
    },
    // True when this is the user's first open of Rey today — the day marker the welcome gate reads,
    // recorded where MarkWelcomeShown does not clear it. Drives which branch landOnStartupThread
    // takes. See the store's own note for why welcome_eligible_today cannot answer this.
    rey_first_open_today() {
      return this.mainStore.rey_first_open_today;
    },
    // Whether this page has already placed the user in Rey once — see the store's note. Written by
    // getThreads when it makes that first decision; read by landOnStartupThread.
    rey_startup_landing_done: {
      get() { return this.mainStore.rey_startup_landing_done; },
      set(value) { this.mainStore.rey_startup_landing_done = value; }
    },
    // The Spark instance's own store mirror of selected_thread, for the Rey conversation list.
    spark_active_thread_id: {
      get() { return this.mainStore.spark_active_thread_id; },
      set(value) { this.mainStore.spark_active_thread_id = value; }
    },
    // A conversation another surface asked this panel to open before it could — see
    // RequestSparkConversationOpen in the store.
    spark_pending_open_thread_id: {
      get() { return this.mainStore.spark_pending_open_thread_id; },
      set(value) { this.mainStore.spark_pending_open_thread_id = value; }
    },
    // True while the open Spark conversation exists only on this client — the "+" control created
    // it and the user has not asked anything yet, so there is no thread in Cosmos to send to.
    // Cleared by materializeDraftConversation on the first send.
    spark_draft_active: {
      get() { return this.mainStore.spark_draft_active; },
      set(value) { this.mainStore.spark_draft_active = value; }
    },
    spark_conversations: {
      get() { return this.mainStore.spark_conversations; },
      set(value) { this.mainStore.spark_conversations = value; }
    },
    spark_view: {
      get() { return this.mainStore.spark_view; },
      set(value) { this.mainStore.spark_view = value; }
    },
    // True while this Rey panel's conversation list covers the chat. The chat stays mounted behind the
    // list (v-show in Views/Messaging.vue), so selected_thread still names a thread nobody is looking at:
    // a message arriving there is unread, not seen.
    chat_covered_by_conversation_list() {
      return this.page_type === 'Spark' && this.spark_view === 'Conversations';
    },
    is_deleting_thread: {
      get() { return this.mainStore.is_deleting_thread; },
      set(value) { this.mainStore.is_deleting_thread = value; }
    },
    is_leaving_thread: {
      get() { return this.mainStore.is_leaving_thread; },
      set(value) { this.mainStore.is_leaving_thread = value; }
    },
    // Localized display text — computed (not data) so a language switch re-renders it.
    // channel_mappings keys are the backend channel names; only the labels are localized.
    channel_mappings() {
      return {
        admin: this.$t('messaging.window.channels.admin'),
        auction: this.$t('messaging.window.channels.auction'),
        appraisals: this.$t('messaging.window.channels.appraisals'),
        newinventory: this.$t('messaging.window.channels.newinventory'),
        parts: this.$t('messaging.window.channels.parts'),
        sales: this.$t('messaging.window.channels.sales'),
        service: this.$t('messaging.window.channels.service'),
        usedinventory: this.$t('messaging.window.channels.usedinventory'),
      };
    },
    // Staffed-hours label for the all-teams-closed banner and the closedError text. Prefers the value the
    // server sent, which is the window it actually gated on — TAC's live published hours normally, the
    // legacy Mon–Fri 10am–4pm constant when TAC Portal is unreachable. Taking it from the server is what
    // keeps the banner honest in BOTH cases; a hardcoded string is necessarily wrong in whichever one it
    // didn't describe.
    //
    // The localized catalog string remains as the fallback for first paint (before GetTacTeams returns)
    // and for a server that predates the supportHours field. Server copy is English-only.
    tac_support_hours_display() {
      return this.tac_support_hours_server || this.$t('messaging.window.tac.supportHours');
    },
    language() {
      return this.mainStore.language;
    },
    // Both TAC lines with their country labels, in the same order and layout as the bullet pair Rey
    // sends in chat (CarlStreet.Sdk.Fusion ErrorMessages.TacPhoneNumberAnswer /
    // SupportChatOutsideHours) so the numbers read the same wherever a user meets them.
    // display mirrors FusionConstants (TacSupportPhoneNumber / TacSupportPhoneNumberCanada) — keep in
    // sync. href strips the display format to digits: the space and parentheses in
    // "(800) 767-0080" are not valid unescaped URL characters, and dialers want digits anyway.
    tac_support_phone_lines() {
      return [
        { label: this.$t('messaging.window.tac.countryUs'), display: this.tac_support_phone },
        { label: this.$t('messaging.window.tac.countryCanada'), display: this.tac_support_phone_canada },
      ].map(line => ({ ...line, href: `tel:${line.display.replace(/[^\d]/g, '')}` }));
    },
    user_access_info() {
      return this.mainStore.user_access_info;
    },
    has_tac_access() {
      return this.mainStore.has_tac_access;
    },
    is_autovision_platform() {
      return this.mainStore.is_autovision_platform;
    },
    current_cif() {
      return this.mainStore.current_cif;
    },
    all_dealers() {
      return this.mainStore.all_dealers || [];
    },
    // The groups the composer's dealership filter offers — the same getter it and the brief's Covers
    // picker read — so a finding's group is applied only when the filter can actually show it.
    usable_dealer_groups() {
      return this.mainStore.usable_dealer_groups || [];
    },
    wingman_init() {
      return this.mainStore.wingman_config?.FusionInitType || null;
    },
    is_inventory_vehicle_page() {
      const init = this.wingman_init;
      return !!(init && init.EntityType === 'InventoryVehicle' && init.EntityIdType === 'StockNumber' && init.EntityId);
    },
    vehicle_thread_preview_name() {
      const entityId = this.wingman_init?.EntityId;
      return entityId ? `Stock #${entityId}` : null;
    },
    is_read_only() {
      return this.mainStore.is_read_only;
    },
    system_language() {
      return this.mainStore.user_settings?.flat?.['Ux.Language'] || 'en-US';
    },
    context_screen_name() {
      return this.mainStore.context_screen_name;
    },
    context_control_id() {
      return this.mainStore.context_control_id;
    },
    show_loader() {
      return this.internal_message_processing || this.web_socket_processing || this.messages_processing || this.loading_connection;
    },
    thread_context() {
      return this.mainStore.thread_context;
    },
    rey_session_id() {
      return this.mainStore.rey_session_id;
    },
    ai_chat_thread_id() {
      return this.mainStore.ai_chat_thread_id;
    },
    internal_messages_unread_count() {
      return this.mainStore.internal_messages_unread_count;
    },
    is_embedded() {
      // True only when FusionWeb runs inside an iframe (e.g. the Chrome-extension blade host).
      // Standalone, top-level FusionWeb renders its own in-app toast/pill, so it must NOT emit the
      // host-bridge postMessages: on a page where the extension content script is also present (it
      // injects on localhost/reyreyfusion), `window.parent === window` means those messages land on
      // the same page and trigger a second, native toast/badge on top of the in-app ones.
      return window.self !== window.top;
    },
    spark_unread_count() {
      return this.mainStore.spark_unread_count;
    },
    messages_thread_msg_id() {
      if(this.$FUSarrayHasEntries(this.messages))
        return this.messages.map(x => x.threadMsgId).sort().reverse();
        
      return []; 
    },
    messages_displayed() {
      const self = this;
      return this.messages.filter(message => {
        if(!self.active_thread_is_system_ai) {
          if(!!message.userSpecific && message.userSpecific === self.current_user_guid && message.inReplyTo)
            return false;
          if(!!message.publishedFrom && self.message_replies_published_by_id[message.publishedFrom])
            return false;
        }
        
        return true;
      }).reverse();
    },
    /**
     * The Scheduled tasks thread's layout — date separators and the folded older results — or null for
     * every other thread, which keeps the plain list it has always had.
     *
     * The arithmetic is in scheduledThreadRows.js and not here: it has enough edge cases (nothing old
     * enough to fold, a fold smaller than the peek, undated results) to deserve its own tests without
     * a mounted component.
     */
    scheduled_rows() {
      if (!this.active_thread_is_scheduled) return null;
      return buildScheduledRows(this.messages_displayed, { expanded: this.earlier_expanded });
    },
    /**
     * What the message loop actually iterates.
     *
     * The loop renders message-item with about twenty props, so it exists exactly once and this swaps
     * what feeds it rather than duplicating it behind a branch. For every thread but the scheduled one
     * this IS messages_displayed, unchanged — which is the safety argument for touching a render path
     * all threads share.
     */
    messages_rendered() {
      if (!this.scheduled_rows) return this.messages_displayed;
      return this.scheduled_rows.filter(row => row.type === 'message').map(row => row.message);
    },
    /**
     * Message id → the day key whose separator belongs immediately above it. A lookup rather than a
     * row list so the loop can stay keyed on messages.
     */
    separator_before_id() {
      const before = {};
      if (!this.scheduled_rows) return before;

      let pending = null;
      this.scheduled_rows.forEach(row => {
        if (row.type === 'separator') pending = row.day;
        else if (row.type === 'message' && pending) {
          before[row.message.id] = pending;
          pending = null;
        }
      });

      return before;
    },
    /** The folded-results row, or null when nothing is folded. */
    earlier_row() {
      return this.scheduled_rows?.find(row => row.type === 'earlier') || null;
    },
    /**
     * The tasks the scheduled thread's header counts, or null while they are unknown for this thread —
     * the header then drops its count line (see scheduled_tasks_loaded_for).
     *
     * Read live out of the Schedules panel's list, not a snapshot taken when the thread opened: the
     * panel opens over this thread without changing it, so a snapshot left the count behind every
     * pause made there until the thread was reopened.
     */
    scheduled_header_tasks() {
      if (!this.selected_thread || this.scheduled_tasks_loaded_for !== this.selected_thread) return null;

      const tasks = Array.isArray(this.scheduled_tasks) ? this.scheduled_tasks : [];
      // The morning brief is left out of both counts: it does not deliver into this thread, and the
      // task list this line summarises excludes it too (see ScheduledManagement's tasks_sorted).
      return tasks.filter(task => task && task.taskType !== 'MorningBrief');
    },
    /** Tasks currently enabled, for the header. Null while unknown. */
    scheduled_active_task_count() {
      if (!this.scheduled_header_tasks) return null;
      return this.scheduled_header_tasks.filter(task => task.enabled !== false).length;
    },
    /** Tasks in total, enabled or paused, for the header. Null while unknown. */
    scheduled_total_task_count() {
      if (!this.scheduled_header_tasks) return null;
      return this.scheduled_header_tasks.length;
    },
    /**
     * The task whose question is still unanswered in this thread, or null.
     *
     * The NEWEST awaiting result wins. A task that stays ambiguous asks again every run — that was a
     * deliberate choice, so that a question cannot scroll out of the thread's twenty-message window
     * and be lost — which means several cards can be open at once and only the latest is worth
     * answering. Answering it settles the question for all of them, because a standing answer belongs
     * to the task rather than to the run that asked.
     */
    awaiting_answer_task_id() {
      const rendered = this.messages_rendered || [];

      for (let idx = rendered.length - 1; idx >= 0; idx -= 1) {
        const metadata = rendered[idx]?.metadata || {};
        const outcome = typeof metadata.scheduledOutcome === 'string' ? metadata.scheduledOutcome.trim() : '';
        const taskId = typeof metadata.scheduledTaskId === 'string' ? metadata.scheduledTaskId.trim() : '';
        if (outcome === 'AwaitingUser' && taskId) return taskId;
      }

      return null;
    },
    /**
     * Whether the Scheduled tasks thread should offer a composer at all.
     *
     * Phase 3 hid it outright, on the grounds that nothing posted there is answered and a working
     * input would invite a question that goes nowhere. That is still true of the thread in general —
     * which is why this is the one exception rather than a reversal: a run has asked something, so
     * there IS now something an owner can say here, and exactly one thing.
     */
    scheduled_answer_pending() {
      return Boolean(this.active_thread_is_scheduled && this.awaiting_answer_task_id);
    },
    latest_ai_message_id() {
      const displayed = this.messages_displayed;
      if(!this.$FUSarrayHasEntries(displayed))
        return null;
      // Suggestion chips belong only to the newest Rey response, and only while it is the last
      // message in the thread — messages_displayed is ordered oldest→newest, so the tail is the
      // newest message. The moment the user sends, their (optimistically added) message becomes
      // the tail and the chips retire. Rey (AI) messages carry the system_ sender prefix, but the
      // system_TACCHAT sender is a human TAC agent — not Rey — so it is excluded here, mirroring
      // MessageItem's message_item_type, which classifies system_TACCHAT separately from AI_MESSAGE.
      // Note AI messages don't carry a reliable threadMsgId, so tail position — not threadMsgId —
      // is the recency signal.
      const lastMessage = displayed[displayed.length - 1];
      const senderId = lastMessage.senderId;
      const is_ai_message = senderId?.indexOf(this.$FUSsystemUserPrefix) >= 0 && senderId !== 'system_TACCHAT';
      return is_ai_message ? lastMessage.id : null;
    },
    message_context() {
      const wingmanVer = this.mainStore.wingman_config?.FusionInitType?.InitRuntimeAppVersion || '';
      const uiVer = this.mainStore.fusion_release_version || '';
      const versionParts = [];
      if (wingmanVer) versionParts.push(`W:${wingmanVer}`);
      if (uiVer) versionParts.push(`U:${uiVer}`);
      const base = {
        releaseVersion: versionParts.length > 0 ? versionParts.join('|') : null,
        pageUrl: this.context_screen_name!==null?String(this.context_screen_name):'',
        controlId: this.context_control_id!==null?String(this.context_control_id):'',
        controlName: ''
      };
      // Group scope: DealerId carries the GROUP id, discriminated by DealerType='Group'.
      // dealerIdType MUST stay null — the server's per-message dealer merge (AveryDataChat)
      // treats any DealerId + DealerIdType pair as a page rooftop, so a group id typed as
      // 'CIF' would pollute the thread's page-dealer context.
      if (this.context_group) {
        return {
          dealerId: this.context_group.id,
          dealerIdType: null,
          dealerType: 'Group',
          ...base
        };
      }
      return {
        dealerId: this.context_cif,
        dealerIdType: 'CIF',
        dealerType: 'DealerRoof',
        ...base
      };
    },
    sender_id_idx() {
      const self = this;
      let fieldData = [];
      this.messages_displayed.forEach(message => {
        let senderId = message.senderId;
        if(senderId && fieldData.indexOf(senderId) === -1)
          fieldData.push(senderId);
      });
      
      return fieldData;
    },
    message_replies_by_id() {
      let fieldData = {};
      this.messages.filter(message => Boolean(message.inReplyTo)).forEach(message => {
        fieldData[message.inReplyTo] = message;
      });
      
      return fieldData;
    },
    message_replies_published_by_id() {
      let fieldData = {};
      this.messages.filter(message => Boolean(message.publishedFrom)).forEach(message => {
        fieldData[message.publishedFrom] = message;
      });
      
      return fieldData;
    },
    is_dark_mode() {
      return this.mainStore.theme === 'dark' ||
        (this.mainStore.theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    message_loader_icon() {
      return this.is_dark_mode ? MessageLoaderIconGray : MessageLoaderIconBlue;
    },
    message_loaded_icon() {
      return MessageLoadedIcon;
    },
    quill_pen_icon() {
      return QuillPenIcon;
    },
    component_key() {
      return this.$attrs.key?this.$attrs.key:('messenger_window_'+Date.now());
    },
    component_id() {
      return this.component_key+'_container';
    },
    dealer_guid() {
      return this.mainStore.active_dealer?.companyId || null;
    },
    emoji_index() {
      return emojiIndex || null;
    },
    web_socket_connected() {
      return this.mainStore.socket_connected;
    },
    web_socket_connecting() {
      return this.mainStore.socket_connecting;
    },
    web_socket_try_again: {
      get() { return this.mainStore.socket_try_again; },
      set(value) { this.mainStore.socket_try_again = value; },
    },
    internal_messaging_state: {
      get() {
        return this.mainStore.internal_messaging_state;
      },
      set(value) {
        this.mainStore.internal_messaging_state = value;
      }
    },
    is_creating_thread: {
      get() { return this.mainStore.is_creating_thread; },
      set(value) { this.mainStore.is_creating_thread = value; },
    },
    unreads_by_thread_id: {
      get() {
        return this.mainStore.unreads_by_thread_id;
      },
      set(value) {
        this.mainStore.unreads_by_thread_id = value;
      }
    },
    channel_threads: {
      get() {
        return this.mainStore.channel_threads;
      },
      set(value) {
        this.mainStore.channel_threads = value;
      }
    },
    user_threads: {
      get() {
        return this.mainStore.user_threads;
      },
      set(value) {
        this.mainStore.user_threads = value;
      }
    },
    object_threads: {
      get() {
        return this.mainStore.object_threads;
      },
      set(value) {
        this.mainStore.object_threads = value;
      }
    },
    channel_threads_processing: {
      get() {
        return this.mainStore.channel_threads_processing;
      },
      set(value) {
        this.mainStore.channel_threads_processing = value;
      }
    },
    user_threads_processing: {
      get() {
        return this.mainStore.user_threads_processing;
      },
      set(value) {
        this.mainStore.user_threads_processing = value;
      }
    },
    object_threads_processing: {
      get() {
        return this.mainStore.object_threads_processing;
      },
      set(value) {
        this.mainStore.object_threads_processing = value;
      }
    },
    users_info: {
      get() {
        return this.mainStore.enterprise_users_data;
      },
      set(value) {
        this.mainStore.enterprise_users_data = value;
      }
    },
    enterprise_users_processing: {
      get() {
        return this.mainStore.enterprise_users_processing;
      },
      set(value) {
        this.mainStore.enterprise_users_processing = value;
      }
    },
    // The Schedules panel's task list. The scheduled thread's header counts this same list rather than
    // a copy of its own: the panel edits these rows in place, so a pause, resume or delete there moves
    // the header's count the moment the save succeeds.
    scheduled_tasks: {
      get() {
        return this.mainStore.scheduled_tasks;
      },
      set(value) {
        this.mainStore.scheduled_tasks = value;
      }
    },
    // Drives the four sub-tab display computeds (user_threads_display, channels_display,
    // recent_threads_display, support_threads_display). The navbar's search input writes to
    // mainStore.messenger_search_query; reading it here keeps the existing filter logic intact.
    filter_channels() {
      return this.mainStore.messenger_search_query;
    },
    current_user_guid() {
      return this.user_access_info?.userId || null;
    },
    loading_connection() {
      return this.loading || this.waiting_for_web_socket;
    },
    can_use_av_rey_assist() {
      return this.mainStore.has_ask_rey_access;
    },
    message_header() {
      if(this.active_thread && (this.active_thread.isChannel || this.active_thread.objectType) && this.active_thread.name) {
        if(this.active_thread.isChannel && this.channel_mappings[this.active_thread.name])
          return this.channel_mappings[this.active_thread.name];
        
        return this.active_thread.name;
      }
      else if(this.active_thread && !this.active_thread.isChannel)
        return this.generateDMName(this.active_thread.participantIds, true);
      
      return null;
    },
    header_override: {
      get() {
        return this.mainStore.header_override;
      },
      set(value) {
        this.mainStore.header_override = value;
      }
    },
    active_thread_participant_guids: {
      get() { return this.mainStore.active_thread_participant_guids; },
      set(value) { this.mainStore.active_thread_participant_guids = value; }
    },
    active_thread_nav_meta: {
      get() { return this.mainStore.active_thread_nav_meta; },
      set(value) { this.mainStore.active_thread_nav_meta = value; }
    },
    active_thread_participants: {
      get() { return this.mainStore.active_thread_participants; },
      set(value) { this.mainStore.active_thread_participants = value; }
    },
    active_thread_participant_ids() {
      // Person GUIDs of the open thread's live roster. Sourced from selected_thread_users_list
      // (not the store's active_thread_participants) because the userJoinedThread WS handler keeps
      // this list current when someone joins mid-conversation. Feeds MessengerInput so the @ mention
      // list only offers people who are actually in the chat.
      return (this.selected_thread_users_list || []).map(user => user.id).filter(Boolean);
    },
    contact_origin_view: {
      get() { return this.mainStore.contact_origin_view; },
      set(value) { this.mainStore.contact_origin_view = value; }
    },
    chat_details_thread_id: {
      get() { return this.mainStore.chat_details_thread_id; },
      set(value) { this.mainStore.chat_details_thread_id = value; }
    },
    chat_details_nav_snapshot: {
      get() { return this.mainStore.chat_details_nav_snapshot; },
      set(value) { this.mainStore.chat_details_nav_snapshot = value; }
    },
    contact_return_guid: {
      get() { return this.mainStore.contact_return_guid; },
      set(value) { this.mainStore.contact_return_guid = value; }
    },
    active_thread_id: {
      get() { return this.mainStore.active_thread_id; },
      set(value) { this.mainStore.active_thread_id = value; }
    },
    enterprise_users() {
      return this.users_info?.enterpriseUsers || [];
    },
    ent_users_by_guid() {
      return this.$FUSarrayOfObjectsKeyMap(this.enterprise_users, 'personGuid') || {};
    },
    ent_user_names_by_guid() {
      let fieldData = {};
      const self = this;
      if(this.$FUSobjectHasFields(this.ent_users_by_guid))
        Object.keys(this.ent_users_by_guid).forEach(userGuid => {
          fieldData[userGuid] = self.$FUSuserDisplayName(self.ent_users_by_guid[userGuid]);
        });
        
      return fieldData;
    },
    channels_display() {
      if (this.$FUSarrayHasEntries(this.channel_threads)) {
        let filterChannels = this.filter_channels ? this.normalizeQuery(this.filter_channels.trim().toLowerCase()) : '';
        return this.channel_threads.filter(channel => {
          if (filterChannels) {
            let channelName = channel.name.toLowerCase();
            return channelName.includes(filterChannels);
          }
          return true;
        }).sort((a, b) => {
          const aUnread = (this.unreads_by_thread_id[a.threadId] || 0) > 0;
          const bUnread = (this.unreads_by_thread_id[b.threadId] || 0) > 0;
          if (aUnread !== bUnread) return aUnread ? -1 : 1;
          const aDate = new Date(this.thread_last_message_at_by_thread_id[a.threadId] || a.lastMessageAt || a.createdAt || 0);
          const bDate = new Date(this.thread_last_message_at_by_thread_id[b.threadId] || b.lastMessageAt || b.createdAt || 0);
          return bDate - aDate;
        });
      }
      return [];
    },
    user_threads_display() {
      const self = this;
      if (this.$FUSarrayHasEntries(this.user_threads)) {
        let filterChannels = this.filter_channels ? this.normalizeQuery(this.filter_channels.trim().toLowerCase()) : '';
        return this.user_threads.map(dm => {
          let dmCopy = self.$FUSjsonCopy(dm);
          let dmHumanParticipants = dm.participantIds.filter(x => x.indexOf('system_')<0);
          dmCopy.name = self.generateDMName(dmHumanParticipants);
          dmCopy.shortName = self.generateDMName(dmHumanParticipants, true);
          // Flag group DMs (incl. ones that have shrunk to two people) so the row can tag them and
          // keep them visually distinct from native 1-on-1s. Matches the dedup bucketing.
          dmCopy.isGroupDm = self.isGroupOriginatedThread(dm);
          return dmCopy;
        }).filter(dm => {
          if (filterChannels) {
            let channelName = dm.name.toLowerCase();
            return channelName.includes(filterChannels);
          }
          return true;
        }).sort((a, b) => {
          const aUnread = (this.unreads_by_thread_id[a.threadId] || 0) > 0;
          const bUnread = (this.unreads_by_thread_id[b.threadId] || 0) > 0;
          if (aUnread !== bUnread) return aUnread ? -1 : 1;
          const aDate = new Date(this.thread_last_message_at_by_thread_id[a.threadId] || a.lastMessageAt || a.createdAt || 0);
          const bDate = new Date(this.thread_last_message_at_by_thread_id[b.threadId] || b.lastMessageAt || b.createdAt || 0);
          return bDate - aDate;
        });
      }
      return [];
    },
    user_threads_by_unique_key() {
      // key = group-ness + joined list of sorted participants. The 'G:'/'D:' prefix keeps a group DM
      // (even one that has shrunk to two people) in a separate bucket from a native 1-on-1, so a
      // leftover group can never be reused as / collided with a 1-on-1 that has the same members.
      let fieldData = {};
      const self = this;
      if(this.$FUSarrayHasEntries(this.user_threads)) {
        this.user_threads.forEach(directMessage => {
          let uniqueKey = self.generateDMkey(directMessage.participantIds);
          let prefix = self.isGroupOriginatedThread(directMessage) ? 'G:' : 'D:';
          fieldData[prefix + uniqueKey] = directMessage;
        });
      }

      return fieldData;
    },
    recent_threads_display() {
      if (this.$FUSarrayHasEntries(this.object_threads)) {
        const self = this;
        let filterChannels = this.filter_channels ? this.normalizeQuery(this.filter_channels.trim().toLowerCase()) : '';
        return this.object_threads.filter(message => {
          if(message.objectType === 'TACChat' || message.objectType.startsWith('SystemAI_'))
            return false;
          
          if (filterChannels) {
            let channelName = message.name.toLowerCase();
            return channelName.includes(filterChannels);
          }
          
          return true;
        }).sort((a, b) => {
          const aUnread = (this.unreads_by_thread_id[a.threadId] || 0) > 0;
          const bUnread = (this.unreads_by_thread_id[b.threadId] || 0) > 0;
          if (aUnread !== bUnread) return aUnread ? -1 : 1;
          const aDate = new Date(this.thread_last_message_at_by_thread_id[a.threadId] || a.lastMessageAt || a.createdAt || 0);
          const bDate = new Date(this.thread_last_message_at_by_thread_id[b.threadId] || b.lastMessageAt || b.createdAt || 0);
          return bDate - aDate;
        });
      }
      return [];
    },
    support_threads_display() {
      if (this.$FUSarrayHasEntries(this.object_threads)) {
        const self = this;
        let filterChannels = this.filter_channels ? this.normalizeQuery(this.filter_channels.trim().toLowerCase()) : '';
        return this.object_threads.filter(message => {
          if(message.objectType !== 'TACChat')
            return false;
          
          if (filterChannels) {
            let channelName = message.name.toLowerCase();
            return channelName.includes(filterChannels);
          }
          
          return true;
        }).sort((a, b) => {
          const aUnread = (this.unreads_by_thread_id[a.threadId] || 0) > 0;
          const bUnread = (this.unreads_by_thread_id[b.threadId] || 0) > 0;
          if (aUnread !== bUnread) return aUnread ? -1 : 1;
          const aDate = new Date(this.thread_last_message_at_by_thread_id[a.threadId] || a.lastMessageAt || a.createdAt || 0);
          const bDate = new Date(this.thread_last_message_at_by_thread_id[b.threadId] || b.lastMessageAt || b.createdAt || 0);
          return bDate - aDate;
        });
      }
      return [];
    },
    all_threads_display() {
      // 'All' tab — merges all four categories sorted by last-message time descending.
      // All source computeds already apply filter_channels, so search just works.
      // Each row is tagged with _displayType so the template can render the right thread-type per row.
      const dms = this.user_threads_display.map(t => Object.assign({}, t, { _displayType: 'Messages' }));
      const threads = this.recent_threads_display.map(t => Object.assign({}, t, { _displayType: 'Threads' }));
      const channels = this.channels_display.map(t => Object.assign({}, t, { _displayType: 'Channels' }));
      const support = this.support_threads_display.map(t => Object.assign({}, t, { _displayType: 'Support' }));
      return dms.concat(threads, channels, support).sort((a, b) => {
        const aUnread = (this.unreads_by_thread_id[a.threadId] || 0) > 0;
        const bUnread = (this.unreads_by_thread_id[b.threadId] || 0) > 0;
        if (aUnread !== bUnread) return aUnread ? -1 : 1;
        const aDate = new Date(this.thread_last_message_at_by_thread_id[a.threadId] || a.lastMessageAt || a.createdAt || 0);
        const bDate = new Date(this.thread_last_message_at_by_thread_id[b.threadId] || b.lastMessageAt || b.createdAt || 0);
        return bDate - aDate;
      });
    },
    all_threads() {
      let fieldData = [];
      if(this.$FUSarrayHasEntries(this.channel_threads))
        this.channel_threads.forEach(x => fieldData.push(x));
      if(this.$FUSarrayHasEntries(this.user_threads))
        this.user_threads.forEach(x => fieldData.push(x));
      if(this.$FUSarrayHasEntries(this.object_threads))
        this.object_threads.forEach(x => fieldData.push(x));
      
      return fieldData;
    },
    all_threads_by_thread_id() {
      return this.$FUSarrayOfObjectsKeyMap(this.all_threads, 'threadId');
    },
    active_thread() {
      if(this.selected_thread && this.all_threads_by_thread_id && this.all_threads_by_thread_id[this.selected_thread])
        return this.all_threads_by_thread_id[this.selected_thread];
      
      return null;
    },
    active_thread_is_system_ai() {
      // A New Topic draft is Rey's too, but it is never in the thread lists — it sits on the
      // placeholder id until its first send creates the conversation — so active_thread is null and
      // the objectType test alone read false. The composer then rendered the internal-messaging
      // layout (the @ mention button in place of the store picker and topic name) until that send.
      // Gated to Spark because spark_draft_active is store state, seen by the InternalMessaging
      // mount as well.
      if(this.active_thread_is_spark_draft) return true;
      return Boolean(this.active_thread?.objectType?.startsWith('SystemAI_'));
    },
    active_thread_is_spark_draft() {
      return this.page_type === 'Spark' && Boolean(this.spark_draft_active)
        && this.selected_thread === this.placeholder_thread_id;
    },
    /**
     * True when the open thread is the Scheduled tasks thread — the one per-user thread scheduled
     * results are delivered into.
     *
     * Read from the server's own flag rather than by matching the thread's ObjectId suffix here: the
     * suffix convention belongs to MessengerApiFunctions.IsScheduledThreadObjectId, and a second copy
     * of it in JavaScript is a rule nothing keeps in step.
     */
    active_thread_is_scheduled() {
      return Boolean(this.active_thread?.isScheduled);
    },
    /**
     * True when the open thread is a generated morning-brief thread — one per owner per day.
     *
     * Read from the server's flag for the same reason the scheduled one is: the ObjectId convention
     * belongs to MessengerApiFunctions.IsBriefThreadObjectId, and a copy of it here is a rule nothing
     * keeps in step.
     */
    active_thread_is_brief() {
      return Boolean(this.active_thread?.isBrief);
    },
    /** The owner-local day the open brief covers, for its header. Empty on every other thread. */
    active_thread_brief_date() {
      return this.active_thread?.briefDate || '';
    },
    /**
     * The open thread's display name, for the label beside the composer's store picker. Named here
     * the same way the conversation list names it — the list and the composer must not disagree about
     * what a thread is called.
     *
     * A brief is included even though it is generated and never titled: its own header greets its
     * reader but never says WHICH morning, and a brief read on Tuesday is still Monday's brief (see
     * BriefThreadHeader.briefDate), so this label is the only thing on that thread naming the day.
     *
     * Empty for two kinds of thread:
     *
     * - Anything that is not Rey's. DMs and channels are named in their own header already.
     * - The Scheduled tasks thread. Its composer only appears at all while the owner is adjusting a
     *   task or answering a question about one, and in both of those states the banner directly above
     *   the input already says which — naming the thread there answers a question nobody is asking.
     */
    active_thread_topic_name() {
      if(!this.active_thread_is_system_ai) return '';
      // A draft has no name yet; call it what the button that opened it is called, rather than
      // "Untitled topic" (sparkConversationLabel's answer for a nameless thread).
      if(this.active_thread_is_spark_draft) return this.$t('navbar.newConversation');
      if(this.active_thread_is_scheduled) return '';
      if(this.active_thread_is_brief) {
        // A name its owner chose outranks the day we derived, exactly as it does in the conversation
        // list — the two must not disagree about what the open thread is called.
        const chosen = (this.active_thread?.name || '').trim();
        return chosen || this.briefThreadLabel(this.active_thread_brief_date);
      }

      return this.sparkConversationLabel(this.active_thread);
    },
    active_thread_is_tac_chat() {
      return this.active_thread?.objectType === 'TACChat';
    },
    active_thread_contextual_data() {
      return this.active_thread?.contextualData || null;
    },
    active_thread_tac_chat_team_name() {
      return this.active_thread_contextual_data?.TacChatTeamName || null;
    },
    can_have_channels() {
      return Boolean(this.dealer_guid);
    },
    left_column_classes() {
      if(!this.creating_new_thread && (!this.active_panel || this.active_panel === 'channels'))
        return 'block w-full pr-2';
      else
        return 'hidden';
    },
    dealer_roofs_with_messenger() {
        const dealers = {};
        this.user_access_info?.dealers?.filter(dealer => dealer.messagingEnabled).forEach(dealer => dealers[dealer.messagingDealerGuid] = dealer.name);
        return dealers;
    },
    messages_by_id() {
      return this.$FUSarrayOfObjectsKeyMap(this.messages, 'id');
    },
    is_typing() {
      return this.streaming_message_id !== null;
    },
    active_thread_is_closed_tac_chat() {
      return this.active_thread_is_tac_chat && this.active_thread?.isClosed;
    },
    // Deduped list of teams the user has recently initiated TAC chats with, most-recent first.
    // Source: object_threads filtered to all TACChat threads (open or closed); team identity
    // comes from ContextualData.TacChatTeamId / .TacChatTeamName persisted at thread creation.
    // Capped at 2 entries per spec. Threads for teams the user has since lost MCP access to
    // are skipped (matches the All list — can't initiate a new chat with that team, shouldn't
    // appear in Recent either).
    recent_tac_team_options() {
      if (!this.$FUSarrayHasEntries(this.object_threads))
        return [];
      const seen = new Set();
      const result = [];
      const tacThreads = this.object_threads.filter(t => t.objectType === 'TACChat');
      tacThreads.sort((a,b) => {
        const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bDate - aDate;
      });
      for (const thread of tacThreads) {
        const teamId = thread.contextualData?.TacChatTeamId;
        const teamName = thread.contextualData?.TacChatTeamName;
        if (!teamId || seen.has(teamId)) continue;
        seen.add(teamId);
        if (!this.userHasTacTeamAccess(teamId)) continue;
        // Backend stamps team names with " Team" suffix (e.g. "AutoVision Team"); strip it for display
        // so the row label matches the canonical team name surfaced in the All list.
        const cleanName = teamName ? teamName.replace(/ Team$/, '') : teamId;
        result.push({
          key: teamId,
          name: cleanName,
          subtitle: thread.createdAt ? this.$t('messaging.window.recentStarted', { date: this.$FUSdateFormat('MM/DD', thread.createdAt) }) : null,
          startedAt: thread.createdAt || null,
        });
        if (result.length >= 2) break;
      }
      return result;
    },
    all_tac_team_options() {
      if (!this.$FUSarrayHasEntries(this.tac_teams_list))
        return [];
      // Backend now pre-filters by MCP access, but we re-apply the same filter on the client
      // for defense-in-depth — a stale build or a bug in the backend filter shouldn't be able
      // to surface a team the user can't actually use. Backend filter is the source of truth;
      // this is a redundant safety net. Also re-sort defensively (alpha, per FusionWeb rules).
      return [...this.tac_teams_list]
        .filter(t => this.userHasTacTeamAccess(t.key))
        .sort((a,b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    },
    // Mirrors ContactsList's messenger_search_query_trimmed: a whitespace-only query counts as no
    // query at all, so it neither filters the list nor gets echoed inside the "No results for" quotes.
    tac_search_query_trimmed() {
      return (this.tac_search_query || '').trim();
    },
    filtered_recent_tac_teams() {
      if (!this.tac_search_query_trimmed) return this.recent_tac_team_options;
      const q = this.tac_search_query_trimmed.toLowerCase();
      return this.recent_tac_team_options.filter(t => t.name.toLowerCase().includes(q));
    },
    filtered_all_tac_teams() {
      if (!this.tac_search_query_trimmed) return this.all_tac_team_options;
      const q = this.tac_search_query_trimmed.toLowerCase();
      return this.all_tac_team_options.filter(t => t.name.toLowerCase().includes(q));
    },
    // Mirrors CarlStreet.Sdk.Fusion.Integration.TacChat.TacChatTeamAccessFilter on the backend.
    // Keep these two maps in sync. A team key with no entry here is treated as inaccessible
    // (matches the backend behavior: Gubagoo is hidden until GubagooMcp ships).
    accessible_mcp_servers() {
      const set = new Set();
      const list = this.user_access_info?.mcpAccess;
      if (!Array.isArray(list)) return set;
      for (const m of list) {
        if (m && m.hasAccess && typeof m.serverName === 'string') {
          set.add(m.serverName.toLowerCase());
        }
      }
      return set;
    },
    // Display name for the team the user has currently selected — used by the
    // "starting your chat" loading state to confirm which team they're connecting to.
    // Looks in the full team list first, then falls back to the Recent list (which is
    // derived from prior threads, so it may carry teams not in the All response).
    selected_team_display_name() {
      if (!this.selected_tac_team_key) return '';
      const all = this.tac_teams_list?.find(t => t.key === this.selected_tac_team_key);
      if (all) return all.name;
      const recent = this.recent_tac_team_options?.find(t => t.key === this.selected_tac_team_key);
      return recent?.name || '';
    },
    message_send_ready() {
      return !(this.loading_connection || this.messages_processing || this.message_sending || this.messages_error || this.active_thread_is_closed_tac_chat || this.is_read_only || this.draft_send_pending);
    },
    // True while the New Topic on screen is still creating the conversation for its first question.
    // Folded into message_send_ready, which is also what makes the composer editable, so a second
    // question cannot be typed or sent until the first has somewhere to go. Scoped by draft rather
    // than component-wide: once the user leaves that New Topic — for a conversation or a fresh New
    // Topic — it no longer applies to anything they can see.
    draft_send_pending() {
      return this.sending_draft_generation !== null && this.isSameDraft(this.sending_draft_generation);
    },
    // Legacy fallback window (Mon–Fri 10am–4pm ET), used only when the server didn't send per-team
    // availability — see tacTeamAvailable. Depends on now_tick so it flips at the hour boundary while
    // the screen stays open.
    tac_support_open() {
      void this.now_tick; // reactive trigger; value itself is unused
      // Backend may bypass the hours restriction for this user (staging + AutoWithDebug admin flag,
      // reported via GetTacTeams). When active, treat support as open so the picker isn't greyed out.
      return this.tac_support_bypass_active || this.isWithinTacSupportHours(new Date());
    },
    // The picker as a whole is unusable only when every team we can show is closed — that's when the
    // "Unavailable during these hours" banner earns its place. Individual rows are gated separately by
    // tacTeamAvailable: with per-team hours, "some teams closed" is the normal case and must not
    // disable the whole list. Loading and empty states have their own messaging, so they aren't this.
    // Note: this gates only STARTING a chat. An already-started TAC chat stays sendable after its
    // team goes off-hours — teams publish their own schedules now, so there is no single boundary to
    // name here. It's the TAC rep's responsibility to close the thread (which then locks the
    // composer via active_thread_is_closed_tac_chat), so we intentionally do not offline-lock it.
    tac_picker_disabled() {
      if (this.tac_teams_loading || !this.$FUSarrayHasEntries(this.tac_teams_list)) return false;
      return !this.tac_teams_list.some(option => this.tacTeamAvailable(option.key));
    },
    // The Start button follows the selected team, not the list as a whole.
    selected_tac_team_available() {
      return this.tacTeamAvailable(this.selected_tac_team_key);
    },
    web_socket_processing() {
      return this.web_socket_connecting || this.loading;
    },
    internal_message_errors() {
      switch (this.messageType) {
        case 'Messages':
          return this.user_threads_error;
        case 'Threads':
        case 'Support':
          return this.object_threads_error;
        case 'Channels':
          return this.channel_threads_error;
        default:
          return '';
      }
    },
    internal_message_processing() {
      switch (this.messageType) {
        case 'All':
          return this.user_threads_processing || this.object_threads_processing || this.channel_threads_processing;
        case 'Messages':
          return this.user_threads_processing;
        case 'Threads':
        case 'Support':
          return this.object_threads_processing;
        case 'Channels':
          return this.channel_threads_processing;
        default:
          return '';
      }
    },
    internal_messages() {
      switch (this.messageType) {
        case 'All':
          return this.all_threads_display;
        case 'Messages':
          return this.user_threads_display;
        case 'Threads':
          return this.recent_threads_display;
        case 'Channels':
          return this.channels_display;
        case 'Support':
          return this.support_threads_display;
        default:
          return [];
      }
    },
    internal_message_avatar_colors() {
      switch (this.messageType) {
        case 'All':
          return this.internal_message_all_to_colors;
        case 'Messages':
          return this.internal_message_users_to_colors;
        case 'Threads':
        case 'Support':
          return null;
        case 'Channels':
          return this.internal_message_channels_to_colors;
        default:
          return null;
      }
    },
    internal_message_users_to_colors() {
      // Derive from the full user_threads list (sorted by last message time, same as the display
      // sort) so a DM participant always gets the same palette color regardless of which tab is
      // active. Using internal_messages (the currently-displayed slice) caused the index to shift
      // between the Messages tab and the All tab, producing different colors for the same person.
      const self = this;
      const sorted = (this.user_threads || []).slice().sort((a, b) => {
        const aDate = new Date(a.lastMessageAt || a.createdAt || 0);
        const bDate = new Date(b.lastMessageAt || b.createdAt || 0);
        return bDate - aDate;
      });
      let idx = 0;
      const fieldData = {};
      sorted.forEach(thread => {
        if(self.$FUSarrayHasEntries(thread.participantIds)) {
          thread.participantIds.forEach(userId => {
            if(!fieldData.hasOwnProperty(userId) && userId.indexOf('system_') < 0 && userId !== self.current_user_guid) {
              fieldData[userId] = self.getAvatarColorForIndex(idx);
              idx++;
            }
          });
        }
      });
      return fieldData;
    },
    internal_message_channels_to_colors() {
      // Derive from the full channel_threads list so channel colors are stable across tabs.
      const self = this;
      const sorted = (this.channel_threads || []).slice().sort((a, b) => {
        const aDate = new Date(a.lastMessageAt || a.createdAt || 0);
        const bDate = new Date(b.lastMessageAt || b.createdAt || 0);
        return bDate - aDate;
      });
      const fieldData = {};
      sorted.forEach((thread, idx) => {
        if(thread.threadId) fieldData[thread.threadId] = self.getAvatarColorForIndex(idx);
      });
      return fieldData;
    },
    internal_message_all_to_colors() {
      // All tab needs both personGuid → color (for DM rows) and threadId → color (for channel
      // rows). Merge the two stable maps so every row type gets a colour.
      return Object.assign({}, this.internal_message_users_to_colors, this.internal_message_channels_to_colors);
    },
    input_config() {
      let fieldData = null;
      if(this.setConfiguration && this.setConfiguration.FuseConfigurations && this.setConfiguration.FuseConfigurations.InputConfiguration)
        fieldData = this.$FUSjsonCopy(this.setConfiguration.FuseConfigurations.InputConfiguration);
      else {
        fieldData = {
          placeholder: this.message_header || '',
          bottomButtons: true,
          tagging: true,
          fileUpload2: true,
          emoji: true,
          send3: true,
        };
      }
      if(this.active_thread_is_closed_tac_chat)
        fieldData.placeholder = this.$t('messaging.window.errors.threadClosed');

      return fieldData;
    },
    creating_new_thread() {
      return ['NewMessage','NewThread','NewChannel','AddToChannel'].indexOf(this.internal_messaging_state)>=0;
    },
    create_thread_mode() {
      // AddToChannel (adding members to the currently open channel) overrides the tab-derived mode
      // so the picker renders regardless of which messageType tab we opened the channel from.
      if (this.internal_messaging_state === 'AddToChannel') return 'AddToChannel';
      // Creating a vehicle thread always uses the participant picker ('Threads' mode), regardless of
      // which messageType tab the Compose menu was opened from — the vehicle option isn't tab-scoped.
      if (this.pending_vehicle_thread) return 'Threads';
      // An explicit "New message" compose is always a DM. It can be launched from a non-Messages
      // context — e.g. the contact card's Message button while viewing a channel's Chat Details,
      // where messageType is 'Channels' — so the NewMessage intent must win over the tab-derived
      // mode below; otherwise the composer would render the channel-creation UI for a DM.
      if (this.internal_messaging_state === 'NewMessage') return 'Messages';
      if (this.messageType !== 'All') return this.messageType;
      const map = { NewMessage: 'Messages', NewThread: 'Threads', NewChannel: 'Channels' };
      return map[this.internal_messaging_state] || 'Messages';
    },
    // Compose mode is driven by the compose STATE, not the filter chip (messageType). The chip
    // (DMs/Support/etc.) only filters the thread list; reusing it for compose leaked a stale
    // 'Support' into the New Message form when a DM was started from Contacts while the Support
    // chip was active, so CreateThread (which only knows Messages/Threads/Channels) rendered
    // blank and createThread() matched no case. Deriving from internal_messaging_state keeps the
    // form and the create handler correct regardless of which chip was last selected.
    compose_mode() {
      switch (this.internal_messaging_state) {
        case 'NewMessage':
          return 'Messages';
        case 'NewThread':
          return 'Threads';
        case 'NewChannel':
          return 'Channels';
        default:
          return null;
      }
    },
    message_replies_published_by_message_id() {
      const fieldData = {};
      
      this.messages.forEach(message => {
        const messageReply = this.message_replies_by_id[message.id];
        
        if(messageReply?.id)
          fieldData[message.id] = this.message_replies_published_by_id.hasOwnProperty(messageReply.id);
        else
          fieldData[message.id] = false;
      });
      
      return fieldData;
    },
    page_type() {
      return this.setConfiguration?.PageType;
    },
  },
  methods: {
    // Exposed for the template: a module-scope import is not reachable from an
    // Options-API template expression.
    getActiveLocaleTag() {
      return getActiveLocaleTag();
    },
    formatToolJson(str) {
      if(!str) return '';
      try {
        return JSON.stringify(JSON.parse(str), null, 2);
      } catch(e) {
        return str;
      }
    },
    getStatusPhrase(key, lang) {
      // lang defaults to the selected System Language; pass 'en' to match the English phrase the
      // backend actually sends today (live-path status text is not yet localized).
      return this.mainStore.getStatusMessage(key, lang || this.system_language);
    },
    fetchThreadsWhenReady(stackCount=0, threadMethod=null) {
      // An unmounted panel stops polling here instead of running threadMethod once the socket is
      // up — see is_unmounted.
      if (this.is_unmounted) return;
      const self = this;
      this.web_socket_try_again = false;
      this.waiting_for_web_socket = true;
      if(stackCount<50) {
        //if done connecting to websocket, fetch threads 
        if (this.web_socket_connected) {
          if(typeof threadMethod === 'function')
            threadMethod();
          else {
            //no thread method to process
            console.log('no thread method to process');
          }
          this.waiting_for_web_socket = false;
        } else {
          setTimeout(function () {
            self.fetchThreadsWhenReady(stackCount + 1, threadMethod);
          }, 100);
        }
      }
      else {
        console.log('error opening WebSocket. Cannot process threads');
        this.error = this.$t('messaging.window.errors.connecting')
        this.web_socket_try_again = true;
        this.waiting_for_web_socket = false;
        this.messages_processing = false;
      }
    },
    waitForFlag(flagName) {
      return new Promise(resolve => {
        if (!this.mainStore[flagName]) { resolve(); return; }
        const stop = this.$watch(`mainStore.${flagName}`, val => {
          if (!val) { stop(); resolve(); }
        });
      });
    },
    async getThreads(threadId=null) {
      this.clearErrors();
      let openThreadId = threadId || this.set_thread_id || this.selected_thread || null;

      const fetchChannels = !this.channel_threads_processing && !this.$FUSarrayHasEntries(this.channel_threads);
      const fetchUsers = !this.user_threads_processing && !this.$FUSarrayHasEntries(this.user_threads);
      const fetchObjects = !this.object_threads_processing && !this.$FUSarrayHasEntries(this.object_threads);

      if (fetchUsers && fetchObjects)
        this.unreads_by_thread_id = {};

      await Promise.all([
        this.channel_threads_processing ? this.waitForFlag('channel_threads_processing') : (fetchChannels ? this.getChannels() : Promise.resolve()),
        this.user_threads_processing ? this.waitForFlag('user_threads_processing') : (fetchUsers ? this.getUserThreads() : Promise.resolve()),
        this.object_threads_processing ? this.waitForFlag('object_threads_processing') : (fetchObjects ? this.getObjectThreads() : Promise.resolve()),
      ]);

      // Unmounted while the lists were loading — see is_unmounted. The panel that replaced this one
      // makes its own decision below, and making one here as well would spend it.
      if(this.is_unmounted) return;

      // A conversation another surface asked for before this panel could open it (see
      // spark_pending_open_thread_id) is the user saying where to be, and outranks everything below
      // exactly as a deep link does. Read here, after the list read, rather than into openThreadId
      // above, because the request can land while that read is out.
      if(this.page_type === 'Spark' && !threadId && this.spark_pending_open_thread_id) {
        this.rey_startup_landing_done = true;
        this.openSparkConversation(this.spark_pending_open_thread_id);
        return;
      }

      // A Spark panel opening cold decides where to land for itself — see landOnStartupThread.
      //
      // The three conditions are all about establishing that nobody has said where to be yet:
      //   - selected_thread null: this is the FIRST getThreads of the mount, not a socket retry or a
      //     webSocketTryAgain re-entry. Without it a reconnect would yank the user out of whatever
      //     they had since opened.
      //   - not in a draft: "+" is the user saying where they want to be, as it is everywhere else
      //     in this file.
      //   - a pending question (the welcome screen's) is the same kind of instruction, and its send
      //     is plumbed through getThreadMessages — which only runs when a REAL thread opens. Landing
      //     on a draft here would swallow it, so that case keeps the old behaviour.
      //
      // openThreadId is either null (nothing pinned at all) or the host's own default, which is not
      // a choice — anything else is a genuine deep link and still wins below.
      const questionPending = Boolean(this.set_new_message || this.setNewMessage);
      if(this.page_type === 'Spark' && !this.selected_thread && !this.spark_draft_active && !questionPending
         && (!openThreadId || openThreadId === this.host_default_thread_id)) {
        await this.landOnStartupThread();
        return;
      }

      // Every other way through here — a deep link, the welcome screen's question, a thread already
      // open — has put the user somewhere they chose, so a later mount must not replay the first-open
      // landing over it. See rey_startup_landing_done.
      if(this.page_type === 'Spark')
        this.rey_startup_landing_done = true;

      if(openThreadId) {
        this.openThread(openThreadId);
        return;
      }

      // Nothing was pinned and nothing is open, so this is a panel opening cold. The design has Rey
      // open on the morning's brief when there is one — "leading with the results it produced before
      // the user logged in".
      //
      // Reached only in the null branch, which is what keeps it from hijacking navigation: a
      // ?thread_id=, a host-pinned thread and a thread the user was already in all produce a non-null
      // openThreadId above and win without this having an opinion.
      await this.openTodaysBriefIfUnread();
    },
    /**
     * Where a cold Spark panel lands, in the order the design asks for.
     *
     * <b>First open of the day</b> — the welcome screen is up, and the day's brief belongs behind it:
     * dismissing the welcome leaves the owner on the findings Rey produced before they logged in. No
     * brief (not opted in, none generated yet) and they start the day on a blank New Topic instead,
     * which is the other thing a morning can reasonably begin with.
     *
     * <b>Any later open the same day</b> — back to the conversation they were last in. This is the
     * case the panel got wrong before multi-conversation: the host's default thread was opened every
     * time, so a user who had moved to a second conversation was silently returned to their first on
     * every reload.
     *
     * "Where they were" includes a New Topic. Someone who clicks it and refreshes before typing is
     * still starting something new, and handing them back the thread they had just left would undo
     * the one thing they did.
     *
     * The first open of the day outranks all of that, a remembered New Topic included: yesterday's
     * unfinished draft is not a reason to skip the day's welcome and brief.
     *
     * A remembered thread that no longer exists falls through to New Topic rather than to the host's
     * default. They deleted it, or it was reset; dropping them into some other conversation they did
     * not pick would be a worse guess than an empty composer.
     *
     * Everything here is best-effort by construction: both store calls resolve rather than reject,
     * and every branch ends somewhere the user can type.
     */
    async landOnStartupThread() {
      if(this.page_type !== 'Spark') return;

      // AWAITED, not read raw. FusionBody starts this resolve in its own created() and this runs
      // roughly 500ms later behind fetchThreadsWhenReady — usually long enough, but it is the first
      // settings read of a freshly authenticated session and an unresolved marker reads as "not
      // their first open today", which is precisely the landing this exists to get right. The store
      // caches the resolution and shares an in-flight read, so the normal path costs nothing.
      //
      // A rejection means there is no user to resolve for, and everything below degrades to the
      // New Topic fallback on its own — there is nothing better to do with it here.
      try {
        await this.ensureWelcomeGate();
      } catch (error) {
        // Deliberately empty — see above.
      }
      // The welcome splash usually resolves in the same gate read this awaited, and replaces the chat
      // window the moment it does: this instance is then gone, and the one mounted when the welcome
      // is dismissed does the landing. See is_unmounted.
      if(this.is_unmounted) return;

      // NOT welcome_eligible_today — FusionBody consumes that the moment it shows the splash, and
      // this runs afterwards. See the rey_first_open_today note in the store.
      //
      // And only once per page (rey_startup_landing_done): rey_first_open_today stays true all day, so
      // without the second test every re-mount on a first-open day took this branch again and moved
      // the user off wherever they had gone since.
      if(this.rey_first_open_today && !this.rey_startup_landing_done) {
        const openedBrief = await this.openTodaysBriefIfUnread();
        // Spent only by a panel that is still here to carry it out. One unmounted during the list read
        // leaves the first-open landing to its replacement.
        if(this.is_unmounted) return;
        this.rey_startup_landing_done = true;
        if(openedBrief) return;
        this.startDraftConversation();
        return;
      }

      const rememberedThreadId = await this.ensureReyLastThread();
      if(this.is_unmounted) return;
      this.rey_startup_landing_done = true;
      // They were on a New Topic when they left — the watcher remembers the draft's placeholder
      // for exactly this. Branched on explicitly rather than left to the "not found" fall-through
      // below, which would happen to land in the same place for the wrong reason.
      if(rememberedThreadId === this.placeholder_thread_id) {
        this.startDraftConversation();
        return;
      }

      // Checked against the object threads getThreads just fetched rather than with another list
      // read: Rey conversations ARE object threads, so the map is already authoritative about which
      // of them still exist (the same check ensureSystemAiThreadInObjectList makes).
      if(rememberedThreadId && this.all_threads_by_thread_id?.[rememberedThreadId]) {
        this.openSparkConversation(rememberedThreadId);
        return;
      }

      this.startDraftConversation();
    },
    /**
     * Opens today's brief, if there is one and the owner has not read it.
     *
     * <b>Unread is the gate, and it is doing real work.</b> Without it every cold open for the rest of
     * the day would snap back to the brief after the owner had already dealt with it — the thread is
     * "leading with" results, not claiming the panel. Once read it stops taking the landing slot.
     *
     * Spark only. Brief threads are Rey conversations, so the InternalMessaging instance has no
     * business opening one — the same scoping rule every SPARK-* handler in this file follows.
     *
     * Best-effort throughout: this decides where a panel LANDS, and a list read that fails should
     * leave the user on the ordinary empty state rather than on an error.
     *
     * Resolves TRUE only when a brief was actually opened. landOnStartupThread needs to know, because
     * "no brief today" is what sends the first open of the day to a New Topic instead — and a
     * fall-through here is indistinguishable from a success without it.
     */
    async openTodaysBriefIfUnread() {
      if(this.page_type !== 'Spark' || this.spark_draft_active) return false;

      // A question typed on the welcome screen arrives as set_new_message, and it is sent into
      // whatever thread ended up selected. So claiming the landing slot here would post that question
      // into the morning brief: the owner asks Rey something on the way in and their message lands in
      // a thread of overnight findings, under a "Welcome back" header, as though they had replied to
      // the brief. Same class of guard as spark_draft_active above — the owner is already saying where
      // they want to be, and this only decides where a panel lands when they have not said.
      //
      // The PROP is checked alongside the data copy because the copy is assigned in created(), which
      // awaits a fetch before it gets there. getThreads is deferred 500ms behind fetchThreadsWhenReady
      // so in practice the copy is set by now — but that is a timing assumption, and the prop has been
      // there since the first render.
      if(this.set_new_message || this.setNewMessage) return false;

      try {
        const rows = await this.getSparkConversationsForCap();
        // Gone while the list was read — opening the brief now would join and load it for nobody,
        // and remember it as where the user was. See is_unmounted.
        if(this.is_unmounted) return false;
        const conversations = Array.isArray(rows) ? rows : [];
        if(!conversations.length) return false;

        this.spark_conversations = conversations;

        // The owner's local day, built from parts. `toISOString()` would give a UTC day and land the
        // wrong side of midnight for anyone west of Greenwich — the same trap the thread's own day
        // separators and its drawer label both document.
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        const brief = conversations.find(row => row
          && row.isBrief
          && row.briefDate === today
          && Number(row.unreadCount) > 0);

        if(brief?.threadId) {
          // Remembered before opening, exactly as openSparkConversation does: getThreads prefers
          // set_thread_id, so without this the next refresh would abandon the brief.
          this.set_thread_id = brief.threadId;
          this.openThread(brief.threadId);
          return true;
        }

        return false;
      } catch (error) {
        // Swallowed on purpose — see the note above. There is no user action here to report against.
        return false;
      }
    },
    async getChannels() {
      if(this.can_have_channels) {
        const self = this;
        this.channel_threads_processing = true;
        const dealerThreadDTO = `dealer/${this.dealer_guid}`;
        await this.mainStore.GetThreads(dealerThreadDTO).then(response => {
          self.channel_threads = self.$FUSarrayHasEntries(response)?response:[];
          self.channel_threads.forEach(x => {
            if(x.threadId) {
              let unreadCount = x.unreadCount || 0;
              // Server unreadCount is the absolute count for this thread — assign, never accumulate.
              // A targeted refetch (reconnect, invite-triggered) skips the initializeThreads reset,
              // so a `+=` here would stack the server value onto the existing count and double it.
              self.unreads_by_thread_id[x.threadId] = unreadCount;
              self.thread_id_to_last_seen_thread_msg_id[x.threadId] = x.lastSeenThreadMsgId;
            }
          });
          self.channel_threads_processing = false;
        }, error => {
          self.channel_threads_processing = false;
          self.channel_threads_error = self.$t('messaging.window.errors.retrievingChannels');
        });
      }
    },
    deleteThread() {
      // Dual-mount scoping — see the MESSENGER-* note in mounted(). Without this the Spark instance
      // also fires and deletes ITS selected_thread, which is the user's Rey AI thread: the server
      // authorizes it (the user is that thread's CreatedByUserId) and drops the thread doc, every
      // message, and all participant records, so deleting an internal chat silently wiped the whole
      // Rey conversation history and the next load minted an empty replacement thread.
      if (this.page_type !== 'InternalMessaging') return;
      if (!this.selected_thread || this.is_deleting_thread) return;
      const self = this;
      this.is_deleting_thread = true;
      this.mainStore.DeleteThread(this.selected_thread).then(() => {
        self.selected_thread = null;
        self.internal_messaging_state = null;
        self.getChannels();
        self.getUserThreads(false);
        self.getObjectThreads(false);
        self.is_deleting_thread = false;
      }, error => {
        self.is_deleting_thread = false;
        console.error('DeleteThread failed:', error);
        self.messages_error = self.$t('messaging.window.errors.deleteThread');
      });
    },
    leaveThread() {
      // Dual-mount scoping — see the MESSENGER-* note in mounted(). Unguarded, the Spark instance
      // tried to leave the Rey AI thread; the server rejects it (its creator can't leave), and the
      // rejection surfaced as a "Unable to leave this conversation." error over the AI chat pane.
      if (this.page_type !== 'InternalMessaging') return;
      if (!this.selected_thread || this.is_leaving_thread) return;
      const self = this;
      // Capture the thread type before selection clears so the success handler refetches the list the
      // thread belongs to — channels, object threads, and DMs load from separate endpoints (same
      // routing the invite handler uses). Without this a left thread/DM would linger until a refresh.
      const navMeta = this.active_thread_nav_meta;
      const isChannel = Boolean(navMeta?.isChannel);
      const isObjectThread = !isChannel && Boolean(navMeta?.objectType);
      this.is_leaving_thread = true;
      this.mainStore.LeaveThread(this.selected_thread).then(() => {
        self.selected_thread = null;
        self.internal_messaging_state = null;
        if (isChannel)
          self.getChannels();
        else if (isObjectThread)
          self.getObjectThreads(false);
        else
          self.getUserThreads(false);
        self.is_leaving_thread = false;
      }, error => {
        self.is_leaving_thread = false;
        console.error('LeaveThread failed:', error);
        self.messages_error = self.$t('messaging.window.errors.leaveConversation');
      });
    },
    removeParticipant(payload) {
      // Owner/enterprise-admin removes another participant from the open thread. Triggered from the
      // Chat Details per-row kebab via the event bus (payload: { userId }).
      // Dual-mount scoping — see the MESSENGER-* note in mounted(). Chat Details is an
      // InternalMessaging subview, so the Spark instance firing here only ever produced a stray
      // remove call against the Rey AI thread for a user who isn't in it.
      if (this.page_type !== 'InternalMessaging') return;
      const userId = payload?.userId;
      if (!this.selected_thread || !userId || this.is_removing_participant) return;
      const self = this;
      this.is_removing_participant = true;
      this.mainStore.RemoveParticipant(this.selected_thread, userId).then(() => {
        // Authoritative refresh for the actor: re-join to repopulate active_thread_participants so the
        // Chat Details roster drops the removed user immediately (the same refresh the invite flow uses).
        // Remaining viewers also receive the server's userRemovedFromThread broadcast; that path is
        // idempotent on top of this.
        self.joinThread();
        self.is_removing_participant = false;
      }, error => {
        self.is_removing_participant = false;
        console.error('RemoveParticipant failed:', error);
        self.messages_error = self.$t('messaging.window.errors.removeParticipant');
      });
    },
    addParticipantsToChannel(users) {
      if (!this.selected_thread || !this.$FUSarrayHasEntries(users) || this.is_adding_participants) return;
      const self = this;
      const threadId = this.selected_thread;
      // Channels and private (object/vehicle) threads share this Add-participants flow, but use
      // different invite endpoints — object threads are not channels server-side. Return to the
      // matching use-state so Back lands on the conversation it was opened from.
      const isChannel = Boolean(this.active_thread?.isChannel);
      const useState = isChannel ? 'UseChannel' : 'UseThread';
      const invitePromise = isChannel
        ? this.mainStore.InviteToChannel({ threadId, userIds: users })
        : this.mainStore.InviteToThread({ threadId, userIds: users });
      this.is_adding_participants = true;
      invitePromise.then(() => {
        self.is_adding_participants = false;
        // Return to the conversation view and refresh the relevant thread list so new members reflect.
        self.internal_messaging_state = useState;
        if (isChannel) self.getChannels();
        else self.getObjectThreads(false);
        // Re-join the thread to refresh active_thread_participants — the invite changes the
        // server-side roster, but the list refresh only updates list metadata. Without this the
        // navbar participants panel and the details page keep showing the stale member list until
        // the thread is fully reopened.
        self.joinThread();
      }, error => {
        self.is_adding_participants = false;
        self.internal_messaging_state = useState;
        console.error('AddParticipants failed:', error);
        self.messages_error = self.$t('messaging.window.errors.addParticipants');
      });
    },
    async getObjectThreads(processing=true) {
      const self = this;
      if (processing) this.object_threads_processing = true;
      const recentThreadDTO = `user/objectThreads`;
      await this.mainStore.GetThreads(recentThreadDTO).then(response => {
        self.object_threads = self.$FUSarrayHasEntries(response)?response:[];
        self.object_threads.forEach(x => {
          if(x.threadId) {
            let unreadCount = x.unreadCount || 0;
            // Server unreadCount is the absolute count for this thread — assign, never accumulate.
            // A targeted refetch (reconnect, invite-triggered) skips the initializeThreads reset,
            // so a `+=` here would stack the server value onto the existing count and double it.
            self.unreads_by_thread_id[x.threadId] = unreadCount;
            self.thread_id_to_last_seen_thread_msg_id[x.threadId] = x.lastSeenThreadMsgId;
          }
        });
        if (processing) self.object_threads_processing = false;
      }, error => {
        if (processing) self.object_threads_processing = false;
        self.object_threads_error = self.$t('messaging.window.errors.retrievingRecent');
      });
    },
    async getUserThreads(processing=true) {
      const self = this;
      if (processing) this.user_threads_processing = true;
      const userThreadDTO = `user/${this.current_user_guid}`;
      await this.mainStore.GetThreads(userThreadDTO).then(response => {
        self.user_threads = self.$FUSarrayHasEntries(response)?response:[];
        self.user_threads.forEach(x => {
          if(x.threadId) {
            let unreadCount = x.unreadCount || 0;
            // Server unreadCount is the absolute count for this thread — assign, never accumulate.
            // A targeted refetch (reconnect, invite-triggered) skips the initializeThreads reset,
            // so a `+=` here would stack the server value onto the existing count and double it.
            self.unreads_by_thread_id[x.threadId] = unreadCount;
            self.thread_id_to_last_seen_thread_msg_id[x.threadId] = x.lastSeenThreadMsgId;
          }
        });
        if (processing) self.user_threads_processing = false;
      }, error => {
        if (processing) self.user_threads_processing = false;
        self.user_threads_error = self.$t('messaging.window.errors.retrievingDirect');
      });
    },
    resetCurrentThread() {
      if(this.selected_thread)
        this.openThread(this.selected_thread);
    },
    resetAiThread() {
      // Backs the navbar "New conversation" control (event MESSENGER-RESET-AI-THREAD).
      // Spark/Rey only: clears the SystemAI thread server-side (no demo conversation,
      // unlike MessengerInput.resetThread's debug reset) and reloads the now-empty thread.
      const self = this;
      if(this.page_type !== 'Spark' || this.is_resetting_ai_thread) return;
      this.is_resetting_ai_thread = true;
      this.mainStore.ResetAIThread('Fusion').then(() => {
        self.is_resetting_ai_thread = false;
        self.resetCurrentThread();
      }, error => {
        self.is_resetting_ai_thread = false;
        // [NPM-71] This branch used to only clear the flag, so a failed reset was
        // indistinguishable from a slow one — itself a reason users click again.
        // Mirrors deleteThread/leaveThread, which already surface a message.
        console.error('ResetAIThread failed:', error);
        self.messages_error = self.$t('messaging.window.errors.resetConversation');
      });
    },
    startDraftConversation() {
      // Backs the navbar "+" control (event SPARK-NEW-CONVERSATION). Spark/Rey only.
      //
      // Entirely local: no request is made here. The conversation becomes real in Cosmos on the
      // first question (materializeDraftConversation), so a user who opens one and wanders off
      // leaves nothing behind. This is what distinguishes "+" from resetAiThread below, which
      // destroys the messages on the thread the user is already in.
      if(this.page_type !== 'Spark') return;

      // End any AI turn still streaming into the conversation we are leaving — same call
      // openThread makes on a thread switch, and for the same reason.
      this.mainStore.closeAiChat('user_closed');
      this.message_intersection_observer?.disconnect();

      this.messages = [];
      this.messages_error = '';
      // A draft has nothing to load, so no load still pending for the thread being left may hold its
      // loader up. This is not hypothetical: on a cold open the host pins its default thread, created()
      // raises this flag in anticipation of loading it, and the landing rules can then open a draft
      // instead — which left an endless spinner. The same flag gates message_send_ready, so the
      // composer was stuck shut behind it too.
      this.messages_processing = false;
      this.reply_to_message = null;
      this.pending_feedback_task_id = null;
      // A draft IS the messages view. created() only sets this when the panel was launched WITH a
      // thread id, so on a panel opened without one (no ?thread_id=, nothing pinned by a host) the
      // right column is still hidden and "+" would show a blank pane with no composer. openThread
      // sets it for every other way into a conversation; the draft has to set it itself because it
      // deliberately never calls openThread.
      this.active_panel = 'messages';
      // The zero-GUID placeholder, not null: the message area and the composer are both gated on
      // `selected_thread` being truthy, so null would leave the user looking at an empty pane with
      // nothing to type into. openThread already treats this id as "not a real thread".
      this.selected_thread = this.placeholder_thread_id;
      this.last_seen_message = { threadMsgId: -1 };
      this.spark_draft_active = true;
      // A new draft, even when one was already open — see draft_generation.
      this.draft_generation += 1;

      // Forget the thread id this panel was opened with. getThreads() prefers set_thread_id over
      // selected_thread, so any later refresh — a socket retry, webSocketTryAgain — would abandon
      // the draft and reopen whatever thread the URL (or the host) originally pinned. With it null
      // that refresh calls openThread(placeholder), which is a no-op.
      this.set_thread_id = null;
    },
    openSparkConversation(threadId) {
      // Backs a row click in the Rey conversation list (event SPARK-OPEN-CONVERSATION).
      // Guarded to Spark for the same dual-mount reason openThreadFromEvent is guarded to
      // InternalMessaging — see the SCOPING RULE note in mounted().
      if(this.page_type !== 'Spark') return;
      if(!threadId || threadId === this.placeholder_thread_id) return;

      // Whatever another surface had queued for this panel is settled by this open: it is either that
      // request arriving (RequestSparkConversationOpen emits this event) or the user choosing
      // something else since. Left set, the next getThreads would reopen it over their choice.
      this.spark_pending_open_thread_id = null;

      // Opening a real conversation abandons any unsaved draft. Nothing to clean up server-side
      // precisely because a draft was never written.
      this.spark_draft_active = false;
      if(threadId === this.selected_thread) return;

      // Re-point the remembered id BEFORE opening, exactly as healSystemAiThread does: getThreads()
      // prefers set_thread_id over selected_thread, and set_thread_id is seeded once in created()
      // from the thread_id the panel was launched with and never cleared. Without this the next
      // refresh — a socket retry, or the initial fetchThreadsWhenReady still in flight — snaps
      // straight back to that launch thread and the user's pick silently reverts.
      this.set_thread_id = threadId;
      this.openThread(threadId);
    },
    handleSparkConversationDeleted(threadId) {
      // The conversation list deleted a conversation and it was the one open here. Same recovery the
      // threadDeleted broadcast triggers, reached directly so it still happens when the socket is
      // down — handleThreadDeleted is idempotent, so both firing is harmless.
      if(this.page_type !== 'Spark') return;
      if(!threadId || threadId !== this.selected_thread) return;
      this.handleThreadDeleted({ threadId });
    },
    async materializeDraftConversation(message = '') {
      // Turns the client-side draft into a real thread, immediately before the first message is
      // sent. Returns true when there is a thread to send to — including the ordinary case where
      // no draft was open at all.
      //
      // spark_draft_active lives in the store, so it is visible to BOTH mounted MessengerWindows.
      // Without the page_type guard, sending an ordinary internal message while a Rey draft was
      // open would create a Rey conversation and redirect that message into it.
      if(this.page_type !== 'Spark' || !this.spark_draft_active) return true;

      // It ALSO returns false when the user left this draft while its conversation was being created
      // (opened another conversation, or started another New Topic). The question is not dropped
      // then — the composer has already cleared, so this send holds the only copy, and a conversation
      // created without it would be the very blank row a draft exists to prevent. It is handed to the
      // new conversation in the background (sendToBackgroundConversation) and the view is left where
      // the user put it, so false means "nothing left for the view send to do", not only "failed".
      //
      // What this question was sent from, fixed now, before the awaits below: which draft (see
      // draft_generation), and the dealer scope and scheduled anchor it was asked under — the
      // thread-switch watcher resets the scope to whatever conversation the user moves to.
      const draftGeneration = this.draft_generation;
      const sendContext = this.message_context;
      const sendMetadata = this.outboundScheduledAnchor();

      // ONE list read, serving both decisions below — whether there is an empty thread to adopt, and
      // whether Recent has room for a new one — so the first send of a draft still costs the single
      // request it always did.
      let conversations = null;
      try {
        const rows = await this.getSparkConversationsForCap();
        conversations = Array.isArray(rows) ? rows : [];
        // Kept for the same reason ensureRecentCapRoom keeps it: a fresh authoritative copy of the
        // list the conversation drawer renders.
        this.spark_conversations = conversations;
      } catch (error) {
        // A list we could not read is not a reason to refuse someone's question. Left null, which
        // both decisions below read as "no information" and handle on their own terms.
        console.error('GetReyConversations failed before draft create:', error);
      }

      // Adopt an empty thread rather than creating a second one beside it. This is the brand-new
      // user: they had no Rey thread at all, so the landing resolve had to create one for them, and
      // it is still empty because the landing rules put them on a New Topic draft rather than in it.
      // Creating here would leave that thread blank forever — a row in their list they never made,
      // which is the whole complaint the draft mechanism exists to avoid.
      const adoptableThreadId = this.adoptableHostDefaultThreadId(conversations);
      if(adoptableThreadId) {
        // Normally already present — it is the host's own thread and getThreads fetched it — so this
        // is only the guard for the case where it is not. See the call below for why the map matters.
        if(!this.all_threads_by_thread_id?.[adoptableThreadId])
          await this.ensureSystemAiThreadInObjectList(adoptableThreadId);
        // Left the draft during the list read: deliver the question there without moving them.
        if(!this.isSameDraft(draftGeneration)) {
          this.sendToBackgroundConversation(adoptableThreadId, message, sendContext, sendMetadata);
          return false;
        }
        // The switch comes after every await, for the reason given on the create path below.
        // The draft's dealer scope carries over, as it does there — see keep_scope_for_thread_id.
        this.keep_scope_for_thread_id = adoptableThreadId;
        this.selected_thread = adoptableThreadId;
        this.spark_draft_active = false;
        this.set_thread_id = adoptableThreadId;
        // No notifyHostOfAiThreadChange: this IS the thread the host already has.
        return true;
      }

      // Recent holds a fixed number of conversations, and this is the moment one would go over it.
      // Asked here rather than when "+" was clicked for the same reason the create itself is: "+"
      // creates nothing, so a draft the user abandons must not have cost them a conversation.
      // Returns false when the user declined, which aborts the send exactly as a failed create does.
      if(!(await this.ensureRecentCapRoom(conversations))) return false;

      try {
        const response = await this.mainStore.CreateReyConversation('Fusion');
        const newThreadId = response?.value?.id;
        // A 200 does not imply success here: CreateReyConversationAsync returns a message-only
        // payload on failure and null members are omitted from the JSON. Same guard healSystemAiThread
        // applies to GetAIThread.
        if(!newThreadId || newThreadId === this.placeholder_thread_id) {
          // The error is only the view's to show while the user is still on the draft it is about.
          // Painted after they left, it would sit over a conversation that has nothing wrong with it.
          if(this.isSameDraft(draftGeneration))
            this.messages_error = this.$t('rey.conversations.createError');
          else
            console.error('CreateReyConversation returned no thread id for a draft the user has left:', response);
          return false;
        }

        // The thread is brand new, so all_threads_by_thread_id has never seen it — and
        // active_thread_is_system_ai reads straight out of that map. Without this the rest of the
        // send, and every render after it, would treat the user's Rey conversation as an ordinary
        // internal-messaging thread: Rey's answers are filtered out of the list, threadContext
        // stops being attached, and cancel becomes a no-op. This helper already handles the
        // eventually-consistent read with a retry and a local seed.
        await this.ensureSystemAiThreadInObjectList(newThreadId);

        // Left the draft while its conversation was being created. Switching the view now would pull
        // them out of wherever they went — and into a conversation this path never loads, so the load
        // of the one they opened would arrive "stale", be dropped, and strand its loader.
        if(!this.isSameDraft(draftGeneration)) {
          this.sendToBackgroundConversation(newThreadId, message, sendContext, sendMetadata);
          return false;
        }

        // The switch is the LAST step, with no await after it (the refresh above used to follow it).
        // sendMessage resumes the instant this returns, so nothing can move the user between the
        // switch and the send reading which thread it is for.
        //
        // The switch does still run the selected_thread watcher before that read, and the watcher
        // resets the dealer scope on every switch — here to the host page's store, since a thread
        // created a moment ago has no dealership on record. This is the draft becoming real, not a
        // move to another conversation, so the scope the user picked on it stays, for this send and
        // the ones after it. See keep_scope_for_thread_id.
        this.keep_scope_for_thread_id = newThreadId;
        this.selected_thread = newThreadId;
        this.spark_draft_active = false;
        // Same reason as openSparkConversation: a refresh that prefers set_thread_id must land on
        // the conversation just created, not the one this panel was launched with.
        this.set_thread_id = newThreadId;

        // Tell the host a conversation was created, marked as such so it isn't taken for a heal.
        this.notifyHostOfAiThreadChange(newThreadId, 'new-topic');
        return true;
      } catch (error) {
        console.error('CreateReyConversation failed:', error);
        // Same rule as the no-id branch above.
        if(this.isSameDraft(draftGeneration))
          this.messages_error = this.$t('rey.conversations.createError');
        return false;
      }
    },
    // Whether the New Topic draft a send came from is still the one on screen. See draft_generation.
    isSameDraft(draftGeneration) {
      return this.spark_draft_active && this.draft_generation === draftGeneration;
    },
    /**
     * Sends a New Topic's first question to the conversation it just became, once the user has left
     * that New Topic — see the note at the top of materializeDraftConversation.
     *
     * The same message sendMessage builds, minus everything that belongs to the view: there is no
     * optimistic insert (the list on screen belongs to another conversation), no composer lock (the
     * user is typing somewhere else), and a failure is only logged (nothing on screen to roll back).
     *
     *   - threadMsgId 0: a freshly created or adopted conversation is empty by construction, and 0 is
     *     what the view path computes from the draft's -1 watermark anyway.
     *   - context / metadata: as captured when send was pressed. The live values now describe the
     *     conversation the user moved to.
     *   - threadContext: the last page context the host broadcast, NOT a fresh requestUpdatedContext.
     *     That request is a single shared slot (pending_context_request) the view the user is now in
     *     may need for its own send, and thread_context is exactly what it falls back to on timeout.
     *
     * The new conversation appears in the conversation list, which refetches whenever it opens.
     */
    sendToBackgroundConversation(threadId, message, sendContext, sendMetadata) {
      const clientMessageId = (crypto?.randomUUID?.() || `msg-${Date.now()}-${Math.random()}`);
      const messageSendDTO = {
        id: clientMessageId,
        threadId: threadId,
        senderId: this.current_user_guid,
        content: message,
        threadMsgId: 0,
        context: sendContext,
        threadContext: this.thread_context || null,
        metadata: sendMetadata,
      };

      // The same event the view send fires. Unconditional on the thread type because this is always
      // a Rey conversation — the only kind a draft becomes.
      if(this.rey_session_id) {
        window.Analytics?.enqueue('chat_message_sent', {
          session_id: this.rey_session_id,
          conversation_id: threadId,
          message_id: clientMessageId,
        });
        this.incrementAiChatMessageCount();
      }

      this.messageSend(messageSendDTO).then(null, error => {
        console.error('Background send of a New Topic question failed:', error);
      });
    },
    // Store wrappers for the background send, per the component-wrapper rule. sendMessage makes the
    // same two calls directly; those are left as they are.
    messageSend(messageSendDTO) {
      return this.mainStore.MessageSend(messageSendDTO);
    },
    incrementAiChatMessageCount() {
      this.mainStore.incrementAiChatMessageCount();
    },
    /**
     * Whether the host's default Rey thread is an empty conversation this draft should take over
     * instead of creating a new one, as a thread id — or null when it is not.
     *
     * Pure, and given the list rather than fetching it, so the rule is a unit test rather than a
     * network fixture.
     *
     * Only ever the HOST DEFAULT, never "any empty conversation in the list". An empty thread the
     * user made themselves is one they may be about to come back to from the drawer, and quietly
     * filling it from somewhere else would move their question to a thread they were not looking at.
     * The host default is different in kind: nobody chose it, the landing resolve named it only
     * because a first-time caller had to be given something.
     *
     * Empty means no message with content has ever been in it — a brief, the scheduled thread, and a
     * pinned conversation are all excluded outright, whatever their message count, because adopting
     * one would put a user's question somewhere it visibly does not belong.
     */
    adoptableHostDefaultThreadId(conversations) {
      const threadId = this.host_default_thread_id;
      if(!threadId || !Array.isArray(conversations)) return null;

      const row = conversations.find(conversation => conversation && conversation.threadId === threadId);
      if(!row) return null;
      if(row.isScheduled || row.isBrief || row.isPinned) return null;
      // Both, not either: the preview is null for a thread whose only messages were empty-content,
      // and the timestamp is the field the list itself sorts on.
      if(row.lastMessageAt || row.lastMessagePreview) return null;
      if(Number(row.unreadCount) > 0) return null;

      return threadId;
    },
    /**
     * Makes room in Recent for one more conversation, asking the user first.
     *
     * Resolves true when the create may go ahead — either there was room, or the user agreed to give
     * up their oldest conversation and it was deleted — and false when it must not: the user said no,
     * or the delete they agreed to failed.
     *
     * The list is REFETCHED rather than read out of the store. spark_conversations is only populated
     * when ReyConversationList mounts, so a user who opens Spark and asks something straight away
     * has an empty cached list, and a cap check against an empty list is one that never fires. The
     * cost is one request, on the first send of a draft only.
     *
     * <paramref name="prefetchedConversations"/> is that same authoritative read, when the caller has
     * already made it — materializeDraftConversation needs the list first to decide whether there is
     * an empty thread to adopt. Passing it through keeps the first send at one request rather than
     * two. Anything that is not an array (including the null a failed read leaves behind) falls back
     * to fetching, so the no-argument call is unchanged.
     */
    async ensureRecentCapRoom(prefetchedConversations = null) {
      try {
        let conversations = prefetchedConversations;
        if(!Array.isArray(conversations)) {
          const rows = await this.getSparkConversationsForCap();
          conversations = Array.isArray(rows) ? rows : [];
          // Kept: this is a fresh authoritative read of the same list the conversation list renders,
          // and leaving the stale copy in place would only make the next cap check wrong again.
          this.spark_conversations = conversations;
        }

        if(!wouldExceedRecentCap(conversations, 1)) return true;

        const victim = oldestUnpinnedRow(conversations);
        // Nothing unpinned to free, so there is no question worth asking. The server refuses over
        // the cap independently, and that refusal already surfaces as createError above.
        if(!victim || !victim.threadId) return true;

        return await this.askToFreeRecentSlot(victim);
      } catch (error) {
        // A list we could not read is not a reason to refuse someone's question. The server enforces
        // the cap on its own, so the worst case is that the refusal arrives as createError instead of
        // as this dialog.
        console.error('GetReyConversations failed during cap check:', error);
        return true;
      }
    },
    // Opens the dialog and parks the send until a button is pressed. Nothing else in this component
    // awaits user input, so there is no existing helper for this to reuse.
    askToFreeRecentSlot(victim) {
      const self = this;
      this.cap_create_error = false;
      this.pending_cap_create_victim = victim;

      return new Promise(resolve => {
        self.cap_create_decision = resolve;
      });
    },
    cancelCapCreate() {
      // Ignored mid-delete for the same reason the list's cancel is: the delete is already in flight
      // server-side and cannot be called off, so closing here would hide an operation that is still
      // going to happen.
      if(this.is_resolving_cap_create) return;
      this.settleCapCreate(false);
    },
    async confirmCapCreate() {
      const victim = this.pending_cap_create_victim;
      if(!victim || !victim.threadId || this.is_resolving_cap_create) return;

      const victimId = victim.threadId;
      this.is_resolving_cap_create = true;
      this.cap_create_error = false;

      try {
        await this.deleteCappedConversation(victimId);
        this.is_resolving_cap_create = false;

        // Drop the row locally, as the list's own delete does. No SPARK-CONVERSATION-DELETED here:
        // that event exists to recover a surface pointing at a destroyed thread, and the thread open
        // behind this dialog is the draft — the victim can never be the conversation on screen.
        this.spark_conversations = (this.spark_conversations || [])
          .filter(row => row.threadId !== victimId);

        this.settleCapCreate(true);
      } catch (error) {
        this.is_resolving_cap_create = false;
        // Dialog stays open with the error and the send does NOT proceed: the confirmation exists to
        // keep the cap honoured, and creating anyway on a failed delete would break exactly the thing
        // the user was asked about.
        this.cap_create_error = true;
        console.error('DeleteThread failed:', error);
      }
    },
    // One exit for both buttons: closes the dialog and hands the answer back to the parked send.
    // The resolver is cleared first so a stray click after it has settled cannot resolve twice.
    settleCapCreate(allowed) {
      const resolve = this.cap_create_decision;
      this.cap_create_decision = null;
      this.pending_cap_create_victim = null;
      this.cap_create_error = false;
      if(resolve) resolve(allowed);
    },
    // The same label rule ReyConversationList.displayTitle applies, deliberately a second copy in
    // the component that renders it rather than a shared helper: the rule is three localized
    // strings, and the i18n spec finds keys by scanning for a literal $t('...') in the file that
    // uses them. Both copies are renderings of one product decision, so both change together.
    sparkConversationLabel(conversation) {
      const name = (conversation && conversation.name ? conversation.name : '').trim();
      if(!name) return this.$t('rey.conversations.untitled');
      if(name.indexOf('Spark AI:') === 0) return this.$t('rey.conversations.legacyThread');
      return name;
    },
    // A morning brief's label: the day it covers, in the viewer's locale. Written with an empty name,
    // so the rule above would call it untitled.
    //
    // Parsed into local parts rather than handed to Date() whole. A bare date string is read as UTC,
    // which shifts it back a day for anyone west of Greenwich — so a brief produced on the owner's
    // Monday would label itself Sunday. The same trap the thread's own day separators document.
    //
    // Falls back to the generic label rather than a blank when the date is missing or malformed, which
    // is true of nothing this code writes but is cheap to survive.
    //
    // Another copy of a localized label rule, for the reason sparkConversationLabel above is one: the
    // i18n spec finds keys by scanning for a literal $t('...') in the file that uses them.
    // ReyConversationList.briefTitle is the sibling copy, and both render the same product decision —
    // so both change together.
    briefThreadLabel(dayKey) {
      const day = (dayKey || '').trim();
      if(!day) return this.$t('rey.conversations.briefThread');

      const parts = day.split('-').map(Number);
      if(parts.length !== 3 || parts.some(Number.isNaN)) return this.$t('rey.conversations.briefThread');

      const [year, month, dayOfMonth] = parts;
      const covered = new Date(year, month - 1, dayOfMonth);
      if(Number.isNaN(covered.getTime())) return this.$t('rey.conversations.briefThread');

      return this.$t('rey.conversations.briefThreadOn', {
        date: covered.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }),
      });
    },
    // Store wrappers for the cap flow, per the component-wrapper rule. Both actions are wrapped
    // elsewhere for their own callers; these are this flow's, and the duplication is intentional.
    getSparkConversationsForCap() {
      return this.mainStore.GetReyConversations('Fusion');
    },
    // Store wrappers for the startup landing flow, same rule. Neither adds logic of its own — the
    // store already caches the read and no-ops the unchanged write — but the call sites are in a
    // decision method and a watcher, which are exactly the places the rule exists to keep clean.
    ensureWelcomeGate() {
      return this.mainStore.EnsureWelcomeGate();
    },
    ensureReyLastThread() {
      return this.mainStore.EnsureReyLastThread();
    },
    rememberReyLastThread(threadId) {
      return this.mainStore.RememberReyLastThread(threadId);
    },
    deleteCappedConversation(threadId) {
      return this.mainStore.DeleteThread(threadId);
    },
    openThread(threadId) {
      if(threadId !== this.placeholder_thread_id) {
        if(threadId !== this.selected_thread) {
          // WS-5: end active AI conversation before switching threads
          this.mainStore.closeAiChat('user_closed');

          this.message_intersection_observer?.disconnect();
          this.messages = [];
          // Drop the typing buffers of finished answers along with the list. The list is refetched with each
          // answer's saved text, but MessageItem prefers a buffer when one exists: it re-typed every finished
          // answer on each return, and an answer that finished while its thread was off screen showed the
          // stale buffer (only the preamble — its later chunks were dropped as off-thread) instead of the
          // saved text. Unfinished buffers belong to turns still streaming and are kept.
          Object.keys(this.streaming_display_buffers).forEach(messageId => {
            if (this.streaming_display_buffers[messageId]?.isComplete)
              delete this.streaming_display_buffers[messageId];
          });
          // Clear the prior channel's member list so a stale roster never shows during the
          // brief window before the new thread's join response repopulates it. selected_thread_users_list
          // is cleared alongside it because it feeds active_thread_participant_ids (the @ mention
          // roster) and would otherwise briefly offer the previous thread's participants.
          this.active_thread_participants = [];
          this.selected_thread_users_list = [];
          // The adjust anchor belongs to the card it was clicked on, so it does not survive leaving
          // that thread. Without this it did: outboundScheduledAnchor prefers feedbackForTaskId over
          // everything else, so the next message sent from ANY conversation was filed as wording
          // feedback on the scheduled task and the question itself was never answered.
          this.pending_feedback_task_id = null;
        }

        this.clearErrors();
        this.selected_thread = threadId;
        this.sent_last_message_from_this_tab = false;
        this.last_sent_thread_msg_id = null;
        this.active_panel = 'messages';
        // Populate navbar avatar state — looked up here so the 'All' case can branch by type.
        const openedThread = this.all_threads_by_thread_id[threadId];
        if(this.page_type === 'InternalMessaging') {
          // Set internal_messaging_state to a back-button state derived from the opened thread's
          // own TYPE, not the active sub-tab. Each sub-tab normally lists only its own type, so for
          // in-tab opens this matches the previous tab-based mapping exactly; it only differs for
          // cross-type opens (e.g. a DM opened from a contact's Shared Chats while the Channels or
          // Threads tab is active), where keying off the tab wrongly dressed the DM in channel/
          // thread chrome (blank avatar, "Delete Channel" label). Fall back to the active sub-tab
          // only when the thread isn't in our cached lists yet. Always set a state — leaving it null
          // while selected_thread is set makes the message_header watcher set header_override truthy
          // WITHOUT a matching back-button state, so the navbar's v-if branches all fail and it
          // renders blank (the ERA bug seen with the 'All' sub-tab plus a thread_id URL auto-open).
          if (openedThread?.isChannel)
            this.internal_messaging_state = 'UseChannel';
          else if (openedThread?.objectType)
            this.internal_messaging_state = 'UseThread';
          else if (openedThread)
            this.internal_messaging_state = 'UseMessage';
          else {
            switch (this.messageType) {
              case 'Channels':
                this.internal_messaging_state = 'UseChannel';
                break;
              case 'Threads':
              case 'Support':
                this.internal_messaging_state = 'UseThread';
                break;
              default:
                this.internal_messaging_state = 'UseMessage';
                break;
            }
          }
        }
        if (openedThread && !openedThread.isChannel && !openedThread.objectType) {
          // DMs: stacked user avatars
          this.active_thread_participant_guids = (openedThread.participantIds || []).filter(id =>
            id !== this.current_user_guid && id.indexOf('system_') < 0
          );
          this.active_thread_nav_meta = {
            createdByUserId: openedThread.createdByUserId || null,
            isChannel: false,
            isPublic: true,
            objectType: null,
            avatarBgColor: null,
            name: null,
            // Non-system member count (including self); Navbar's is_group_dm uses it to offer Leave
            // only on DMs with more than two people.
            participantCount: (openedThread.participantIds || []).filter(id => id.indexOf('system_') < 0).length,
            // Group-originated marker (incl. groups that have shrunk to two people) so the header can
            // tag the DM as a group and keep it distinct from a native 1-on-1.
            isGroup: this.isGroupOriginatedThread(openedThread),
          };
        } else {
          this.active_thread_participant_guids = [];
          if (openedThread) {
            const avatarBgColor = openedThread.isChannel
              ? (this.internal_message_channels_to_colors[threadId] || '#A9B3CA')
              : (['Appraisal', 'InventoryVehicle'].includes(openedThread.objectType) ? '#E3FFF1' : null);
            this.active_thread_nav_meta = {
              name: openedThread.name || null,
              objectType: openedThread.objectType || null,
              isChannel: !!openedThread.isChannel,
              isPublic: !!openedThread.isPublic,
              createdByUserId: openedThread.createdByUserId || null,
              avatarBgColor,
            };
          } else {
            this.active_thread_nav_meta = null;
          }
        }
        this.joinThread();
      }
    },
    openThreadFromEvent(threadId) {
      // Entry point for other messenger subviews (e.g. EnterpriseContactDetails' Shared chats)
      // that want to open a thread without owning openThread's join/load/nav-state logic.
      // openThread flips internal_messaging_state to a back-button state, which unmounts the
      // calling subview and reveals this (still-mounted) window.
      // MESSENGER-OPEN-THREAD is a global bus event, so BOTH mounted MessengerWindow instances
      // (the Spark/AI body and the InternalMessaging body) receive it. Only the InternalMessaging
      // instance should act — the hidden Spark window opening an internal thread would needlessly
      // join/load it and swap its own selected_thread (mirrored to spark_active_thread_id) in the background.
      if(this.page_type !== 'InternalMessaging') return;
      if(!threadId) return;
      // If this open is a shared-chat detour launched from a contact profile that was itself opened
      // from the Chat/Thread Details page, snapshot the conversation we're leaving BEFORE openThread
      // overwrites the live thread context (nav meta, roster, selected thread). Backing out of the
      // contact profile restores the details page from this snapshot with no reload; the thread id
      // is kept so the actual re-open can be deferred to the moment the user re-enters the chat.
      if(this.contact_origin_view === 'ChatDetails' && this.selected_thread) {
        this.chat_details_thread_id = this.selected_thread;
        this.chat_details_nav_snapshot = {
          navMeta: this.active_thread_nav_meta ? { ...this.active_thread_nav_meta } : null,
          participants: (this.active_thread_participants || []).slice(),
          participantGuids: (this.active_thread_participant_guids || []).slice(),
        };
      }
      this.openThread(threadId);
    },
    notifyHostToOpenSupportChat(threadId) {
      if (!threadId) return;
      // Dedupe duplicate WS delivery so a repeated 'Support' invite doesn't re-post to the host.
      const now = Date.now();
      if (!this._recent_support_host_posts) this._recent_support_host_posts = new Map();
      for (const [k, ts] of this._recent_support_host_posts) {
        if (now - ts > 4000) this._recent_support_host_posts.delete(k);
      }
      if (this._recent_support_host_posts.has(threadId)) return;
      this._recent_support_host_posts.set(threadId, now);
      // Blade hosts (WingCommander extension, ERA/FOCUS) load Rey and Internal Messages as separate
      // iframes, so the in-SPA jump can't reach the Internal Messages surface. Post up to the host so
      // it can activate the Internal Messages blade at fusion/messages?thread_id=<threadId>, whose
      // deep-link (FusionMain reads ?thread_id) auto-opens the thread.
      //
      // This post is NOT suppressed in the toolbar layout — it fires from any Spark window, host or
      // not. It doesn't need to be, because the two jump paths can't currently coexist: FusionToolbar
      // (which owns the in-SPA jump) is only mounted under entryType TOOLBAR, while blade hosts run
      // entryType CONTROLLER, where each blade is its own iframe rendering a single FusionBody and no
      // toolbar. So in a blade host this post is the only path, and in the toolbar layout the parent
      // has no SUPPORT-CHAT-STARTED listener and the post is inert.
      // If the toolbar is ever embedded in a host that DOES listen for SUPPORT-CHAT-STARTED, that
      // invariant breaks and the user gets both a blade switch and a tab switch — at which point this
      // needs a real gate on which layout is running (entryType isn't threaded down this far today).
      this.$FUSpostFusionMessage('SUPPORT-CHAT-STARTED', { threadId });
    },
    restoreChatDetails() {
      // Re-show the Chat/Thread Details page after a shared-chat detour WITHOUT a network reload:
      // restore the conversation's nav meta + roster from the snapshot taken when the detour began.
      // The live thread context still points at the shared chat; that is only re-opened later, when
      // the user backs out of the details page into the conversation (see Navbar.goBack).
      // Dual-mount scoping — see the MESSENGER-* note in mounted(). The snapshot is only ever taken
      // by the InternalMessaging instance, so the Spark instance had no snapshot to restore and just
      // clobbered the shared internal_messaging_state with 'ChatDetails'.
      if (this.page_type !== 'InternalMessaging') return;
      const snapshot = this.chat_details_nav_snapshot;
      if(snapshot) {
        this.active_thread_nav_meta = snapshot.navMeta;
        this.active_thread_participants = snapshot.participants;
        this.active_thread_participant_guids = snapshot.participantGuids;
      }
      this.internal_messaging_state = 'ChatDetails';
    },
    joinThread() {
      const self = this;
      const joinThreadDTO = {
        threadId: this.selected_thread,
        userId: this.current_user_guid,
      };
      
      this.messages_processing = true;
      this.messages_error = '';
      this.$nextTick(() => this.scrollToLastMessageIfClose());
      self.mainStore.ThreadsJoin(joinThreadDTO).then(response => {
        // Guarded like getThreadMessages below, and it has to be: this handler goes on to START that
        // load for whatever is open when it lands, so a stale join left unguarded would aim a fresh
        // load at the new view — on a New Topic, at the all-zeros id. See dropStaleThreadLoad.
        if(self.dropStaleThreadLoad(joinThreadDTO.threadId)) return;
        if(response && self.$FUSarrayHasEntries(response.users)) {
          self.selected_thread_users_list = response.users;
          // Feed the navbar participants panel. The join response resolves every member of the
          // thread's ParticipantIds to { id, name, email, avatar }.
          self.active_thread_participants = response.users;
        }
        // A missing thread SOFT-SUCCEEDS here: JoinThread returns { message: "Thread {id} not
        // found.", id } and ThreadsController wraps it in Ok(), so this handler runs with HTTP 200
        // and NO users. A real join always resolves one entry per ParticipantIds member, so "200
        // with no users" is a structural signal the thread is gone. Catching it here — before
        // getThreadMessages(), whose 404 is what sets 'Error Loading Messages' — is what keeps the
        // error off screen entirely. Non-Spark surfaces fall through to the original behaviour.
        else if(self.healSystemAiThread(joinThreadDTO.threadId)) {
          return;
        }
        // Keep the DM nav meta's participantCount in step with the freshly-resolved roster so
        // Navbar's is_group_dm reflects live add/remove without waiting for a reopen.
        self.syncDmParticipantCount();
        self.getThreadMessages();
        self.updateMessagesColumnHeight();
      }, error => {
        console.error('Error:', error);
        // Stale: the error belongs to a thread the user has left. Painting it would put "unable to
        // join" over the view they are in now, and clearing create_thread_message would throw away
        // the pending first message of the thread they just created.
        if(self.dropStaleThreadLoad(joinThreadDTO.threadId)) return;
        self.messages_error = self.$t('messaging.window.errors.joiningThread');
        self.create_thread_message = '';
        self.messages_processing = false;
      });
    },
    /**
     * Whether a join or message-load response is for a thread the user has since left — and if so,
     * settles the loader the way that load would have. Returns true when the caller must drop the
     * response without touching anything else.
     *
     * joinThread and getThreadMessages are one asynchronous load, and the user can open another
     * thread, start a New Topic or go Back before it lands. Their handlers read selected_thread when
     * the response ARRIVES, so a late one used to land on whatever view the user was in by then: the
     * old thread's messages and roster, an error banner over the new view, a pending first message
     * sent into the wrong thread — and on a New Topic, a late 404 that healed the all-zeros id and
     * pulled the user out of the draft into the host's thread.
     *
     * Keyed on the thread id rather than a request counter, for the same reason the selected_thread
     * watcher is the single place the open thread is remembered: every path that changes the open
     * thread (open, New Topic, adopt, create, delete, Back) invalidates in-flight responses by
     * itself, with nothing to remember at each site. Two loads of the SAME thread — a reopen, a
     * reconnect refetch — are both accepted, and should be: both carry that thread's data.
     *
     * <b>The loader is the subtle part.</b> Dropping a response must not drop its spinner duty:
     *   - Moved to another REAL thread: that thread's own join/load has raised the loader and will
     *     lower it. Lowering it here would drop the spinner before its messages arrive.
     *   - Moved to no thread (Back, delete, leave) or to a New Topic: nothing else will ever lower
     *     it. A guard that merely ignored the response would leave it spinning over the list — open
     *     a chat, press Back before it loads, and the loader never clears.
     */
    dropStaleThreadLoad(requestedThreadId) {
      if(this.selected_thread === requestedThreadId) return false;

      if(!this.selected_thread || this.selected_thread === this.placeholder_thread_id)
        this.messages_processing = false;

      return true;
    },
    syncDmParticipantCount() {
      // participantCount is snapshotted onto the DM nav meta when the thread opens; recompute it
      // here from the live roster (active_thread_participants) whenever that roster changes so a
      // group DM that drops to two people stops offering "Leave Conversation" (and vice-versa)
      // without needing the thread reopened. Only DM nav meta carries participantCount — channels
      // and object threads gate Leave on other flags, so leave their meta untouched.
      const meta = this.active_thread_nav_meta;
      if (!meta || meta.isChannel || meta.objectType) return;
      const count = (this.active_thread_participants || []).filter(user => user?.id && user.id.indexOf('system_') < 0).length;
      this.active_thread_nav_meta = { ...meta, participantCount: count };
    },
    getAiThread() {
      // Resolves (or creates) this user's SystemAI_Fusion thread. Same endpoint the WingCommander
      // extension calls on every page load, so both sides converge on one thread id.
      return this.mainStore.GetAIThread('Fusion');
    },
    notifyHostOfAiThreadChange(threadId, reason) {
      // Tell the host (WingCommander extension) that this user's Rey thread id changed so it can
      // re-point its caches. This is required, not cosmetic: the extension force-pins thread_id into
      // the Rey blade iframe URL from its OWN currentaiThreadId (blade.js updateIframeUrl), and
      // several paths re-assert it — a blade destroy/recreate when hasWingmanConfig flips, a
      // FUSION_SWITCH_USER reload, and its next createAIThread convergence. Without this post
      // the extension eventually reloads the iframe straight back onto the deleted thread.
      // Mirrors the SUPPORT-CHAT-STARTED handoff and is inert in hosts with no listener.
      //
      // `reason` says why, because the host must treat the two differently and can't tell them apart:
      //   - 'heal': the thread it holds was deleted and this replaces it. It adopts the id and purges
      //     every cached copy of the dead one.
      //   - 'new-topic': a New Topic's first question created a conversation beside the user's
      //     thread, which still exists. The extension ignores it: a host that asked for Rey
      //     conversations pins only the landing default (see host_default_thread_id), and landing on
      //     the last conversation is ours to do. Treated as a heal, it cost a purge of every tab's
      //     cached thread ids, reverted by the next landing resolve.
      // A host that predates the field sees neither and handles both as a heal, as before.
      if(!threadId) return;
      this.$FUSpostFusionMessage('AI-THREAD-CHANGED', { threadId, reason });
    },
    async ensureSystemAiThreadInObjectList(threadId) {
      // The heal resolves a thread id our cached lists have never seen, and openThread reads the
      // thread's metadata straight out of all_threads_by_thread_id. Without this refresh
      // active_thread is null, so active_thread_is_system_ai reads FALSE — and the Rey surface
      // silently renders as an ordinary internal-messaging thread for the rest of the session:
      // messages_displayed drops every Rey answer (they carry userSpecific + inReplyTo, which that
      // computed filters out on non-AI threads), the user's own question inherits the AI gradient
      // via MessageItem's `messageReply && !threadIsSystemAi` class, sendMessage stops attaching
      // threadContext, and cancelMessage becomes a no-op.
      //
      // Same fetch → check → delayed-retry shape startTacChat uses after creating a thread
      // (see the newThreadId block there): Cosmos is eventually consistent and the just-created
      // thread occasionally misses the first read.
      //
      // Deliberately NOT getThreads(): that short-circuits the object fetch whenever object_threads
      // is already non-empty, and it IS non-empty here — it still holds the thread we just lost.
      await this.getObjectThreads(false);
      if(!this.all_threads_by_thread_id?.[threadId]) {
        await new Promise(resolve => setTimeout(resolve, 500));
        await this.getObjectThreads(false);
      }
      if(this.all_threads_by_thread_id?.[threadId]) return true;

      // Both reads missed. startTacChat opens anyway at this point, but a TACChat thread rendering
      // as a plain thread is cosmetic whereas this failure hides every Rey answer with no error
      // shown anywhere. Seed the minimum active_thread_is_system_ai needs so that cannot happen:
      // objectType is not guessed — GetOrCreateSystemAIThread builds it as SystemAIPrefix + systemId
      // for the same 'Fusion' system id getAiThread() requests. The next getObjectThreads replaces
      // object_threads wholesale, so this partial record is superseded by the real one as soon as
      // Cosmos catches up.
      console.warn(`[MessengerWindow] Healed Rey thread ${threadId} missing from object threads after retry — seeding locally`);
      this.object_threads = [...this.object_threads, {
        threadId,
        objectType: 'SystemAI_Fusion',
        isChannel: false,
        isPublic: true,
        participantIds: [],
      }];
      return false;
    },
    healSystemAiThread(deadThreadId) {
      const self = this;
      // Recover in place when this user's Rey thread no longer exists — usual cause is an admin
      // "Reset AI Thread" in FusionMgmt, which hard-deletes the thread doc AND the user's
      // UserThreads status doc. Re-resolves a live thread id and re-enters it with no page reload,
      // so 'Error Loading Messages' is never painted.
      //
      // Returns true when this call owns the recovery (caller must stop its normal load path).
      //
      // Only the Rey/Spark surface owns a system AI thread. Deliberately NOT gated on
      // active_thread_is_system_ai: once the thread doc is deleted it drops out of object_threads,
      // so active_thread is null and that computed reads false exactly when the heal must fire.
      if(this.page_type !== 'Spark') return false;
      // A duplicate request for the SAME dead thread collapses into the heal already running — join
      // detection, the 404 backstop and a server push can all fire for one death. A request for a
      // different thread does not collapse: see heal_thread_id.
      if(this.heal_in_flight && this.heal_thread_id === deadThreadId) return true;
      if(this.unhealable_thread_ids.indexOf(deadThreadId) >= 0) return false;

      this.heal_in_flight = true;
      // Supersedes a heal for another thread if one is running. That heal is already stale — the
      // user can only be on one thread — and abandonStaleHeal makes it stand down when it lands.
      this.heal_thread_id = deadThreadId;
      // Hold the spinner and keep the error slot empty for the whole heal so the swap is invisible.
      this.messages_processing = true;
      this.messages_error = '';

      this.getAiThread().then(async response => {
        // Checked before anything below is applied — the re-point, the host notice and both loads
        // all act on the view, and the user may have left it while this was resolving.
        if(self.abandonStaleHeal(deadThreadId)) return;
        // A 200 does not imply success: GetOrCreateSystemAIThread has three paths that return a null
        // id, and null members are omitted from the JSON. Guard exactly as DebugController does.
        const newThreadId = response?.value?.id;
        if(!newThreadId || newThreadId === self.placeholder_thread_id) {
          self.unhealable_thread_ids.push(deadThreadId);
          self.heal_in_flight = false;
          self.heal_thread_id = null;
          self.messages_processing = false;
          return;
        }
        if(newThreadId === self.selected_thread) {
          // Server handed back the id we already hold. Happens when only the UserThreads status doc
          // was deleted and the thread doc survived — the join we just made re-created the
          // membership, so the thread is usable again. Load it rather than re-entering (openThread
          // would recurse through joinThread) and don't mark it unhealable.
          //
          // Still refresh first: GetUserObjectThreads is driven by the UserThreads status docs, so
          // any getObjectThreads that ran between the status-doc delete and this heal evicted the
          // entry, leaving active_thread_is_system_ai false on an id that is otherwise fine.
          await self.ensureSystemAiThreadInObjectList(newThreadId);
          // The refresh is a second await, and so a second window to have moved on in.
          if(self.abandonStaleHeal(deadThreadId)) return;
          self.heal_in_flight = false;
          self.heal_thread_id = null;
          self.getThreadMessages();
          return;
        }
        // Re-point every place the old id is remembered BEFORE re-entering: a socket retry
        // (fetchThreadsWhenReady) prefers set_thread_id and would otherwise snap back to the dead id.
        self.set_thread_id = newThreadId;
        self.notifyHostOfAiThreadChange(newThreadId, 'heal');
        // Pull the fresh thread into object_threads BEFORE openThread — it reads the thread's
        // metadata out of all_threads_by_thread_id and there is no later refresh that would
        // populate it (userJoinedThread only touches the roster). heal_in_flight stays true across
        // the await so a concurrent trigger still collapses into this recovery.
        await self.ensureSystemAiThreadInObjectList(newThreadId);
        // Same second window as the branch above. The re-point before the await is safe to leave:
        // every Spark route away writes set_thread_id itself, after it.
        if(self.abandonStaleHeal(deadThreadId)) return;
        self.heal_in_flight = false;
        self.heal_thread_id = null;
        // openThread ends in joinThread(), which loads the fresh (empty) history normally.
        self.openThread(newThreadId);
      }, () => {
        // A failure that lands after the user has moved on must not lower a loader that another
        // thread's load now owns.
        if(self.abandonStaleHeal(deadThreadId)) return;
        // Re-resolve failed (offline, auth gap). Clear the spinner to an empty pane rather than
        // painting an error, and do NOT mark the id unhealable — the next open, or the extension's
        // next PAGE_HTML cycle, retries.
        self.heal_in_flight = false;
        self.heal_thread_id = null;
        self.messages_processing = false;
      });
      return true;
    },
    /**
     * Whether an in-flight heal has lost the right to act, checked each time one of its steps
     * completes. Returns true when the heal must stop there without touching anything else.
     *
     * A heal re-resolves a dead Rey thread across two awaits (getAiThread, then the object-list
     * refresh), and the user can open another conversation, start a New Topic, or open a SECOND
     * dead thread during either. A late heal used to apply itself anyway — re-point set_thread_id
     * and re-enter the recovered thread — pulling the user out of wherever they had gone.
     *
     *   - Replaced: a heal for another thread has started since. It owns heal_in_flight and the
     *     loader now, so this one touches neither. Nothing is lost by standing down: both resolve
     *     through the same GET threads/system/Fusion/ai.
     *   - Left: the user is no longer on the thread being healed. Release the heal, settle the loader
     *     by dropStaleThreadLoad's rule (another real thread's own load owns it; on a New Topic
     *     nothing else will ever lower it), and apply nothing — no re-point, no host notice, no
     *     unhealable mark. Returning to the dead thread retries the heal, and the host corrects
     *     itself on its own next resolve.
     *
     * Judged when the step COMPLETES, not when the heal started, so someone who wanders off and
     * comes back to the dead thread before it lands is on it again — and is healed.
     */
    abandonStaleHeal(deadThreadId) {
      if(this.heal_thread_id !== deadThreadId) return true;
      if(this.selected_thread === deadThreadId) return false;

      this.heal_in_flight = false;
      this.heal_thread_id = null;
      // Called for its loader rule; its return value is already known to be true here.
      this.dropStaleThreadLoad(deadThreadId);
      return true;
    },
    handleAiThreadReset(data) {
      // Server-pushed notice that this user's system AI thread was reset (admin "Reset AI Thread").
      // User-targeted rather than group-targeted: the deleted thread's WebPubSub group is not a
      // reliable delivery path once the thread is gone, whereas the client's connection carries the
      // userId as its Web PubSub user identity (NegotiateController GetWebSocketURL ->
      // GetClientUrlWithoutGroups), so a per-user send always lands.
      //
      // This is only the fast path. The join-response detection in joinThread is the guarantee: a
      // push that arrives while the socket is down or mid-auth is simply lost, and the next open heals.
      if(this.page_type !== 'Spark') return;
      const resetThreadId = data?.threadId || this.selected_thread;
      if(resetThreadId !== this.selected_thread) return;
      this.healSystemAiThread(resetThreadId);
    },
    getThreadMessages() {
      const self = this;
      // The thread this load is FOR, fixed now. The handlers below used to re-read selected_thread
      // when the response arrived, which by then may be a different thread, a New Topic, or nothing.
      const requestedThreadId = this.selected_thread;
      this.messages_processing = true;
      this.messages_error = '';
      this.$nextTick(() => this.scrollToLastMessageIfClose());
      this.mainStore.GetThreadMessages(requestedThreadId).then(response => {
        // Stale: the user has left the thread these messages belong to. See dropStaleThreadLoad.
        if(self.dropStaleThreadLoad(requestedThreadId)) return;
        if(!self.$FUSarrayHasEntries(response)) {
          self.unreads_by_thread_id[self.selected_thread] = 0;
          self.checkClearMessageUnreads();
        }
        self.messages = self.$FUSarrayHasEntries(response)?response:[];
        self.messages.forEach(message => {
          if(self.$FUSarrayHasEntries(message.toolCitations))
            self.messages_tool_citations[message.id] = message.toolCitations;
        });
        // Preview is now seeded by the API (lastMessagePreview on the thread object).
        // No client-side seeding needed from message history.
        // Hydrate previously-submitted feedback so thumb icons render in their persisted state
        // after a reload. The map is empty by default; without this, every reload drops back to
        // "unrated" even though the rows exist in Cosmos. Best-effort — a failure here just leaves
        // the icons un-rated, same as today.
        self.hydrateFeedbackForSelectedThread();
        self.messages_processing = false;
        self.last_seen_message = self.messages.find(message => message.threadMsgId === self.thread_id_to_last_seen_thread_msg_id[this.selected_thread]) ||
                                              {threadMsgId: self.thread_id_to_last_seen_thread_msg_id[this.selected_thread] ?? -1};
        // WS-5: fire chat_conversation_started once the thread is confirmed as an AI thread
        if(self.active_thread_is_system_ai && !self.mainStore.ai_chat_thread_id && self.rey_session_id) {
          self.mainStore.openAiChat(self.selected_thread);
          window.Analytics?.enqueue('chat_conversation_started', {
            session_id: self.rey_session_id,
            conversation_id: self.selected_thread,
            entry_point: 'thread_list',
          });
        }
        self.$nextTick(() => {
          self.messages_displayed.forEach(message => {
            const el = document.getElementById(`message_tracking_${message.id}`);
            if(el) {
              self.message_intersection_observer.observe(el);
            }
          });
          
          setTimeout(() => {
            self.scrollToLastMessageIfClose(true, false);
          }, 50);
          if(self.set_new_message) {
            if (self.setVoiceInitiated) {
              self.voice_mode_active = true;
              self.preamble_tts_started = false;
            }
            self.sendSystemMessage(self.set_new_message);
            self.set_new_message = null;
          }
          if(self.create_thread_message) {
            self.sendMessage(self.create_thread_message);
            self.create_thread_message = '';
          }
        });
      }, error => {
        console.error('Error:', error);
        // Stale, and it matters most here: the heal below targets selected_thread, so a late 404
        // for a thread the user has left would heal whatever is open NOW — on a New Topic, the
        // all-zeros id, which re-resolves the host's thread and opens it out from under them.
        if(self.dropStaleThreadLoad(requestedThreadId)) return;
        // Backstop for the narrow race where the thread was deleted BETWEEN our join and this
        // history call: the join returned a real roster, so the structural check there didn't fire.
        // A 404 here means the doc is gone — heal instead of showing the error.
        if(error?.code === 'thread_missing' && self.healSystemAiThread(self.selected_thread))
          return;
        self.messages_error = self.$t('messaging.window.errors.loadingMessages');
        self.messages_processing = false;
        self.create_thread_message = '';
      });
    },
    generateDMkey(participantIds) {
      const self = this;
      return this.$FUSarrayHasEntries(participantIds)?participantIds.filter(x => x!==self.current_user_guid).sort().join(','):'none';
    },
    isGroupOriginatedThread(dm) {
      // A DM is treated as a group if the server flagged it (isGroup — set once it is/was a 3+
      // participant group and never cleared, so a since-shrunk group still counts) OR, as a fallback
      // for legacy threads created before that flag, if it currently has 3+ non-system participants.
      if (!dm) return false;
      if (dm.isGroup) return true;
      const humanCount = (dm.participantIds || []).filter(id => id && id.indexOf('system_') < 0).length;
      return humanCount >= 3;
    },
    isCloseToBottom(maxDistanceFromBottom=100) {
      if(this.$refs.messageList && this.$refs.messageList.scrollHeight) {
        return this.$refs.messageList.scrollHeight - this.$refs.messageList.clientHeight <= this.$refs.messageList.scrollTop + maxDistanceFromBottom;
      }
      return false;
    },
    scrollToLastMessageIfClose(forceScroll=false, smoothScroll=true) {
      const self = this;
      if(this.$refs.messageList && this.$refs.messageList.scrollHeight) {
        if(this.isCloseToBottom() || forceScroll) {
          this.$refs.messageList.scrollTo({
            top: self.$refs.messageList.scrollHeight,
            behavior: smoothScroll?'smooth':'instant'
          });
          this.show_scroll_down_btn = false;
        }
      }
    },
    updateLastMessageSeen(message) {
      if(message && this.last_seen_message.threadMsgId < message.threadMsgId) {
        let highestMsg = message;
        if (this.$FUSarrayHasEntries(this.messages_displayed)) 
          highestMsg = this.messages_displayed.reduce((prev, current) => (prev.threadMsgId > current.threadMsgId) ? prev : current);
        this.last_seen_message = highestMsg;
        this.thread_id_to_last_seen_thread_msg_id[highestMsg.threadId] = highestMsg.threadMsgId;
        this.send_last_message_seen_debounced();
      }
    },
    sendLastMessageSeen() {
      const self = this;
      const threadId = this.last_seen_message.threadId;
      const threadsUpdateLastSeen = {
        userId: this.current_user_guid,
        threadId,
        lastSeenMessageId: this.last_seen_message.id,
        lastSeenThreadMsgId: this.last_seen_message.threadMsgId,
      };
      this.mainStore.ThreadsUpdateLastSeen(threadsUpdateLastSeen).then(response => {
        self.unreads_by_thread_id[threadId] = 0;
        self.checkClearMessageUnreads();
      }, error => {
        console.error('Failed to update last seen:', error);
      });
    },
    checkClearMessageUnreads() {
      if(this.internal_messages_unread_count === 0)
        this.$FUSpostFusionMessage('INTERNAL-MESSAGES-UNREADS', false);
      if(this.spark_unread_count === 0)
        this.$FUSpostFusionMessage('SPARK-UNREADS', false);
    },
    selectionWithinInput(selection) {
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const editableDiv = this.$refs.messenger_input;
        return editableDiv && editableDiv.contains(range.commonAncestorContainer);
      }
      
      return false;
    },
    handleTyping() {
      const self = this;
      
      // Reset voice mode when user types manually
      if (this.voice_mode_active) {
        console.log('[Voice] User typed manually, deactivating voice mode');
        this.voice_mode_active = false;
        this.last_voice_message_id = null;
      }
      
      const messageTypingStartDTO = {
        threadId: this.selected_thread,
        userId: this.current_user_guid,
      };
      if(!this.typing_already_sent) {
        this.mainStore.MessageTypingStart(messageTypingStartDTO);
        this.typing_already_sent = true;
      }
      
      if(this.typing_clear_timeout_id)
        clearTimeout(this.typing_clear_timeout_id);
      this.typing_clear_timeout_id = setTimeout(() => {
        self.stopTyping();
      }, this.typing_clear_timeout_timer);
    },
    stopTyping() {
      const messageTypingStartDTO = {
        threadId: this.selected_thread,
        userId: this.current_user_guid,
      };
      this.typing_already_sent = false;
      const stopTypingMessage = JSON.stringify({
        type: 'stopTyping',
        threadId: this.selected_thread,
        userId: this.current_user_guid
      });
      console.log(stopTypingMessage);
      this.mainStore.MessageTypingStop(messageTypingStartDTO);
    },
    hydrateFeedbackForSelectedThread() {
      const self = this;
      const conversationId = this.selected_thread;
      if(!conversationId) return;
      this.mainStore.GetFeedbackForConversation(conversationId).then(response => {
        // Bail if the user has navigated to a different thread while the request was in flight —
        // seeding a stale thread's feedback would flip random icons on the now-active thread.
        if(self.selected_thread !== conversationId) return;
        if(!self.$FUSarrayHasEntries(response)) return;
        response.forEach(row => {
          if(row && row.messageId && row.feedbackType)
            self.message_feedback_status[row.messageId] = row.feedbackType;
          // Seed comment alongside the status — the GET endpoint already returns Comment on each row.
          // Empty/null comments collapse to '' so the map stays string-typed.
          if(row && row.messageId)
            self.message_feedback_comments[row.messageId] = row.comment || '';
        });
      }, () => {
        // Swallow — best-effort hydration. Icons stay un-rated, matching today's behavior.
      });
    },
    /**
     * The person asked to adjust a delivered result. The NEXT message they send is recorded as feedback
     * on that task rather than answered — see the banner above the composer and step 1f server-side.
     */
    adjustScheduledTask(taskId) {
      if (!taskId) return;
      this.pending_feedback_task_id = taskId;
      // focusMessengerInput is MessengerInput's own name for this. Called through optional chaining
      // because the ref is absent while the composer is hidden — but named correctly, so it actually
      // fires when it is there rather than being a no-op nobody would ever notice.
      this.$refs.messengerInput?.focusMessengerInput?.();
    },
    cancelScheduledAdjustment() {
      this.pending_feedback_task_id = null;
    },
    /**
     * The person asked to talk about a morning-brief finding. The starter question goes into the
     * composer — it is NOT sent.
     *
     * Seeding rather than sending is the whole point of the control: the finding's sentence arrives
     * quoted, and what the person actually wants to know about it is theirs to write. It also keeps the
     * path clear of MessengerInput's eager self-clear, which wipes the box the moment a send is
     * dispatched and cannot put text back.
     *
     * Reached through the ref for the same reason adjustScheduledTask is — the composer owns its
     * contenteditable and there is no prop that seeds it. Unlike that one, the ref is reliably there:
     * a brief thread renders the composer unconditionally, so nothing has to re-render first.
     *
     * `scope` is what the finding's brief ran on (MessageItem's brief_chat_scope), and the composer's
     * dealership filter is moved onto it first: the question is about the stores the card shows, so it is
     * asked under the same store or group. See applyFindingScope.
     */
    seedComposerPrompt(text, scope) {
      if (!text) return;
      this.applyFindingScope(scope);
      this.$refs.messengerInput?.seedMessengerInput?.(text);
    },
    /**
     * Puts the composer's dealership filter on a finding's store or group, through the same two handlers
     * the filter's own picks go through.
     *
     * Only to something the filter offers, matched the way the filter matches: a group from
     * usable_dealer_groups (or "All rooftops" for someone with more than one store), named as the filter
     * names it; a store from the access list, spelled as the list spells it (see threadDealerCif for why
     * the spelling matters). Anything else — a deleted group, a lost store, an older card with neither —
     * leaves the filter where it is rather than arming a scope it has no row to show.
     */
    applyFindingScope(scope) {
      if (!scope) return;

      if (scope.groupId) {
        if (scope.groupId === ALL_ROOFTOPS_GROUP_ID) {
          if (this.all_dealers.length > 1)
            this.updateGroupContext({ id: ALL_ROOFTOPS_GROUP_ID, name: this.$t('messaging.input.allRooftops') });
          return;
        }
        const group = this.usable_dealer_groups.find(g => g && g.id === scope.groupId);
        if (group) this.updateGroupContext({ id: group.id, name: group.name });
        return;
      }

      if (scope.dealerId) {
        const dealer = this.all_dealers.find(
          candidate => (candidate.cifNumber || '').toLowerCase() === String(scope.dealerId).toLowerCase());
        if (dealer) this.updateDealerContext(dealer.cifNumber);
      }
    },
    /**
     * The person asked to open a delivered result in its own thread.
     *
     * Everything that makes the fork happen is server-side — create, name, seed — because the seed is a
     * message from Rey and the send endpoint refuses a sender that is not the caller. So this method's
     * whole job is the two things the server cannot do: ask about the Recent cap first, and navigate.
     */
    async forkScheduledResult(messageId) {
      if (!messageId || !this.selected_thread || this.forking_result) return;

      // Asked BEFORE the request for the same reason materializeDraftConversation asks before its
      // create: the cap refusal comes back as a 409, and recovering from it afterwards would mean
      // telling the user their fork failed and then asking whether they would like to make room —
      // two steps where one will do. Declining aborts, exactly as a failed create does.
      if (!(await this.ensureRecentCapRoom())) return;

      this.forking_result = true;
      try {
        const response = await this.forkScheduledResultRequest(this.selected_thread, messageId);
        const newThreadId = response?.value?.id;
        // A 200 does not imply success: the fork endpoint returns a message-only payload on failure
        // and null members are omitted from the JSON. Same guard materializeDraftConversation applies.
        if (!newThreadId) {
          this.messages_error = this.$t('messaging.scheduledFork.failed');
          return;
        }

        // The fork is brand new, so the thread map has never seen it — and active_thread_is_system_ai
        // reads straight out of that map. Without this the rest of the session would treat the new
        // conversation as an ordinary internal-messaging thread. Same helper the draft-create path uses.
        await this.ensureSystemAiThreadInObjectList(newThreadId);
        this.openSparkConversation(newThreadId);
      } catch (error) {
        console.error('ForkScheduledResult failed:', error);
        this.messages_error = this.$t('messaging.scheduledFork.failed');
      } finally {
        this.forking_result = false;
      }
    },
    /**
     * The scheduled-task anchor to stamp on the message being sent, or undefined for an ordinary one.
     *
     * Only ever ONE of the two. They mean different things and the server reads them into different
     * stores, so a message carrying both would be asking it to guess — and the guess it would have to
     * make is the dangerous direction.
     */
    outboundScheduledAnchor() {
      if (this.pending_feedback_task_id) return { feedbackForTaskId: this.pending_feedback_task_id };
      if (this.scheduled_answer_pending) return { answerForTaskId: this.awaiting_answer_task_id };
      return undefined;
    },
    /** Store wrapper for the fork request. */
    forkScheduledResultRequest(threadId, messageId) {
      return this.mainStore.ForkScheduledResult('Fusion', threadId, messageId);
    },
    /**
     * The back link on a forked thread's origin banner. Just navigation — the Scheduled tasks thread is
     * already in the thread map, because a fork can only exist if a result was delivered into it.
     */
    openScheduledThread(threadId) {
      if (!threadId) return;
      this.openSparkConversation(threadId);
    },
    /**
     * A date separator's label: Today, Yesterday, or the date itself.
     *
     * The day key is YYYY-MM-DD in the viewer's own zone, so it is split into local parts rather than
     * passed to Date — a bare date string is read as UTC, which shifts it back a day for anyone west
     * of Greenwich and would label this morning's results "Yesterday".
     */
    scheduledDayLabel(dayKey) {
      if (!dayKey) return '';

      const parts = String(dayKey).split('-').map(Number);
      if (parts.length !== 3 || parts.some(Number.isNaN)) return '';

      const [year, month, day] = parts;
      const date = new Date(year, month - 1, day);
      if (Number.isNaN(date.getTime())) return '';

      const today = new Date();
      const midnight = d => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const daysApart = Math.round((midnight(today) - midnight(date)) / 86400000);

      if (daysApart === 0) return this.$t('messaging.scheduledDay.today');
      if (daysApart === 1) return this.$t('messaging.scheduledDay.yesterday');

      return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    },
    /**
     * The full-results route out of the folded row. The inbox already exists and already spans every
     * store, so this opens it rather than reproducing run history in the thread — the twenty most
     * recent messages are all this thread can ever hold, and the rest live there.
     */
    openScheduledResultsInbox() {
      window.event_bus.$emit('SCHEDULED-OPEN-RESULTS');
    },
    /**
     * The header's task counts. Fetched when the scheduled thread is opened rather than on mount:
     * a user who never opens it should not pay for the request, and the counts are stale the moment
     * a task is paused elsewhere anyway.
     */
    async loadScheduledTaskCounts() {
      const threadId = this.selected_thread;
      try {
        const response = await this.getScheduledTasksForHeader();
        // Written only while the thread it was fetched for is still the open one. The list is shared
        // with the Schedules panel, so a reply that outlived a same-device user switch would otherwise
        // become the incoming user's task list — the switch empties the thread lists, which is what
        // turns active_thread_is_scheduled false here.
        if (this.selected_thread !== threadId || !this.active_thread_is_scheduled) return;

        // Into the store's list rather than into counts of our own, so the header and the panel read
        // one list. Normalised the way the panel's own fetch does it.
        this.scheduled_tasks = Array.isArray(response) ? response : (response && response.tasks) || [];
        this.scheduled_tasks_loaded_for = threadId;
      } catch (error) {
        // Swallowed: the header drops its count line and still names the thread. A failed courtesy
        // request must not stop someone reading their results.
        console.error('GetScheduledTasks failed:', error);
      }
    },
    /** Store wrapper for the task list the header counts. */
    getScheduledTasksForHeader() {
      return this.mainStore.GetScheduledTasks();
    },
    /**
     * The task a message belongs to, if it is a delivered scheduled result. Read from the message rather
     * than tracked separately so it cannot drift out of step with what is on screen.
     */
    scheduledTaskIdForMessage(messageId) {
      const message = this.messages.find(m => m.id === messageId);
      const id = message?.metadata?.scheduledTaskId;
      return (typeof id === 'string' && id.trim()) ? id.trim() : null;
    },
    /**
     * Records feedback against the TASK as well as the message (§5.5). Deliberately not chained: the
     * action resolves to null on failure rather than rejecting, because the message feedback beside it is
     * what the UI reflects and a second store being unreachable must not make a thumbs-up look like it
     * failed.
     */
    submitScheduledTaskFeedback(taskId, kind, note) {
      this.mainStore.SubmitScheduledTaskFeedback(taskId, kind, note);
    },
    submitFeedback(feedbackData) {
      const self = this;

      // A thumb on a delivered scheduled result is BOTH message feedback and task feedback. Written to
      // both stores rather than diverted: the conversation feedback system has its own consumers, and
      // silently taking its rows away would be an invisible regression in somebody else's numbers.
      const scheduledTaskId = this.scheduledTaskIdForMessage(feedbackData.messageId);
      if (scheduledTaskId)
        this.submitScheduledTaskFeedback(
          scheduledTaskId, feedbackData.feedbackType === 0 ? 'Useful' : 'NotUseful', null);

      let submitFeedbackDTO = this.$FUSjsonCopy(feedbackData);
      submitFeedbackDTO.conversationId = this.selected_thread;
      
      const message_id = submitFeedbackDTO.messageId;
      this.message_feedback_status[message_id] = true;
      
      this.mainStore.SubmitFeedback(submitFeedbackDTO).then(response => {
        if(response && response.feedbackType)
          self.message_feedback_status[message_id] = response.feedbackType;
        else
          self.message_feedback_status[message_id] = 'Error processing feedback';
      }, error => {
        self.message_feedback_status[message_id] = 'Error processing feedback';
      });
      
      const threadsDisableDTO = {
        threadId: this.selected_thread,
        messageId: feedbackData.messageId
      };
      this.mainStore.ThreadsDisable(threadsDisableDTO).then(response => {
      }, error => {
      });
    },
    submitComment(payload) {
      // Comment auto-saves are routed through the same SubmitFeedback action — server-side, this is just
      // another submit with feedbackType=ThumbsDown and a populated Comment. The existing broadcast then
      // fans the new comment to every sibling window via WebPubSub, and our case 'feedbackUpdated' handler
      // writes message_feedback_comments[messageId]. We deliberately do NOT optimistically update the local
      // map here — letting the WS round-trip be the source of truth keeps both windows in lockstep without
      // a separate idempotency check.
      const self = this;
      const message_id = payload.messageId;

      // On a delivered scheduled result the comment is the useful half — it is the instruction that will
      // shape tomorrow's wording. Recorded as an Adjustment so it carries no verdict of its own.
      const scheduledTaskId = this.scheduledTaskIdForMessage(message_id);
      if (scheduledTaskId && payload.comment)
        this.submitScheduledTaskFeedback(scheduledTaskId, 'Adjustment', payload.comment);

      const submitFeedbackDTO = {
        conversationId: this.selected_thread,
        messageId: message_id,
        feedbackType: 1, // 1 = ThumbsDown — comment only makes sense in that state per the UI gating
        comment: payload.comment || '',
      };
      this.mainStore.SubmitFeedback(submitFeedbackDTO).then(response => {
        // Server returns the full MessageFeedback row. Use the persisted value directly so client and server
        // can never diverge (e.g. if the server trimmed/sanitized the comment).
        if (response && typeof response.comment === 'string')
          self.message_feedback_comments[message_id] = response.comment;
      }, () => {
        // Submit failed — leave the existing value in place. The user's local draft is still in MessageItem's
        // comment_draft and will retry naturally on the next blur.
      });
    },
    generateDMName(participantIds, shortName=false) {
      const self = this;
      let fieldData = '';
      let currentUser = this.ent_users_by_guid && this.ent_users_by_guid[this.current_user_guid]?this.ent_users_by_guid[this.current_user_guid]:null;
      let currentUserName = currentUser ? this.$FUSuserDisplayName(currentUser) : '';
      if(this.$FUSarrayHasEntries(participantIds)) {
        let dmNamesList = participantIds.filter(participantId => participantId !== self.current_user_guid && participantId.indexOf(this.$FUSsystemUserPrefix) < 0).map(participantId => {
          const user = self.ent_users_by_guid && self.ent_users_by_guid[participantId]?self.ent_users_by_guid[participantId]:null;
          return user ? self.$FUSuserDisplayName(user) : self.$FUSresolveGhostUser(participantId).friendlyName;
        }).sort((a,b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        // shortName -> compact row/header label ("Alice", "Alice and Bob", "Alice and 2 more").
        // Otherwise the full, comma-joined list of every participant, which is what thread filtering
        // searches against so a group DM still matches on any member's name.
        let assembled = shortName ? self.$FUSformatParticipantNames(dmNamesList) : dmNamesList.join(", ");
        fieldData = assembled || (currentUserName?currentUserName+' (You)':currentUserName);
      }

      return fieldData;
    },
    sendSystemMessage(message='', systemId='system_REY') {
      let taggedMessage = message;
      if(!this.active_thread_is_system_ai)
        taggedMessage = `@${systemId} ${taggedMessage}`;
      this.sendMessage(taggedMessage);
    },
    // Rey acknowledges a personality change made on the Preferences screen, in the voice she was
    // just switched to. Purely local: the text is canned per persona and never round-trips, so
    // there is no LLM turn and no latency — but equally it is not persisted, and a reload drops it.
    // (Changing personality by ASKING Rey goes through UpdateUserSettings instead, and she answers
    // for herself there — this path exists only because the dropdown produces no reply of its own.)
    handleReyPersonalityChanged(payload) {
      // Same double-mount hazard as the MESSENGER-* handlers, inverted: this event is only ever
      // meaningful on the Spark/AI instance, so the InternalMessaging mount must ignore it rather
      // than drop a Rey message into a person-to-person thread.
      if(!this.active_thread_is_system_ai || !this.selected_thread)
        return;

      const personality = payload?.personality;
      if(!personality)
        return;

      // The literal prefix below is load-bearing beyond readability: i18n/index.spec.js scans source
      // for $t('...') and treats a captured key ending in '.' as a dynamic prefix, which is the only
      // thing marking these four catalog entries as reachable. Assembling the key in a variable
      // first would make them scan as dead and fail the unused-key test.
      const content = this.$t('settings.rey.personalityChanged.' + personality);

      // vue-i18n echoes the key back when it is missing, so an unrecognized personality would render
      // the raw path as the message body. Better to say nothing — the setting still saved either
      // way, and this message is only the acknowledgement.
      if(!content || content.startsWith('settings.rey.personalityChanged.'))
        return;

      // Mirrors the optimistic-insert shape in sendMessage(). senderId must carry the system prefix
      // for MessageItem to style it as Rey rather than as the current user.
      //
      // threadMsgId deliberately does NOT advance past last_seen_message, unlike the optimistic
      // insert's last+1. That message is about to exist server-side under the same id; this one
      // never will. If it became the highest threadMsgId, scrolling it into view would trip
      // updateLastMessageSeen -> sendLastMessageSeen and POST this purely local id as the thread's
      // read receipt, leaving the server pointing at a message that does not exist. Reusing the
      // current value keeps the strict > comparison there false. Display order is unaffected:
      // messages_displayed only reverses insertion order, it does not sort on threadMsgId.
      this.messages.unshift({
        id: (crypto?.randomUUID?.() || `persona-${Date.now()}-${Math.random()}`),
        threadId: this.selected_thread,
        senderId: 'system_REY',
        content: content,
        threadMsgId: (this.last_seen_message?.threadMsgId ?? 0),
        sentAt: new Date().toISOString(),
        type: 'message',
        role: 'assistant',
      });

      const self = this;
      setTimeout(() => {
        self.scrollToLastMessageIfClose(true);
      }, 50);
    },
    async sendMessage(message='') {
      const self = this;
      if(!this.message_send_ready)
        return;

      if (!message || message === '_br_') return;

      // A draft conversation has to become a real thread before anything below runs: the
      // optimistic insert, the send DTO and the analytics event all stamp `selected_thread`, and
      // the placeholder id a draft carries is not a thread anyone can send to. Awaited rather than
      // fired alongside, because every one of those needs the real id.
      if(this.page_type === 'Spark' && this.spark_draft_active) {
        // Lock the composer for this New Topic before the first await. Every other send takes that
        // lock through message_sending, but a first send only reaches that line once its
        // conversation exists — and until then the box was live, so a second question started a
        // second create and one New Topic became two conversations.
        //
        // Not message_sending set earlier: that flag covers the whole component, and would lock the
        // composer of wherever the user went if they left mid-create. See draft_send_pending.
        const draftGeneration = this.draft_generation;
        this.sending_draft_generation = draftGeneration;
        let continueInView;
        try {
          continueInView = await this.materializeDraftConversation(message);
        } finally {
          // Released however materialize ends — success, decline, failure, or a throw. Only if the
          // lock is still this send's: a fresh New Topic may have started its own send meanwhile, and
          // this one finishing must not unlock that one mid-create. On success there is no gap:
          // message_sending is set a few synchronous lines below, before anything can yield.
          if(this.sending_draft_generation === draftGeneration)
            this.sending_draft_generation = null;
        }
        if(!continueInView)
          return;
      }

      // Everything this send targets, fixed NOW. The page-context request below is an await, and the
      // message it builds used to read selected_thread (and the scope and anchor) after it — so
      // switching conversations while it was pending sent the question to the one switched to.
      // Read here, these are the values the send has always used when nothing moves: materialize
      // makes its view switch the last thing it does, so there is no window before this line.
      const sendThreadId = this.selected_thread;
      const sendContext = this.message_context;
      const sendMetadata = this.outboundScheduledAnchor();
      const sendToReyThread = this.active_thread_is_system_ai;

      this.message_sending = true;

      // Generate a client-side message ID so we can optimistically display the message
      // immediately and deduplicate when the WebPubSub broadcast comes back.
      const clientMessageId = (crypto?.randomUUID?.() || `msg-${Date.now()}-${Math.random()}`);

      const threadMsgId = this.last_seen_message.threadMsgId + 1;

      // Optimistically insert the user's message into the list NOW — don't wait for
      // the HTTP round-trip or WebPubSub broadcast. The user sees their message instantly.
      const optimisticMessage = {
        id: clientMessageId,
        threadId: sendThreadId,
        senderId: this.current_user_guid,
        content: message,
        threadMsgId: threadMsgId,
        sentAt: new Date().toISOString(),
        type: 'message',
        role: 'user',
        context: sendContext,
      };
      this.messages.unshift(optimisticMessage);
      this.sent_last_message_from_this_tab = true;
      this.last_sent_thread_msg_id = threadMsgId;

      setTimeout(() => {
        self.scrollToLastMessageIfClose(true);
      }, 50);

      // Request updated context from parent window (WebView) if configured.
      // This runs AFTER the optimistic insert so the user already sees their message.
      let threadContext = null;
      if(sendToReyThread) {
        this.message_send_ready = false;
        try {
          threadContext = await this.requestUpdatedContext();
        } catch (e) {
          console.warn('[MessengerWindow] Context request failed, using fallback:', e.message);
          threadContext = this.thread_context || null;
        } finally {
          this.message_send_ready = true;
        }
      }

      const messageSendDTO = {
        id: clientMessageId,
        threadId: sendThreadId,
        senderId: this.current_user_guid,
        content: message,
        threadMsgId: threadMsgId,
        context: sendContext,
        threadContext: threadContext,
        // The anchor that makes step 1f possible: with it the turn records feedback, without it the
        // message is an ordinary question. Read once and cleared below.
        //
        // The answer anchor is stamped the same way and is deliberately NOT merged with it: feedback may
        // shape wording, an answer settles what gets checked, and the server routes them to different
        // stores. Feedback wins where somehow both apply — the conservative way round, because filing
        // feedback as an answer would put a claim about WHAT TO CHECK into every future run on the
        // strength of a note about tone.
        metadata: sendMetadata,
      };
      this.pending_feedback_task_id = null;

      // WS-5: fire chat_message_sent for AI threads (no message content in payload)
      if(sendToReyThread && this.rey_session_id) {
        window.Analytics?.enqueue('chat_message_sent', {
          session_id: this.rey_session_id,
          conversation_id: sendThreadId,
          message_id: clientMessageId,
        });
        this.mainStore.incrementAiChatMessageCount();
      }

      this.mainStore.MessageSend(messageSendDTO).then(response => {
        self.message_sending = false;
      }, error => {
        self.message_sending = false;
        // Backend rejected the send — remove the optimistic message
        const idx = self.messages.findIndex(m => m.id === clientMessageId);
        if (idx >= 0) self.messages.splice(idx, 1);
        // Backend rejected the send because the TAC chat is closed. Resync the local thread
        // state so the input locks immediately — also covers the case where the WS close
        // broadcast was missed (e.g., websocket dropped) and this 403 is the first signal
        // the client gets that the chat is no longer accepting messages.
        if (error && typeof error === 'object' && error.code === 'thread_closed') {
          self.markTacThreadClosed(error.threadId || messageSendDTO.threadId);
          console.error('TAC thread closed:', error);
          self.messages_error = self.$t('messaging.window.errors.tacChatClosed');
        }
      });
      this.typing_already_sent = false;
    },
    cancelMessage() {
      if(this.active_thread_is_system_ai && this.ai_response_pending) {
        console.log('Cancelling streamed message...');
        if(this.streaming_message_id)
          this.cancelled_message_ids.push(this.streaming_message_id);
        this.ai_response_pending = false;
        // Stop any in-progress TTS playback (e.g. preamble "Looking.")
        if (this.$refs.messengerInput) {
          this.$refs.messengerInput.stopTts();
        }
        this.preamble_tts_started = false;
        this.mainStore.MessageStop(this.selected_thread).then(response => {
          console.log('Message stop request sent successfully');
        }).catch(error => {
          console.error('Error stopping message:', error);
        });
      }
    },
    requestUpdatedContext() {
      // Top-level (standalone) FusionWeb has no host to ask. FusionMain would post REQUEST-CONTEXT to
      // this same window and then drop its own message, so every Rey send sat out the full timeout
      // before it went — only to use the fallback anyway. Answer with that fallback now.
      // Top-level is not the same as hostless: ERA's webview loads FusionWeb as the top document and
      // still answers REQUEST-CONTEXT. A host announces itself through wingmanConfig's InitSystem (the
      // same signal Navbar's is_host_embedded uses), so only a page with neither skips the ask.
      if (!this.is_embedded && !this.wingman_init?.InitSystem) return Promise.resolve(this.thread_context || null);
      const self = this;
      const timeoutMs = this.thread_context_timeout_ms;
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          self.pending_context_request = null;
          reject(new Error('Context request timed out'));
        }, timeoutMs);
        
        self.pending_context_request = { resolve, reject, timeout };
        window.event_bus.$emit('REQUEST-CONTEXT-FOR-MESSAGE');
      });
    },
    handleContextForMessage(wingmanConfig) {
      if (this.pending_context_request) {
        clearTimeout(this.pending_context_request.timeout);
        this.pending_context_request.resolve(wingmanConfig);
        this.pending_context_request = null;
      }
    },
    publishMessage(threadId, messageId) {
      const self = this;
      const messageSendDTO = {
        id: messageId,
        threadId: threadId
      };

      this.message_sending = true;
      this.mainStore.MessagePublish(messageSendDTO).then(response => {
        self.message_sending = false;
      }, error => {
        self.message_sending = false;
      });
    },
    replyToMessage(senderId) {
      this.reply_to_message = senderId;
    },
    createChannel() {
      const self = this;
      if(this.new_channel_name && this.new_channel_name.trim()) {
        this.channel_threads_processing = true;
        this.selected_thread = null;
        this.active_panel = null;

        // If a channel with the same name already exists, open it instead of creating a new one
        const existingChannel = this.channel_threads.find(ch => ch.name && ch.name.trim().toLowerCase() === this.new_channel_name.trim().toLowerCase());
        if(existingChannel && existingChannel.threadId) {
          this.openThread(existingChannel.threadId);
          this.channel_threads_processing = false;
          this.is_creating_thread = false;
          return;
        }

        this.internal_messaging_state = 'UseChannel';
        const createChannelDTO = {
          name: this.new_channel_name,
          // Hand-created channels are always private; public channels are system-created only.
          isPublic: false,
          dealerId: this.dealer_guid,
          participantIds: this.new_channel_participants,
        };
        this.mainStore.CreateChannel(createChannelDTO).then(async response => {
          self.new_channel_name = "";
          self.new_channel_participants = [];
          if (response.id) {
            await self.getChannels();
            self.openThread(response.id);
          }
          else {
            self.messages_processing = false;
            self.channel_threads_processing = false;
          }
          setTimeout(() => {
            self.is_creating_thread = false;
          }, 500);
        }, error => {
          //Error creating channel
          self.messages_processing = false;
          self.channel_threads_processing = false;
          self.is_creating_thread = false;
        });
      }
    },
    //WebSocket Functions
    ensureSocketConnected() {
      this.mainStore.ConnectSocket();
    },
    async webSocketTryAgain() {
      this.waiting_for_web_socket = true;
      this.$nextTick(() => this.scrollToLastMessageIfClose());
      this.mainStore.TryAgainSocket();
      this.fetchThreadsWhenReady(0, this.getThreads);
    },
    handleConnectionError(message) {
      this.error = message;
    },
    handleSocketReconnected() {
      // A socket RE-connect means we may have missed the live stream (streamStart→streamEnd) of an
      // in-flight or just-finished Rey turn — e.g. a hard navigation reloaded this panel mid-generation.
      // Rey's answer is persisted in Cosmos even when its live delivery was lost, so reconcile by
      // refetching thread history. Guarded: getThreadMessages() replaces this.messages wholesale, so
      // skip when a local stream is mid-flight (it would clobber the streaming buffer — the ongoing
      // stream plus the server's final re-broadcast already cover that case) or when no thread is open.
      if (!this.selected_thread || this.selected_thread === this.placeholder_thread_id) return;
      if (this.ai_response_pending) return;
      this.getThreadMessages();
    },
    handleWebSocketMessage(data) {
      const self = this;
      console.log('Message received through WebSocket:', data);
      let lastReadId = data?(this.thread_id_to_last_seen_thread_msg_id[data.threadId] || null):null;
      if(lastReadId > 0 && data.threadMsgId<0)
        data.threadMsgId = lastReadId+1;
      if(data && data.type) {
        switch (data.type) {
          case 'userJoinedThread':
            if (data.userId !== this.current_user_guid && data.threadId === this.selected_thread) {
              const usersListExists = this.selected_thread_users_list.some(user => user.id === data.userId);
              if (!usersListExists) {
                //TODO: create a 'new user' method
                let newUser = {
                  // avatar: null,
                  // email: null,
                  id: data.userId,
                  name: data.name
                };
                this.selected_thread_users_list.push(newUser);
                // The visible "joined the conversation." line is now a persisted activity message
                // created server-side when a participant is added (see PostThreadActivityMessageAsync)
                // and delivered via the normal 'message' event — no ephemeral row is inserted here.
              }
            }
            break;
          case 'startTyping':
            if(data.threadId===this.selected_thread && this.users_typing.indexOf(data.userId)<0 && data.userId!==this.current_user_guid)
              this.users_typing.push(data.userId);
            break;
          case 'stopTyping':
            if(data.threadId===this.selected_thread && this.users_typing.indexOf(data.userId)>=0 && data.userId!==this.current_user_guid)
              this.users_typing = this.users_typing.filter(x => x !== data.userId);
            break;
          case 'conversationTitle':
            // The titler has named a Rey conversation from its first question (server-side,
            // asynchronously — see FireReyConversationTitling). Patch the cached row so the list
            // renames itself without a refetch; if the list has never been loaded there is nothing
            // to patch and its next mount fetches the name anyway.
            if (data.threadId && data.title) {
              this.spark_conversations = (this.spark_conversations || []).map(conversation =>
                conversation.threadId === data.threadId ? { ...conversation, name: data.title } : conversation);
              // BOTH caches, because two endpoints fill them and the OPEN thread is read from this
              // one: object_threads comes from GetUserObjectThreads, and active_thread — which is what
              // names the topic above the composer — is a lookup into it. Patching only
              // spark_conversations renamed the row in the conversation list while the composer kept
              // the pre-title name for the rest of the session, because nothing refetches
              // object_threads after a send.
              this.object_threads = (this.object_threads || []).map(thread =>
                thread.threadId === data.threadId ? { ...thread, name: data.title } : thread);
            }
            break;
          case 'conversationPinned':
            // The owner pinned or unpinned a Rey conversation somewhere else (another tab, another
            // device — see SetReyConversationPinnedAsync). Patch the cached row so the list moves it
            // between sections without a refetch; if the list has never been loaded there is nothing to
            // patch and its next mount reads the state anyway.
            //
            // No `&& data.pinned` guard, unlike conversationTitle above: UNPINNING sends false, and a
            // truthiness check on the payload would drop exactly half of these frames.
            if (data.threadId) {
              this.spark_conversations = (this.spark_conversations || []).map(conversation =>
                conversation.threadId === data.threadId ? { ...conversation, isPinned: Boolean(data.pinned) } : conversation);
            }
            break;
          case 'feedbackUpdated':
            // Server broadcasts this after a thumb is saved (FeedbackService.BroadcastFeedbackUpdatedAsync),
            // so any other surface this user has open (desktop, mobile browser) flips the icon live.
            // Idempotency check: the originating client already wrote the optimistic value in submitFeedback();
            // skipping when it matches avoids a redundant reactive write and a no-op render.
            if (data.messageId && data.feedbackType && this.message_feedback_status[data.messageId] !== data.feedbackType)
              this.message_feedback_status[data.messageId] = data.feedbackType;
            // Comment write is independent of the vote write — a comment-only re-submit (no vote change)
            // still produces a feedbackUpdated event and must propagate. Coerce undefined/null to '' so the
            // map values stay string-typed; same idempotency guard prevents reactive churn.
            if (data.messageId && this.message_feedback_comments[data.messageId] !== (data.comment || ''))
              this.message_feedback_comments[data.messageId] = data.comment || '';
            break;
          case 'activity':
          case 'message':
            // TAC agent has closed the chat — backend already set IsClosed=true in Cosmos and broadcast
            // the close system message via this same channel. Flip the local thread cache so the
            // active_thread_is_closed_tac_chat computed reacts and the input locks, without waiting
            // for a thread refetch. Runs regardless of whether the thread is currently selected so
            // navigating back to a closed thread shows the locked state immediately.
            if (data?.tacChatData?.isChatCloseMessage === true)
              this.markTacThreadClosed(data.threadId);
            // Activity lines ("X joined/left the conversation.") render inline but are informational —
            // they must not drive the thread-list preview or bump the unread badge.
            const isActivityMessage = data.type === 'activity';
            // Update the DM list preview and timestamp for any incoming human message, active or background
            if (!isActivityMessage && data.content && data.senderId && data.senderId.indexOf(this.$FUSsystemUserPrefix) < 0)
              this.updateThreadPreview(data.threadId, data.content, data.sentAt || new Date().toISOString());
            if (data.threadId === this.selected_thread) {
              let newData = this.$FUSjsonCopy(data);
              this.handleActiveThreadMessage(newData, 'message');
              // Added above so it is there when the thread is reopened, but still unread while the list
              // covers the chat. Same exclusions as the inactive branch below, for the same reasons.
              if (this.chat_covered_by_conversation_list && !isActivityMessage && data.senderId !== this.current_user_guid && !data.isRebroadcast)
                this.incrementUnreadCount(data.threadId);
            }
            // The final Rey answer already drove its unread bump via the streamStart branch, and is now
            // ALSO re-broadcast as a persisted 'message' (BroadcastPersistedAiMessage) so a reconnected
            // client can render it. On a surface where the thread isn't selected that re-broadcast would
            // land here and bump the count a SECOND time, so skip it — but ONLY on the isRebroadcast flag
            // the server stamps on that specific delivery. A sender-name skip (system_*) is wrong here: it
            // also swallows live TAC support-agent replies (senderId 'system_TACCHAT'), which arrive with
            // no streamStart to compensate and must still badge. Human messages increment as before.
            else if (!isActivityMessage && data.senderId !== this.current_user_guid && !data.isRebroadcast)
              this.handleInactiveThreadMessage(data);
            break;
          case 'invite':
            if (!this.is_creating_thread) {
              // Support (TAC) chat created for this user — hand the user off into it on the Internal
              // Messages surface. In blade hosts (WingCommander extension, ERA/FOCUS) Rey and Internal
              // Messages load as separate iframes, so FusionToolbar's in-SPA jump can't reach them;
              // post up to the host so it can open the Internal Messages blade at
              // fusion/messages?thread_id=<id>. Only the Rey/Spark surface fires this so the Internal
              // Messages window doesn't hand off to itself. Done first so the handoff isn't blocked by
              // the thread-map lookup below (all_threads_by_thread_id can be null before lists load).
              if (data.threadType === 'Support' && this.page_type === 'Spark')
                this.notifyHostToOpenSupportChat(data.threadId);
              // Do NOT pre-increment the unread count here. The getObjectThreads/getUserThreads
              // refetch below pulls the authoritative unreadCount from the server for the new
              // thread; a manual +1 on top of that refetch double-counted the invite (the refetch
              // merges with += onto whatever local value already exists).
              const inviteThread = this.all_threads_by_thread_id[data.threadId];
              const isSparkInvite = inviteThread?.objectType?.startsWith('SystemAI_');
              if (isSparkInvite)
                this.$FUSpostFusionMessage('SPARK-UNREADS', true);
              else
                this.$FUSpostFusionMessage('INTERNAL-MESSAGES-UNREADS', true);
              // Refetch the list the invited thread belongs to so it appears without a browser
              // refresh. Channels load from a separate endpoint (getChannels → dealer/{dealerGuid})
              // that the thread refetches never touch, so a channel invite would not surface the new
              // channel until a refresh. The server tags the invite payload with threadType
              // ('channel' | 'group' | 'object'); branch on it.
              if (data.threadType === 'channel') {
                this.getChannels();
              } else {
                this.getObjectThreads(false);
                this.getUserThreads(false);
              }
            }
            break;
          case 'threadDeleted':
            this.handleThreadDeleted(data);
            break;
          case 'aiThreadReset':
            this.handleAiThreadReset(data);
            break;
          case 'userRemovedFromThread':
            this.handleUserRemovedFromThread(data);
            break;
          case 'status_update':
            console.log('[STATUS_UPDATE] Received:', { threadId: data.threadId, selectedThread: this.selected_thread, inReplyTo: data.inReplyTo, content: data.content, match: data.threadId === this.selected_thread });
            // Pulses for a thread that is not on screen are still recorded (keyed by the question's unique
            // message id), so its status row shows the latest pulse when the user switches back to it. Only
            // the composer placeholder, the stop control and the scroll follow the thread on screen.
            if(data.threadId) {
              const statusIsForSelectedThread = data.threadId===this.selected_thread;
              let inReplyToId = data.inReplyTo || null;
              let isFirstStatusMessage = !this.$FUSarrayHasEntries(this.status_updates_for_msg[inReplyToId]);
              console.log('[STATUS_UPDATE] Processing:', { inReplyToId, isFirstStatusMessage, hasPlaceholder: this.input_placeholder !== null });
              if(inReplyToId!==null) {
                if (statusIsForSelectedThread && (this.input_placeholder !== null || isFirstStatusMessage)) {
                  this.input_placeholder = data.content;

                  // Hide the noisy "thinking" iteration status from the placeholder. There is no
                  // machine-readable status type on the wire (StatusUpdatePart carries only text), and the
                  // backend status may be English (current live path) or localized (once status localization
                  // lands), so match the thinking phrase in BOTH the selected UI language AND English.
                  // Matching only the localized phrase silently failed for non-English (backend sends
                  // English) — the same class of bug as the stop button.
                  const contentLower = data.content ? data.content.toLowerCase() : '';
                  const thinkingLocal = this.getStatusPhrase('thinking_iteration').toLowerCase();
                  const thinkingEn = this.getStatusPhrase('thinking_iteration', 'en').toLowerCase();
                  if ((thinkingLocal && contentLower.includes(thinkingLocal)) ||
                      (thinkingEn && contentLower.includes(thinkingEn))) {
                    this.input_placeholder = null;
                  }
                }

                // The FIRST status update of a turn means the AI has started working, so show the stop
                // control. Driven by the status ARRIVING (language-independent) — not by matching its
                // localized TEXT, which silently failed for non-English (the backend sends English status
                // while the UI expected a translated phrase). Gate on the FIRST status only: a later status,
                // or one REPLAYED by a WebPubSub reconnect after the turn ended (see the streamMessage
                // handler), must NOT re-arm the button once it was cleared — otherwise it reappears after the
                // answer/suggestion chips. status_updates_for_msg persists for the turn (cleanupMessageState
                // does not clear it), so isFirstStatusMessage is false for every status after the first.
                // Cleared by the turn-ending signals (streamEnd / cancelMessage).
                if (statusIsForSelectedThread && isFirstStatusMessage) {
                  this.ai_response_pending = true;
                  this.streaming_message_id = inReplyToId;
                }
                
                let statusUpdatesForMsg = [];
                if(!isFirstStatusMessage)
                  statusUpdatesForMsg = [...this.status_updates_for_msg[inReplyToId]];
                statusUpdatesForMsg.push(data);
                this.status_updates_for_msg[inReplyToId] = statusUpdatesForMsg;

                const msgExists = this.messages.find(m => m.id === inReplyToId);
                console.log('[STATUS_UPDATE] Stored:', { inReplyToId, totalUpdates: statusUpdatesForMsg.length, messageExistsInMessages: !!msgExists, messagesCount: this.messages.length, messageIds: this.messages.map(m => m.id).slice(-5) });

                if (statusIsForSelectedThread) {
                  setTimeout(() => {
                    const shouldScroll = self.isCloseToBottom(10);
                    if(isFirstStatusMessage || shouldScroll)
                      self.scrollToLastMessageIfClose(true);
                  }, 150);
                }
              }
            }
            break;
          case 'tool_execution':
            console.log('[TOOL_EXEC] Received:', data);
            if(data.threadId===this.selected_thread) {
              let inReplyToId = data.inReplyTo || null;
              if(inReplyToId!==null) {
                let execs = this.tool_executions_for_msg[inReplyToId] || [];
                execs.push({
                  id: data.id,
                  toolName: (data.metadata && data.metadata.toolName) || data.content || '',
                  friendlyName: (data.metadata && data.metadata.friendlyName) || data.content || '',
                  arguments: (data.metadata && data.metadata.arguments) || '',
                  result: (data.metadata && data.metadata.result) || '',
                  isError: (data.metadata && data.metadata.isError) || false,
                  durationMs: (data.metadata && data.metadata.durationMs) || 0,
                  traceId: (data.metadata && data.metadata.traceId) || '',
                  sentAt: data.sentAt,
                });
                this.tool_executions_for_msg[inReplyToId] = execs;
                console.log('[TOOL_EXEC] Stored:', { inReplyToId, totalExecs: execs.length, toolName: execs[execs.length-1].toolName, keys: Object.keys(this.tool_executions_for_msg) });
              }
            }
            break;
          case 'streamStart':
            if (data.threadId === this.selected_thread) {
              this.handleActiveThreadMessage(data, 'streamStart');
              // A Rey answer that starts while the list covers the chat is counted here, once, as the
              // inactive branch counts it; its final re-broadcast is skipped on isRebroadcast.
              if (this.chat_covered_by_conversation_list && data.senderId !== this.current_user_guid)
                this.incrementUnreadCount(data.threadId);
            }
            else if (data.senderId !== this.current_user_guid) {
              this.handleInactiveThreadMessage(data);
            }
            break;
          case 'streamPlaceholder':
            this.handleStreamContent(data, this.processStreamPlaceholder);
            break;
          case 'streamMessage':
            let newData = this.$FUSjsonCopy(data);
            if(this.streaming_display_buffers.hasOwnProperty(newData.id))
              this.streaming_display_buffers[newData.id]['pauseTyping'] = null;

            // FactualityVerifying: verification started. Show the spinner badge and,
            // when the message carries the answer-only text, swap the bubble to it —
            // the ephemeral preamble drops here, so the cited replacement that follows
            // only adds citation markers to otherwise-identical prose (no visible
            // rewrite). Gated on the active stream — WebPubSub reconnects can replay
            // this message after the turn ended.
            if (newData.formattingStyle === 'FactualityVerifying') {
              if (this.streaming_message_id === newData.id) {
                console.log('[VERIFY_INDICATOR] Showing verifying spinner', { msgId: newData.id, contentLen: newData.content?.length });
                this.verifying_messages[newData.id] = true;
                // Clear the streaming buffer and message content so narration
                // text is replaced by the verified content. Stop the typewriter
                // so it doesn't continue typing narration into the new buffer.
                if (this.streaming_display_buffers[newData.id]) {
                  delete this.streaming_display_buffers[newData.id];
                }
                this.streaming_message_id = null;
                // Clear message content — narration will be replaced by verified text
                this.messages = this.messages.map(msg => {
                  if (msg.id === newData.id) {
                    let updatedMsg = self.$FUSjsonCopy(msg);
                    updatedMsg.content = '';
                    return updatedMsg;
                  }
                  return msg;
                });
              }
              break;
            }

            // Merge directly (bypassing the streaming_message_id gate) when this
            // streamMessage carries artifacts/suggestions, or when it's the cited
            // FactualityVerified replacement — by then the FactualityVerifying swap
            // has already cleared streaming_message_id and the display buffer.
            if(this.$FUSarrayHasEntries(newData.artifacts) || this.$FUSarrayHasEntries(newData.suggestions)
               || newData.formattingStyle === 'FactualityVerified'
               || newData.formattingStyle === 'FactualityStreaming'
               || newData.formattingStyle === 'PreambleStreaming') {
              console.log('[STREAM_DEBUG] Artifacts streamMessage arrived', {
                msgId: newData.id,
                hasArtifacts: this.$FUSarrayHasEntries(newData.artifacts),
                artifactCount: newData.artifacts?.length,
                artifactTypes: newData.artifacts?.map(a => a.type),
                hasSuggestions: this.$FUSarrayHasEntries(newData.suggestions),
                formattingStyle: newData.formattingStyle,
                hasBuffer: !!this.streaming_display_buffers[newData.id],
                streamingMessageId: this.streaming_message_id
              });
              const bufferData = this.streaming_display_buffers[newData.id];
              this.processStreamMessage(bufferData || { buffer: '', isComplete: true, pauseTyping: null }, newData);
            } else {
              this.handleStreamContent(newData, this.processStreamMessage);
            }
            if(this.$FUSarrayHasEntries(newData.toolCitations))
              this.messages_tool_citations[newData.id] = [...(this.messages_tool_citations[newData.id] || []), ...newData.toolCitations];
            // Trigger preamble TTS on first chunk when voice mode is active
            this.triggerPreambleTts(newData);
            break;
          case 'streamEnd':
            console.log('[STREAM_END]', {
              msgId: data.id,
              hasBuffer: !!this.streaming_display_buffers[data.id],
              bufferLen: this.streaming_display_buffers[data.id]?.buffer?.length,
              isComplete: this.streaming_display_buffers[data.id]?.isComplete
            });
            if (this.preamble_spinner_timeout) {
              clearTimeout(this.preamble_spinner_timeout);
              this.preamble_spinner_timeout = null;
            }
            if (this.streaming_display_buffers[data.id]) {
              let streamingDisplayBuffer = this.$FUSjsonCopy(this.streaming_display_buffers[data.id]);
              streamingDisplayBuffer.isComplete = true;
              this.streaming_display_buffers[data.id] = streamingDisplayBuffer;
            }
            this.verifying_messages[data.id] = false;
            this.removeUserFromTyping(data.senderId);
            this.$emit('messageReceived');
            this.cleanupMessageState(data.inReplyTo || null);
            this.ai_response_pending = false;
            break;
          default:
            break;
        }
      }
      
      return false;
    },
    visibilityChanged() {
      if (document.visibilityState === 'hidden' && this.typing_clear_timeout_id) {
        clearTimeout(this.typing_clear_timeout_id);
        this.typing_clear_timeout_id = null;
        this.stopTyping();
      }
    },
    completeStatusLoaders(inReplyToId = null) {
      // Mark finished in reactive state rather than patching the DOM: a row patched in place lost its
      // finished look (spinner and blinking pulse came back) once openThread cleared the list and the
      // row re-rendered, and a turn that ended while its thread was off screen was never marked at all.
      // The turn that just ended is marked even when its thread is not on screen; every row on screen is
      // marked too, as the DOM sweep did, so a row left spinning by a turn that never ended still settles.
      if (inReplyToId)
        this.completed_status_msg_ids[inReplyToId] = true;

      this.messages.forEach(message => {
        if (this.$FUSarrayHasEntries(this.status_updates_for_msg[message.id]))
          this.completed_status_msg_ids[message.id] = true;
      });
    },
    messageListScrollEventHandler(eventData) {
      const isAtBottom = this.isCloseToBottom(50);
      const hasMessages = this.$FUSarrayHasEntries(this.messages_displayed);
      
      // Show button when scrolled away from bottom and there are messages
      // Hide button when at bottom
      this.show_scroll_down_btn = !isAtBottom && hasMessages;
    },
    handleActiveThreadMessage(data, messageType) {
      const shouldScroll = this.isCloseToBottom();
      let messageData = this.$FUSjsonCopy(data);
      
      // Deduplicate: if a message with this id already exists (e.g. optimistic insert),
      // update it in place instead of adding a duplicate.
      const existingIdx = this.messages.findIndex(m => m.id === data.id);
      if (existingIdx >= 0) {
        // Update the existing message with the server's version (may have additional fields)
        this.messages[existingIdx] = Object.assign({}, this.messages[existingIdx], messageData);
        // Re-arm the seen observer before handlePostMessageTasks observes this row again: observe() is a
        // no-op for an element already watched, so a row already on screen (Rey's streamed answer) kept the
        // threadMsgId the stream guessed in its seen receipt instead of the saved one this merge brings in.
        // 'message' frames only: they carry the saved number, while a replayed streamStart carries a new guess.
        if (messageType === 'message') {
          const trackedRow = document.getElementById(`message_tracking_${data.id}`);
          if (trackedRow)
            this.message_intersection_observer?.unobserve(trackedRow);
        }
        return this.handlePostMessageTasks(data, shouldScroll, messageType);
      }
      
      // Handle message preparation based on type
      if(messageType === 'message') {
        if(data.senderId.indexOf(this.$FUSsystemUserPrefix) >= 0)
          messageData = this.initMessageStreamingBuffer(data, true);
        else {
          // Detect if another tab sent this message — suppress TTS in this tab
          if (data.senderId === this.current_user_guid && data.threadMsgId !== this.last_sent_thread_msg_id) {
            this.sent_last_message_from_this_tab = false;
          }
          this.messages.unshift(data);
          return this.handlePostMessageTasks(data, shouldScroll, messageType);
        }
      }
      else if(messageType === 'averyMessage')
        messageData = this.initMessageStreamingBuffer(data, true);
      else if(messageType === 'streamStart' || messageType === 'streamUpdate') {
        messageData = this.initMessageStreamingBuffer(data);
        messageData.type = 'message';
      }
      
      if (messageType === 'streamStart' || messageType === 'streamUpdate') {
        this.streaming_message_id = data.id;
      }
      
      // Add message to list (for Avery and stream messages)
      this.messages.unshift(messageData);
      this.reply_to_message = null;
      this.input_placeholder = null;
      return this.handlePostMessageTasks(data, shouldScroll, messageType);
    },
    initMessageStreamingBuffer(data, isComplete = false) {
      let messageData = this.$FUSjsonCopy(data);
      
      this.streaming_display_buffers[data.id] = {
        buffer: data.content || '',
        isComplete: isComplete,
        pauseTyping: null
      };
      
      messageData.content = '';
      return messageData;
    },
    handlePostMessageTasks(data, shouldScroll, messageType) {
      const self = this;
      // Common cleanup tasks
      if (messageType !== 'streamStart') {
        this.removeUserFromTyping(data.senderId);
        this.$emit('messageReceived');
      }
      
      // Handle UI updates
      this.$nextTick(() => {
        self.handleMessageUIUpdates(data, shouldScroll, messageType);
      });
    },
    updateThreadPreview(threadId, content, sentAt = null) {
      if (!threadId || !content) return;
      // Strip basic markdown and collapse whitespace so preview reads as plain text
      const plain = content
        .replace(/#{1,6}\s*/g, '')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/`(.+?)`/g, '$1')
        .replace(/\n+/g, ' ')
        .trim();
      this.thread_preview_by_thread_id = Object.assign({}, this.thread_preview_by_thread_id, { [threadId]: plain });
      if (sentAt)
        this.thread_last_message_at_by_thread_id = Object.assign({}, this.thread_last_message_at_by_thread_id, { [threadId]: sentAt });
    },
    handleInactiveThreadMessage(data) {
      if (this.all_threads_by_thread_id && this.all_threads_by_thread_id.hasOwnProperty(data.threadId)) {
        this.incrementUnreadCount(data.threadId);
        this.notifyHostOfInternalMessage(data);
      }
      else {
        window.event_bus.$emit('listItemEvent', 'MESSENGER-UPDATE-STATUS');
        // A message for a thread this client has never seen, which is exactly what a morning brief is:
        // the delivery run CREATES the thread and posts into it, so the first the client hears of it is
        // this frame. Until now that landed on the emit above and stopped there — nothing in the app
        // listens to it — so the new thread existed on the server and nowhere else, and the brief was
        // silent until something else refetched. That was the page refresh people were doing.
        //
        // The refetch, not a manual +1: getObjectThreads pulls the AUTHORITATIVE unreadCount for the
        // new thread, which is what the invite branch above relies on for the same reason. Once the
        // thread is in the lists its count reaches spark_unread_count and the Spark tab lights up.
        //
        // processing=false so the thread list does not flash its spinner for a background delivery, and
        // debounced because a run delivers several results at once and each one arrives as its own
        // frame. Note this component is mounted twice in the toolbar layout, so the debounce collapses
        // a burst per instance but not the pair — the same duplication the invite branch already has.
        this.refreshObjectThreadsSoon();
      }
    },
    // Built on FIRST USE rather than in created(), which awaits a fetch before it reaches its own
    // debounce wiring — a frame arriving during startup would find the handle still null and be
    // dropped, and a delivery that lands while the panel is opening is exactly the one this exists to
    // catch. Cached on the instance so the debounce window is shared across frames rather than reset
    // by each one.
    //
    // Longer than the seen-receipt debounce in created() on purpose: this coalesces a whole delivery
    // run's worth of frames into one list refetch, where that one paces a receipt for a single thread
    // the user is looking at.
    refreshObjectThreadsSoon() {
      if (!this.refresh_object_threads_debounced)
        this.refresh_object_threads_debounced = this.$FUSdebounce(() => {
          return Promise.resolve(this.getObjectThreads(false)).then(() => this.notifyHostOfRefreshedUnreads());
        }, 1200);

      this.refresh_object_threads_debounced();
    },
    // The host's unread dots after refreshObjectThreadsSoon pulled in a thread this client had never
    // seen. Every other unread path posts as it counts (incrementUnreadCount, the invite branch), but
    // this one's count arrives only in the refetch — so a brand-new brief lit FusionToolbar's Rey
    // blade, which reads the store, and never the WingCommander extension's, which only hears these
    // posts. Posts only the "there are unreads" side: clearing is checkClearMessageUnreads' job, once
    // a thread is actually read.
    notifyHostOfRefreshedUnreads() {
      if (this.is_unmounted) return;
      if (this.spark_unread_count > 0)
        this.$FUSpostFusionMessage('SPARK-UNREADS', true);
      if (this.internal_messages_unread_count > 0)
        this.$FUSpostFusionMessage('INTERNAL-MESSAGES-UNREADS', true);
    },
    notifyHostOfInternalMessage(data) {
      // Bridge for the embedded (Chrome-extension) host, which renders the incoming-message toast
      // natively — FusionWeb's in-app toast isn't visible when Fusion is embedded as a per-blade
      // iframe. Only emit when actually embedded: standalone (top-level) FusionWeb shows its own
      // in-app toast, and emitting here would trigger a duplicate native toast from the extension's
      // content script (which also injects on the standalone page). Only the InternalMessaging
      // surface emits (not Spark), so it can't post a duplicate. Human (non-system) senders on
      // Support (TACChat) threads only.
      if (!this.is_embedded || this.page_type === 'Spark') return;
      if (!data || !data.threadId) return;
      const senderId = data.senderId || '';
      if (!senderId || senderId === this.current_user_guid) return;
      if (senderId.indexOf(this.$FUSsystemUserPrefix) >= 0) return;
      // Scope the host toast to Support (TACChat) chats only, mirroring the standalone in-app toast
      // in FusionToolbar. DMs, channels, non-Support object threads, and Spark (SystemAI_) threads
      // all classify as non-TACChat and are skipped (a not-yet-loaded thread is skipped too, since
      // it won't be TACChat until the thread list is populated).
      const thread = this.all_threads_by_thread_id[data.threadId];
      if (thread?.objectType !== 'TACChat') return;
      let preview = (data.content || '').toString()
        .replace(/#{1,6}\s*/g, '')
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/`(.+?)`/g, '$1')
        .replace(/\n+/g, ' ')
        .trim();
      if (preview.length > 90) preview = preview.slice(0, 90).trimEnd() + '…';
      this.$FUSpostFusionMessage('INTERNAL-MESSAGE-RECEIVED', {
        title: data.senderName || data.name || this.resolveSenderName(data) || 'New message',
        preview: preview,
        threadId: data.threadId,
        threadMsgId: data.threadMsgId ?? null,
      });
    },
    // Resolve the sender's display name from the message's senderId. Prefer the enterprise user's
    // fullName (first + last), falling back to friendlyName (first name only), then a deterministic
    // ghost name. Used for the host toast title so a DM reads as the sender rather than the thread
    // name ("Unnamed Thread"). Mirrors FusionToolbar.resolveSenderName for the standalone in-app toast.
    resolveSenderName(data) {
      const senderId = data?.senderId || '';
      if (!senderId) return '';
      const entUser = this.ent_users_by_guid[senderId];
      if (entUser) return entUser.fullName || entUser.friendlyName || '';
      return this.$FUSresolveGhostUser(senderId).friendlyName;
    },
    incrementUnreadCount(threadId) {
      let currentUnreadsForThread = this.unreads_by_thread_id[threadId] || 0;
      this.unreads_by_thread_id[threadId] = (currentUnreadsForThread + 1);
      const thread = this.all_threads_by_thread_id[threadId];
      const isSparkThread = thread?.objectType?.startsWith('SystemAI_');
      if (isSparkThread)
        this.$FUSpostFusionMessage('SPARK-UNREADS', true);
      else
        this.$FUSpostFusionMessage('INTERNAL-MESSAGES-UNREADS', true);
    },
    handleThreadDeleted(data) {
      const threadId = data?.threadId;
      if (!threadId) return;

      // A thread/channel was deleted server-side and broadcast to every participant. Drop it from
      // whichever list holds it so it disappears without a browser refresh — thread ids are unique
      // across the three lists, so filtering all three is safe. Assign through the computed setters;
      // never mutate store state directly.
      this.channel_threads = this.channel_threads.filter(thread => thread.threadId !== threadId);
      this.user_threads = this.user_threads.filter(thread => thread.threadId !== threadId);
      this.object_threads = this.object_threads.filter(thread => thread.threadId !== threadId);
      // The Rey conversation list is a fourth cache of the same ids and is NOT rebuilt from the three
      // above, so it needs its own filter or a deleted conversation lingers in the hamburger list
      // until the list is remounted — including when the delete happened in another tab.
      this.spark_conversations = (this.spark_conversations || []).filter(conversation => conversation.threadId !== threadId);

      // Drop its unread bookkeeping so the deleted thread can't keep inflating the tab badge
      // rollups (internal_messages_unread_count / spark_unread_count iterate this map).
      if (this.unreads_by_thread_id[threadId] !== undefined) {
        const remainingUnreads = { ...this.unreads_by_thread_id };
        delete remainingUnreads[threadId];
        this.unreads_by_thread_id = remainingUnreads;
      }
      delete this.thread_id_to_last_seen_thread_msg_id[threadId];

      // If the user is currently viewing the thread that was just deleted, replace the message pane
      // with a notice. Mirrors the join-error pattern: keep selected_thread set and raise
      // messages_error — the message list and input are gated off by `!messages_error`.
      //
      // Spark is the exception. On the Rey surface the usual cause is the user deleting their OWN
      // open conversation from the list, and a dead-end error pane is the wrong answer to an action
      // they just took deliberately — there is nothing to recover and no other thread to fall back
      // to. Drop them into a fresh draft instead, which is also the right landing place when another
      // tab did the deleting. The notice is kept for InternalMessaging, where a thread you are
      // reading can be deleted by someone else and you do need to be told.
      if (this.selected_thread === threadId) {
        if (this.page_type === 'Spark')
          this.startDraftConversation();
        else
          this.messages_error = this.$t('messaging.window.errors.conversationDeleted');
      }

      // Recompute the Spark / internal-messages unread indicators now that the thread is gone.
      this.checkClearMessageUnreads();
    },
    handleUserRemovedFromThread(data) {
      const threadId = data?.threadId;
      const removedUserId = data?.userId;
      if (!threadId || !removedUserId) return;

      if (removedUserId === this.current_user_guid) {
        // I was removed. Whether I'm inside the thread or not, drop it from whichever list holds it so
        // it disappears without a browser refresh — thread ids are unique across the three lists, so
        // filtering all three is safe. Assign through the computed setters; never mutate store state.
        this.channel_threads = this.channel_threads.filter(thread => thread.threadId !== threadId);
        this.user_threads = this.user_threads.filter(thread => thread.threadId !== threadId);
        this.object_threads = this.object_threads.filter(thread => thread.threadId !== threadId);

        // Drop its unread bookkeeping so the removed thread can't keep inflating the tab badge
        // rollups (internal_messages_unread_count / spark_unread_count iterate this map).
        if (this.unreads_by_thread_id[threadId] !== undefined) {
          const remainingUnreads = { ...this.unreads_by_thread_id };
          delete remainingUnreads[threadId];
          this.unreads_by_thread_id = remainingUnreads;
        }
        delete this.thread_id_to_last_seen_thread_msg_id[threadId];

        // If I'm currently inside the thread, replace the message pane with a notice and gate the input
        // off (the message list + input are gated by `!messages_error`) — the "kicked out" case.
        // Mirrors handleThreadDeleted.
        if (this.selected_thread === threadId)
          this.messages_error = this.$t('messaging.window.errors.removedFromConversation');

        // Recompute the Spark / internal-messages unread indicators now that the thread is gone.
        this.checkClearMessageUnreads();
        return;
      }

      // Someone else left / was removed. Drop them from the thread's participant list in whichever
      // list holds it so the derived name + avatar bundle recompute live for every remaining viewer
      // without a reopen/reload: the DM row name (generateDMName) and stacked avatars read this, and
      // active_thread (→ header name fallback) is derived from these lists too. Assign through the
      // computed setters with fresh arrays; never mutate store state in place.
      const dropParticipant = threads => threads.map(t =>
        t.threadId === threadId && Array.isArray(t.participantIds)
          ? { ...t, participantIds: t.participantIds.filter(id => id !== removedUserId) }
          : t
      );
      this.user_threads = dropParticipant(this.user_threads);
      this.channel_threads = dropParticipant(this.channel_threads);
      this.object_threads = dropParticipant(this.object_threads);

      // If I'm viewing that thread, also shrink the open-thread state: the navbar header name +
      // avatar bundle read active_thread_participant_guids, and Chat Details / the participant panel
      // read selected_thread_users_list + active_thread_participants.
      if (threadId === this.selected_thread) {
        this.active_thread_participant_guids = (this.active_thread_participant_guids || []).filter(id => id !== removedUserId);
        this.selected_thread_users_list = this.selected_thread_users_list.filter(user => user.id !== removedUserId);
        this.active_thread_participants = this.active_thread_participants.filter(user => user.id !== removedUserId);
        // Roster shrank live — refresh the DM participantCount so is_group_dm re-evaluates.
        this.syncDmParticipantCount();
      }
    },
    handleStreamContent(data, contentProcessor) {
      if (data.threadId === this.selected_thread && this.streaming_message_id === data.id) {
        const bufferData = this.streaming_display_buffers[data.id];
        if (bufferData)
          return contentProcessor(bufferData, data);
      }
      
      return false;
    },
    processStreamMessage(bufferData, data) {
      const self = this;

      // PreambleStreaming: filler content streamed during verify dead air.
      // If there's already a buffer (Conversational narration), APPEND the
      // preamble to it so narration → preamble types seamlessly. If no buffer,
      // initialize a new one. When FactualityVerified arrives, the buffer
      // replacement watcher in MessageItem.vue resets the typewriter for the
      // verified answer.
      if (data.formattingStyle === 'PreambleStreaming') {
        let preambleContent = data.content || '';
        const existingBuffer = this.streaming_display_buffers[data.id];
        if (existingBuffer) {
          // Append to existing buffer — narration → preamble, seamless typing
          existingBuffer.buffer += preambleContent;
          existingBuffer.pauseTyping = null;
          console.log('[PREAMBLE_APPEND]', {
            msgId: data.id,
            preambleLen: preambleContent.length,
            bufferLenAfter: existingBuffer.buffer.length
          });
        } else {
          // No existing buffer — initialize new one
          this.messages = this.messages.map(msg => {
            if (msg.id === data.id) {
              let updatedMsg = self.$FUSjsonCopy(msg);
              updatedMsg.content = '';
              return updatedMsg;
            }
            return msg;
          });
          this.streaming_display_buffers[data.id] = {
            buffer: preambleContent,
            isComplete: false,
            pauseTyping: null
          };
          this.streaming_message_id = data.id;
          console.log('[PREAMBLE_NEW]', {
            msgId: data.id,
            preambleLen: preambleContent.length
          });
        }
        // Show verifying spinner after the preamble typewriter finishes.
        // Estimate: ~100 chars/sec typing speed + 500ms buffer.
        const estimatedTypeMs = (preambleContent.length / 100 * 1000) + 500;
        if (this.preamble_spinner_timeout) clearTimeout(this.preamble_spinner_timeout);
        this.preamble_spinner_timeout = setTimeout(() => {
          this.verifying_messages[data.id] = true;
        }, estimatedTypeMs);
        return false;
      }

      // FactualityStreaming: subsequent verified content after the first FactualityVerified.
      // Append directly to the buffer without separator — the caller includes any
      // needed newlines in the content. Works for both token streaming (non-section
      // regen, each message is a token) and block streaming (each message is a
      // complete block that includes its own \n\n prefix when needed).
      if (data.formattingStyle === 'FactualityStreaming') {
        let newContent = data.content || '';
        const existingBuffer = this.streaming_display_buffers[data.id];
        if (existingBuffer) {
          existingBuffer.buffer += newContent;
          existingBuffer.pauseTyping = null;
        } else {
          this.messages = this.messages.map(msg => {
            if (msg.id === data.id) {
              let updatedMsg = self.$FUSjsonCopy(msg);
              updatedMsg.content = (updatedMsg.content || '') + newContent;
              return updatedMsg;
            }
            return msg;
          });
        }
        return false;
      }

      // FactualityVerified: first verified block. Initialize the streaming buffer
      // so the typewriter effect types it out. Subsequent FactualityStreaming blocks
      // will append to this same buffer for seamless typing.
      if (data.formattingStyle === 'FactualityVerified') {
        console.log('[FACTUALITY_VERIFIED]', {
          msgId: data.id,
          contentLen: data.content?.length,
          contentPreview: data.content?.substring(0, 40),
          hasBuffer: !!this.streaming_display_buffers[data.id],
          existingBufferLen: this.streaming_display_buffers[data.id]?.buffer?.length,
          existingBufferPreview: this.streaming_display_buffers[data.id]?.buffer?.substring(0, 40)
        });
        // Clear the preamble spinner timeout — verified answer is arriving
        if (this.preamble_spinner_timeout) {
          clearTimeout(this.preamble_spinner_timeout);
          this.preamble_spinner_timeout = null;
        }
        // Clear verifying spinner — the verified answer will type out now
        this.verifying_messages[data.id] = false;
        console.log('[STREAM_DEBUG] FactualityVerified arrived', {
          msgId: data.id,
          contentLen: data.content?.length,
          hasArtifacts: this.$FUSarrayHasEntries(data.artifacts),
          hasSuggestions: this.$FUSarrayHasEntries(data.suggestions),
          hasCitations: this.$FUSarrayHasEntries(data.toolCitations),
          hasBuffer: !!this.streaming_display_buffers[data.id],
          streamingMessageId: this.streaming_message_id
        });

        // Keep the verifying spinner visible — it will be hidden by streamEnd
        // when all blocks (including FactualityStreaming) have finished.
        this.messages = this.messages.map(msg => {
          if (msg.id === data.id) {
            let updatedMsg = self.$FUSjsonCopy(msg);
            updatedMsg.content = '';
            if (self.$FUSarrayHasEntries(data.artifacts)) {
              if (!Array.isArray(updatedMsg.artifacts))
                updatedMsg.artifacts = [];
              for (let artifact of data.artifacts)
                updatedMsg.artifacts.push({ ...artifact });
            }
            if (self.$FUSarrayHasEntries(data.suggestions))
              updatedMsg.suggestions = data.suggestions;
            return updatedMsg;
          }
          return msg;
        });

        // Merge tool citations so mouseover hover icons show on the replaced message
        if (this.$FUSarrayHasEntries(data.toolCitations))
          this.messages_tool_citations[data.id] = [...(this.messages_tool_citations[data.id] || []), ...data.toolCitations];

        // Initialize streaming buffer with the first block's content so the
        // typewriter picks it up. FactualityStreaming blocks will append to this.
        this.streaming_display_buffers[data.id] = {
          buffer: data.content || '',
          isComplete: false,
          pauseTyping: null
        };
        this.streaming_message_id = data.id;
        return false;
      }

      let isChart = false;

      // Handle artifacts
      this.messages = this.messages.map(msg => {
        if (msg.id === data.id) {
          let updatedMsg = self.$FUSjsonCopy(msg);
          if (self.$FUSarrayHasEntries(data.artifacts)) {
            console.log('[STREAM_DEBUG] Merging artifacts onto message', {
              msgId: data.id,
              artifactCount: data.artifacts.length,
              artifactTypes: data.artifacts.map(a => a.type),
              currentArtifacts: updatedMsg.artifacts?.length || 0
            });
            if (!Array.isArray(updatedMsg.artifacts))
              updatedMsg.artifacts = [];
            
            for (let artifact of data.artifacts) {
              let newArtifact = { ...artifact };
              if (newArtifact.type === 'Chart' || newArtifact.type === 0) {
                isChart = true;
                newArtifact.chartContent = newArtifact.content;
              }
              updatedMsg.artifacts.push(newArtifact);
            }
          }
          
          if (self.$FUSarrayHasEntries(data.suggestions)) {
            updatedMsg.suggestions = self.$FUSarrayHasEntries(updatedMsg.suggestions)
              ? updatedMsg.suggestions.concat(data.suggestions)
              : data.suggestions;
          }
          
          return updatedMsg;
        }
        
        return msg;
      });
      
      // Add content to buffer if not a chart
      if (!isChart)
        bufferData.buffer += data.content || '';
      
      return isChart;
    },
    processStreamPlaceholder(bufferData, data) {
      // No-op: charts are rendered from message.artifacts array in the template,
      // not from <viz-N> text placeholders. The streamPlaceholder event is still
      // sent by the orchestrator but we don't need to inject placeholder divs
      // into the streaming buffer.
      return false;
    },
    handleCopyCodeClick(event) {
      const copyButton = event.target.closest('.copy-code-btn');
      if (!copyButton) return;
      
      event.preventDefault();
      event.stopPropagation();
      
      const codeBlockId = copyButton.getAttribute('data-code-block-id');
      if (!codeBlockId) return;
      
      const codeElement = document.getElementById(codeBlockId);
      if (!codeElement) return;
      
      const codeText = codeElement.textContent || codeElement.innerText || '';
      
      copyButton.parentElement.classList.add('copy-success-pulse');
      const textarea = document.createElement('textarea');
      textarea.value = codeText;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setTimeout(() => {
        copyButton.parentElement.classList.remove('copy-success-pulse');
      }, 800);
    },
    handleTableDownloadClick(event) {
      const downloadButton = event.target.closest('.download-table-btn');
      if (!downloadButton) return;

      event.preventDefault();
      event.stopPropagation();

      const tableId = downloadButton.getAttribute('data-table-id');
      if (!tableId) return;

      const tableElement = document.getElementById(tableId);
      if (!tableElement) return;

      const table = readTable(tableElement);
      if (table.rows.length === 0) return;

      // A one-sheet workbook built exactly as the message-level "download all tables" button builds
      // each of its sheets (MessageItem.downloadAllTables), so a table downloads the same either way.
      // Building is async (ExcelJS loads on first use); the flag stops a double-click saving it twice.
      if (downloadButton.dataset.downloading) return;
      downloadButton.dataset.downloading = 'true';
      return buildTablesWorkbook([table]).then(blob => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `rey-table-${this.$FUSdateFormat('YYYY-MM-DD', new Date())}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Pulse the button itself, not its parent as the copy-code button does: the download glyph sets
        // its own colour, so an animated colour on the parent never reaches it.
        downloadButton.classList.add('copy-success-pulse');
        setTimeout(() => {
          downloadButton.classList.remove('copy-success-pulse');
        }, 800);
      }).catch(error => {
        console.error('[MessengerWindow] Unable to build the table workbook', error);
      }).finally(() => {
        delete downloadButton.dataset.downloading;
      });
    },
    // The button is a role="button" span, so Enter/Space do not fire a click natively — route them to
    // the click handler so keyboard users can download too. Matches a native <button>: Enter activates
    // on key-down, Space on key-up, and a held key's auto-repeat never re-fires (it would otherwise
    // save a burst of files or trip the browser's "multiple downloads" prompt).
    handleTableDownloadKeydown(event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (!event.target.closest('.download-table-btn')) return;
      if (event.key === ' ' || event.repeat) {
        // Space activates on key-up; swallow the key-down (and any repeats) so the list does not scroll.
        event.preventDefault();
        return;
      }
      this.handleTableDownloadClick(event);
    },
    handleTableDownloadKeyup(event) {
      if (event.key !== ' ') return;
      if (!event.target.closest('.download-table-btn')) return;
      this.handleTableDownloadClick(event);
    },
    cleanupMessageState(inReplyToId = null) {
      this.completeStatusLoaders(inReplyToId);
      this.streaming_message_id = null;
    },
    removeUserFromTyping(senderId) {
      if (this.users_typing.includes(senderId)) {
        this.users_typing = this.users_typing.filter(x => x !== senderId);
      }
    },
    handleMessageUIUpdates(data, shouldScroll, messageType) {
      // Setup message observer
      const el = document.getElementById(`message_tracking_${data.id}`);
      if(el)
        this.message_intersection_observer.observe(el);
      
      // Handle scrolling
      if (shouldScroll)
        this.scrollToLastMessageIfClose(shouldScroll);
    },
    backToMessages() {
      if(this.typing_clear_timeout_id) {
        clearTimeout(this.typing_clear_timeout_id);
        this.typing_clear_timeout_id = null;
        this.stopTyping();
      }

      this.clearErrors();
      this.clear_messenger_input = Date.now();

      this.active_panel = null;
      this.selected_thread = null;
      this.internal_messaging_state = null;
      this.header_override = null;
    },
    clearErrors() {
      this.error = '';
      this.channel_threads_error = '';
      this.user_threads_error = '';
      this.object_threads_error = '';
      this.messages_error = '';
    },
    updateMessagesColumnHeight() {
      if(this.$el) {
        this.messenger_container_height = this.$el.offsetHeight;
      }
    },
    handleInputFocus() {
      this.scrollToLastMessageIfClose(true);
    },
    updateDealerContext(context) {
      this.context_cif = context;
      // A single-dealer pick ends any group scope.
      this.context_group = null;
    },
    updateGroupContext(group) {
      this.context_group = group || null;
    },
    /**
     * The dealership one of the caller's Rey conversations is operating under, or null when it cannot
     * be established — read by the selected_thread watcher to restore the picker per conversation.
     *
     * Taken from the conversation row's `contextDealerId`, which the SERVER resolved (see
     * MessengerApiFunctions.ResolveConversationDealerContext) rather than being worked out again
     * here. That keeps one rule in one place: the picker and the dealership pill on the conversation
     * list are then reading the same answer and cannot disagree about which store a conversation is
     * using.
     *
     * Returns the CIF AS SPELLED IN THE CALLER'S ACCESS LIST, and only if it appears there. Both
     * halves are load-bearing:
     *  - MessengerInput's active_dealer_label matches `cifNumber === selectedDealerCif` exactly and
     *    falls back to an empty string, so a casing difference — which hosts do produce — would
     *    restore a working scope under a BLANK picker label.
     *  - A CIF the caller has no access to is not a scope worth restoring; the server would refuse it
     *    anyway, and the picker has no row to name it with.
     * Either way the watcher falls back to the host page's CIF, which is what it used before.
     *
     * Group-scoped conversations are deliberately NOT restored (contextDealerType 'Group'): a group
     * turn scopes its history to the PAGE rooftop, so there is no per-conversation history to keep in
     * agreement, and re-arming a group would fan the next message across stores the user did not pick
     * here — the case the context_group reset exists to prevent.
     */
    threadDealerCif(threadId) {
      if (!threadId) return null;

      // Only populated once ReyConversationList has mounted (see ensureRecentCapRoom's note). A
      // conversation opened without ever visiting the list therefore has no row, and the watcher
      // falls back to the host page's dealer exactly as it always did.
      const conversation = (this.spark_conversations || []).find(row => row.threadId === threadId);
      const dealerId = conversation?.contextDealerId;
      if (!dealerId) return null;
      if (String(conversation.contextDealerType || '').toLowerCase() === 'group') return null;

      const dealer = this.all_dealers.find(
        candidate => (candidate.cifNumber || '').toLowerCase() === String(dealerId).toLowerCase());

      return dealer?.cifNumber || null;
    },
    fetchUsersForPopup() {
      if (this.enterprise_users_processing || this.users_info) return;
      const self = this;
      this.enterprise_users_processing = true;
      this.mainStore.fetchEnterpriseUsers(this.dealer_guid).then(response => {
        self.users_info = response;
        self.enterprise_users_processing = false;
      }, error => {
        //Error fetching enterprise users
        self.enterprise_users_processing = false;
      });
    },
    normalizeQuery(query) {
      // Remove leading '#' if present
      if (query.startsWith('#')) {
        return query.substring(1);
      }
      return query;
    },
    createThread(createThread=null) {
      if(createThread) {
        // Committing to create a thread ends any contact-card detour: the New Message screen may
        // have been reached from a contact profile (which itself may have been opened from a
        // Chat/Thread Details participant list). Clear those breadcrumbs so the freshly created
        // conversation flows normally — Back from it lands on the messenger list, not the contact
        // card or details page — and no stale shared-chat snapshot lingers. The header re-syncs on
        // its own when state flips to a Use* view (see the internal_messaging_state watcher).
        this.contact_return_guid = null;
        this.contact_origin_view = null;
        this.chat_details_thread_id = null;
        this.chat_details_nav_snapshot = null;
        let usersSelected = createThread?.users;
        let message = createThread?.message;
        let channelName = createThread?.channelName;
        // Vehicle thread: the picker collected the invitees. Hand them to the private, invite-only
        // vehicle-create flow (which manages is_creating_thread itself) instead of the
        // DM/channel paths below.
        if (this.pending_vehicle_thread) {
          this.createVehicleThread(usersSelected || []);
          return;
        }
        this.is_creating_thread = true;
        // Dispatch on create_thread_mode — the same signal that decided which composer UI was
        // rendered and what data the picker collected — NOT the raw messageType tab. They diverge
        // when a DM compose is launched from a non-Messages context (e.g. the contact card's
        // Message button while viewing a channel): the composer is in Messages mode, but
        // messageType is still 'Channels', which would otherwise route Create to channel creation.
        switch (this.create_thread_mode) {
          case 'Messages':
            this.dm_users_selected = usersSelected;
            this.create_thread_message = message;
            this.createGroupThread();
            break;
          case 'Channels':
            this.new_channel_name = channelName;
            this.new_channel_participants = usersSelected || [];
            this.createChannel();
            break;
        }
      }
      else {
        this.pending_vehicle_thread = false;
        this.pending_vehicle_thread_context = null;
        this.compose_new = false;
        this.internal_messaging_state = null;
      }
    },
    createGroupThread() {
      const self = this;
      if(this.$FUSarrayHasEntries(this.dm_users_selected)) {
        this.user_threads_processing = true;
        this.selected_thread = null;
        this.active_panel = null;

        //if a DM to selected users already exists, open that thread and do not create a new one.
        // Match within the same bucket (group vs 1-on-1): a 2-person compose reuses only a native
        // 1-on-1, and a 3+ compose reuses only a group whose CURRENT set is identical — so a group
        // that has shrunk to two people is never reused here, and recreating a former group (e.g.
        // {A,B,C} after C left) correctly makes a fresh thread.
        const uniqueDMKey = this.generateDMkey(this.dm_users_selected);
        const composeIsGroup = uniqueDMKey !== 'none' && uniqueDMKey.split(',').length >= 2;
        const composeLookupKey = (composeIsGroup ? 'G:' : 'D:') + uniqueDMKey;
        if(uniqueDMKey && uniqueDMKey !== 'none' && this.user_threads_by_unique_key[composeLookupKey]) {
          let threadId = this.user_threads_by_unique_key[composeLookupKey].threadId;
          this.openThread(threadId);
          this.user_threads_processing = false;
          // Mirror the reset the network paths below run — this early return also opens a thread,
          // so is_creating_thread must be cleared or the compose overlay/Create button stays stuck
          // (and createChannel's is_creating_thread guard blocks) on the next compose.
          setTimeout(() => {
            self.is_creating_thread = false;
          }, 500);
          return;
        }

        this.internal_messaging_state = 'UseMessage';
        const createGroupThreadDTO = {
          participantIds: this.dm_users_selected,
        };
        this.mainStore.CreateGroupThread(createGroupThreadDTO).then(async response => {
          self.new_group_participants = [];
          if(response.id) {
            self.selected_thread = response.id;
            await self.getUserThreads();
            self.openThread(response.id);
          }
          else {
            self.messages_processing = false;
            self.user_threads_processing = false;
          }
          setTimeout(() => {
            self.is_creating_thread = false;
          }, 500);
        }, error => {
          console.log(error);
          self.new_group_participants = [];
          self.messages_processing = false;
          self.user_threads_processing = false;
          self.is_creating_thread = false;
          if(error && error.indexOf('401')>=0)
            self.messages_error = self.$t('messaging.window.errors.dmNotAllowed');
        });
      }
    },
    messagesIntersected(entries) {
      const self = this;
      const mostRecentMessage = entries.filter(entry => entry.isIntersecting).map(entry => self.messages_by_id[entry.target.id.replace('message_tracking_','')])
                                       .reduce((mostRecentMessage, message) => mostRecentMessage.threadMsgId > message.threadMsgId ? mostRecentMessage : message, {threadMsgId: -1});
      if(mostRecentMessage.threadMsgId > -1)
        this.updateLastMessageSeen(mostRecentMessage);
    },
    updateStreamingMessageDisplay(messageId, displayContent) {
      // Capture whether the user is following along BEFORE the new chunk grows the DOM.
      const wasCloseToBottom = this.isCloseToBottom();
      if (wasCloseToBottom) {
        // Keep the view pinned to the bottom so the response scrolls into view as it types.
        // Instant (non-smooth) because this fires repeatedly on the typing interval — a smooth
        // animation per tick would fight itself. Force-scroll since the DOM has already grown by nextTick.
        this.$nextTick(() => this.scrollToLastMessageIfClose(true, false));
      } else {
        this.show_scroll_down_btn = true;
      }
    },
    onStreamingComplete(messageId) {
      // Copy the fully-typed buffer to message.content so it persists after the buffer is cleaned up
      const buffer = this.streaming_display_buffers[messageId];
      if (buffer && buffer.buffer) {
        this.messages = this.messages.map(msg => {
          if (msg.id === messageId) {
            let updatedMsg = this.$FUSjsonCopy(msg);
            updatedMsg.content = buffer.buffer;
            return updatedMsg;
          }
          return msg;
        });
      }
      // Clear streaming state
      this.streaming_message_id = null;
      // Handle TTS playback for voice conversations
      this.handleStreamingComplete(messageId);
    },
    handleStreamingComplete(messageId) {
      const message = this.messages_by_id[messageId];
      
      // Check if this is a voice-initiated response that needs TTS.
      // playTts() calls stopTts() first, so any in-flight preamble audio is cleanly
      // replaced by the full message synthesis.
      if (this.voice_mode_active && this.sent_last_message_from_this_tab && message && this.active_thread_is_system_ai) {
        const isSameThread = message.threadId === this.selected_thread;
        const isFromAI = message.senderId?.startsWith('system_');
        
        const isCancelled = this.cancelled_message_ids.includes(messageId);

        if (isSameThread && isFromAI && !isCancelled) {
          // Prefer the streaming buffer (accumulated text) over message.content, which is empty for streaming messages
          const buffer = this.streaming_display_buffers[messageId];
          const rawContent = buffer?.buffer || message.content;
          const textContent = this.extractTextForTts(rawContent);
          if (textContent && this.$refs.messengerInput) {
            console.log('[Voice] Triggering TTS for AI response, messageId:', messageId);
            this.$refs.messengerInput.playTts(textContent, messageId);
          }
        }
      }
      
      // Reset preamble flag after stream completes
      this.preamble_tts_started = false;
      // Note: Do NOT delete streaming_display_buffers here - that clears the displayed text!
      // The buffer is cleaned up by MessageItem when streaming is marked complete via isComplete flag
    },
    handleSpeakMessage(messageId) {
      const message = this.messages_by_id[messageId];
      if (!message || !this.$refs.messengerInput) return;
      const buffer = this.streaming_display_buffers[messageId];
      const rawContent = buffer?.buffer || message.content;
      const textContent = this.extractTextForTts(rawContent);
      if (textContent) {
        console.log('[TTS] Manual speak triggered, messageId:', messageId);
        this.$refs.messengerInput.playTts(textContent, messageId);
      }
    },
    extractTextForTts(content) {
      if (!content) return '';
      
      // Remove HTML tags, markdown formatting, and viz markers
      let text = content
        .replace(/<viz-[^>]+>[\s\S]*?<\/viz-[^>]+>/gi, '') // Remove viz blocks
        .replace(/<[^>]+>/g, ' ')  // Remove HTML tags
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')  // Markdown links -> just the text
        .replace(/[*_~`#]+/g, '')  // Remove markdown formatting chars
        .replace(/\s+/g, ' ')  // Normalize whitespace
        .trim();
      
      // Truncate very long responses for TTS (avoid reading data tables)
      const maxLength = 1000;
      if (text.length > maxLength) {
        text = text.substring(0, maxLength) + '...';
      }
      
      return text;
    },
    handleVoiceBargeIn() {
      console.log('[Voice] Barge-in received, stopping any TTS playback');
      if (this.$refs.messengerInput) {
        this.$refs.messengerInput.stopTts();
      }
      // Reset preamble flag since we're interrupting
      this.preamble_tts_started = false;
    },
    handleVoiceTranscribed(text) {
      console.log('[Voice] Voice message transcribed:', text?.substring(0, 50));
      // Activate voice mode - TTS will be triggered for AI responses in this thread
      this.voice_mode_active = true;
      this.preamble_tts_started = false;  // Reset for new response
      console.log('[Voice] Voice mode activated');
    },
    triggerPreambleTts(data) {
      // Only trigger if voice mode is active, we haven't started, and this is from AI
      if (!this.voice_mode_active || !this.sent_last_message_from_this_tab || this.preamble_tts_started) return;
      if (!this.active_thread_is_system_ai) return;
      // Don't start TTS for a message the user already cancelled
      if (this.cancelled_message_ids.includes(data.id)) return;
      
      const isFromAI = data.senderId?.startsWith('system_');
      if (!isFromAI) return;
      
      // Get current accumulated content
      const buffer = this.streaming_display_buffers[data.id];
      const content = buffer?.buffer || data.content || '';
      
      // Only start TTS if we have non-trivial content. Low threshold because the preamble
      // arrives as a single complete chunk (not word-by-word), so even "Looking." (8 chars) must pass.
      if (content.length < 3) return;
      
      // Mark that we've started preamble TTS
      this.preamble_tts_started = true;
      
      // Extract clean text for TTS
      const textContent = this.extractTextForTts(content);
      if (textContent && this.$refs.messengerInput) {
        console.log('[Voice] Starting preamble TTS:', textContent.substring(0, 50) + '...');
        this.$refs.messengerInput.playTts(textContent, data.id);
      }
    },
    handleTtsEnded() {
      console.log('[Voice] TTS playback ended');
      // Keep voice mode active so subsequent responses also get TTS
      // Voice mode is deactivated when user types manually or leaves thread
    },
    closeComposeMenu() {
      this.compose_new = false;
      document.removeEventListener('click', this.closeComposeMenu);
    },
    clearThreadCreationError() {
      this.thread_creation_error = '';
    },
    // AutoVision-only: handler for the "New Thread" compose option. The option is only shown when
    // the wingmanConfig reports an InventoryVehicle / StockNumber page (see is_inventory_vehicle_page).
    // Closes the compose menu and routes to the New Thread participant picker (CreateThread.vue's
    // 'Threads' mode), mirroring the New Channel flow. The vehicle thread is private and invite-only,
    // so the creator picks the participants here; creation happens on confirm via createThread →
    // createVehicleThread. The page's vehicle context is snapshotted so creation uses the page the
    // user started from.
    composeNewThread() {
      this.closeComposeMenu();
      if (!this.is_inventory_vehicle_page) {
        this.messages_error = this.$t('messaging.window.errors.vehicleThreadPageOnly');
        return;
      }
      this.messages_error = '';
      this.thread_creation_error = '';
      this.pending_vehicle_thread_context = this.wingman_init;
      this.pending_vehicle_thread = true;
      this.fetchUsersForPopup();
      this.active_panel = null;
      this.internal_messaging_state = 'NewThread';
    },
    // Wrapper for the JoinOrCreateVehicleThread store action. Creates a private, invite-only vehicle
    // thread for the snapshotted page's vehicle and invites the picked participants. The server
    // resolves the stock number to a VIN, always creates a fresh thread, and notifies invitees. Opens
    // the thread on success; surfaces a message via messages_error on failure.
    createVehicleThread(participantIds = []) {
      const init = this.pending_vehicle_thread_context || this.wingman_init;
      if (!init || init.EntityType !== 'InventoryVehicle' || init.EntityIdType !== 'StockNumber' || !init.EntityId) {
        this.messages_error = this.$t('messaging.window.errors.vehicleThreadPageOnly');
        return;
      }
      if (this.is_creating_thread) return;
      const self = this;
      this.is_creating_thread = true;
      this.messages_error = '';
      this.thread_creation_error = '';
      const joinVehicleThreadDTO = {
        stockNumber: init.EntityId,
        dealerId: init.DealerId || this.current_cif,
        dealerGuid: this.dealer_guid,
        entityType: init.EntityType,
        entityIdType: init.EntityIdType,
        participantIds: participantIds || [],
      };
      this.mainStore.JoinOrCreateVehicleThread(joinVehicleThreadDTO).then(async response => {
        self.pending_vehicle_thread = false;
        self.pending_vehicle_thread_context = null;
        if (response && response.threadId) {
          self.selected_thread = response.threadId;
          await self.getObjectThreads(false);
          self.openThread(response.threadId);
        }
        else {
          self.thread_creation_error = self.$t('messaging.window.errors.createVehicleThread');
        }
        setTimeout(() => {
          self.is_creating_thread = false;
        }, 500);
      }, error => {
        self.is_creating_thread = false;
        console.error('CreateVehicleThread failed:', error);
        self.thread_creation_error = self.$t('messaging.window.errors.createVehicleThread');
      });
    },
    composeNew(composeType=null) {
      if(composeType===null) {
        if (!this.compose_new) {
          window.event_bus.$emit('close-options-menu');
          this.compose_new = true;
          this.$nextTick(() => document.addEventListener('click', this.closeComposeMenu));
        } else {
          this.closeComposeMenu();
        }
      }
      else {
        this.compose_new = false;
        this.internal_messaging_state = composeType;
        if (composeType === 'NewTacChat') {
          // Picker renders in the left column; reset active_panel so left_column_classes shows it
          // even if the user was previously viewing a thread.
          this.active_panel = null;
          this.selected_tac_team_key = null;
          this.tac_teams_error = '';
          this.tac_search_query = '';
          this.loadTacTeams();
        }
      }
    },
    // Bumps now_tick every 60s so tac_support_open re-evaluates across the 10am/4pm ET boundary
    // while the user lingers on the Support tab or an open TAC chat. Cleared in beforeDestroy.
    startSupportClock() {
      if (this.support_clock_interval) return;
      const self = this;
      this.support_clock_interval = setInterval(() => { self.now_tick = Date.now(); }, 60000);
    },
    stopSupportClock() {
      if (this.support_clock_interval) {
        clearInterval(this.support_clock_interval);
        this.support_clock_interval = null;
      }
    },
    // Mirrors CarlStreet.Core.Utils.TacSupportHours (keep in sync): TAC support is staffed
    // Mon–Fri, 10:00–16:00 America/New_York. Intl with an explicit timeZone yields Eastern
    // wall-clock regardless of the user's device timezone; the runtime handles DST.
    isWithinTacSupportHours(date) {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', weekday: 'short', hour: '2-digit', hour12: false,
      }).formatToParts(date);
      const weekday = parts.find(p => p.type === 'weekday')?.value;
      let hour = parseInt(parts.find(p => p.type === 'hour')?.value, 10);
      if (hour === 24) hour = 0; // some engines emit '24' for midnight with hour12:false
      const isWeekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(weekday);
      return isWeekday && hour >= 10 && hour < 16;
    },
    // Availability and after-hours copy always come from the server's team list, looked up by key.
    // Both lookups below go through here rather than trusting the row object they were rendered from:
    // the Recent rows are built from thread history (recent_tac_team_options) and carry neither field,
    // so reading them off the row would quietly fall back to the legacy window for exactly the teams a
    // user reaches for most.
    tacTeamServerRecord(teamKey) {
      if (!teamKey || !this.$FUSarrayHasEntries(this.tac_teams_list)) return null;
      return this.tac_teams_list.find(option => option.key === teamKey) || null;
    },
    // True when a TAC team can be reached right now. The server decides this per team (TAC Portal
    // publishes hours per support team, and SparkApi falls back to the legacy window when TAC is
    // unreachable), so we just read what it sent. `available` is missing only when talking to a server
    // that predates per-team hours, or before the team list has loaded — fall back to the client-side
    // window there rather than showing every team as open.
    tacTeamAvailable(teamKey) {
      if (!teamKey) return false;
      // A retired team can never be reached, so it short-circuits ahead of the legacy-window fallback
      // below. Without this it would read OPEN during 10am–4pm ET: a discontinued team is absent from
      // tac_teams_list, so `available` comes back undefined and the fallback would speak for it.
      if (this.tacTeamDiscontinued(teamKey)) return false;
      const available = this.tacTeamServerRecord(teamKey)?.available;
      if (typeof available === 'boolean') return available;
      return this.tac_support_open;
    },
    // True when a team the user has history with is no longer offered at all — TAC discontinued it, so
    // ReynoldsTacClient dropped it from the catalog and the server stopped listing it. Distinct from
    // closed, which is temporary; this one never reopens.
    //
    // Only the Recent list can reach this state: those rows are built from thread history
    // (recent_tac_team_options) and carry their own persisted TacChatTeamId/TacChatTeamName, so they
    // survive the team leaving the catalog. The All list is derived from tac_teams_list and so drops it
    // on its own.
    //
    // Guarded on the list having actually loaded — the same reason tac_picker_disabled is. Mid-load
    // tac_teams_list is empty, and without this guard EVERY Recent row would momentarily read as
    // discontinued and render disabled.
    tacTeamDiscontinued(teamKey) {
      if (!teamKey) return false;
      if (this.tac_teams_loading || !this.$FUSarrayHasEntries(this.tac_teams_list)) return false;
      return this.tacTeamServerRecord(teamKey) === null;
    },
    // The badge text for a non-selectable row. Both states dim the row identically, so the label is the
    // only thing telling the user whether to come back later ("Closed") or not at all.
    tacTeamRowBadge(teamKey) {
      return this.tacTeamDiscontinued(teamKey)
        ? this.$t('messaging.window.tac.discontinuedLabel')
        : this.$t('messaging.window.tac.closedLabel');
    },
    // Closed copy for a team. TAC can supply its own after_hours_message, but only in English — it does
    // no locale negotiation — so Spanish and French users get our localized string instead of being
    // dropped out of their language.
    tacTeamClosedMessage(teamKey) {
      const afterHoursMessage = this.tacTeamServerRecord(teamKey)?.afterHoursMessage;
      if (afterHoursMessage && this.language === 'en') return afterHoursMessage;
      return this.$t('messaging.window.tac.closedError', {
        hours: this.tac_support_hours_display,
        usPhone: this.tac_support_phone,
        caPhone: this.tac_support_phone_canada,
      });
    },
    loadTacTeams() {
      if (this.tac_teams_loading) return;
      this.tac_teams_loading = true;
      this.tac_teams_error = '';
      this.mainStore.GetTacTeams(this.current_cif).then(response => {
        this.tac_teams_list = response?.teams || [];
        this.tac_support_bypass_active = response?.supportBypassActive === true;
        // Keep the data() literals when the server omits these — an older server, not an empty answer.
        this.tac_support_hours_server = response?.supportHours || '';
        if (response?.supportPhoneUs) this.tac_support_phone = response.supportPhoneUs;
        if (response?.supportPhoneCanada) this.tac_support_phone_canada = response.supportPhoneCanada;
        this.tac_teams_loading = false;
      }, error => {
        console.error('GetTacTeams failed:', error);
        this.tac_teams_error = this.$t('messaging.window.errors.loadSupportTeams');
        this.tac_teams_loading = false;
      });
    },
    selectTacTeam(teamKey) {
      this.selected_tac_team_key = teamKey;
    },
    // Flips the local isClosed flag on a TAC thread when the close webhook signals it via the
    // WS-MESSAGE broadcast (tacChatData.isChatCloseMessage === true) or when the backend rejects
    // a send with FusionConstants.TacChatClosedSendRejected. The active_thread_is_closed_tac_chat
    // computed depends on this.active_thread?.isClosed, so flipping it here drives the input lock.
    // Idempotent — does nothing if the thread isn't loaded or is already closed.
    markTacThreadClosed(threadId) {
      if (!threadId || !this.$FUSarrayHasEntries(this.object_threads)) return;
      const idx = this.object_threads.findIndex(t => t.threadId === threadId);
      if (idx >= 0 && !this.object_threads[idx].isClosed)
        this.object_threads[idx].isClosed = true;
    },
    // Mirrors CarlStreet.Sdk.Fusion.Integration.TacChat.TacChatTeamAccessFilter.MapTeamKeyToMcpServerName.
    // All ERA-IGNITE sub-teams (era_ignite_*) gate behind a single EraIgniteMcp. Other product
    // teams have 1:1 mappings. Gubagoo maps to GubagooMcp which doesn't exist yet — it'll be
    // hidden until that MCP server ships, matching the backend behavior.
    tacTeamToMcpServer(teamKey) {
      if (!teamKey) return null;
      if (typeof teamKey === 'string' && teamKey.toLowerCase().startsWith('era_ignite_'))
        return 'eraignitemcp';
      switch ((teamKey || '').toLowerCase()) {
        case 'autovision':   return 'autovisionmcp';
        case 'reverse_risk': return 'reverseriskmcp';
        case 'recon_vision': return 'reconvisionmcp';
        case 'focus_crm':    return 'focusmcp';
        case 'gubagoo':      return 'gubagoomcp';
        default:             return null;
      }
    },
    userHasTacTeamAccess(teamKey) {
      const requiredMcp = this.tacTeamToMcpServer(teamKey);
      if (!requiredMcp) return false;
      return this.accessible_mcp_servers.has(requiredMcp);
    },
    backFromNewTacChat() {
      this.selected_tac_team_key = null;
      this.tac_teams_error = '';
      this.internal_messaging_state = null;
    },
    startTacChat() {
      if (!this.selected_tac_team_key || this.start_tac_chat_processing) return;
      // Defense-in-depth: the Start button is disabled for a closed team, but guard here too (the
      // backend re-checks that team's hours and will 403 a stale client).
      if (!this.selected_tac_team_available) {
        this.tac_teams_error = this.tacTeamClosedMessage(this.selected_tac_team_key);
        return;
      }
      this.start_tac_chat_processing = true;
      const payload = {
        teamKey: this.selected_tac_team_key,
        dealerId: this.current_cif,
        reyConversationId: this.ai_chat_thread_id || null,
        // So a 403 quotes this team's hours in the user's language — see StartTacChatRequest.Language.
        lang: this.language,
      };
      this.mainStore.StartTacChatFromMessaging(payload).then(async response => {
        this.start_tac_chat_processing = false;
        const newThreadId = response?.threadId;
        this.internal_messaging_state = null;
        this.selected_tac_team_key = null;
        if (newThreadId) {
          // Refresh object threads so the new TAC thread appears in support_threads_display,
          // then open it. Mirrors the createGroupThread → getUserThreads → openThread pattern.
          // Cosmos has eventual consistency — the just-created thread occasionally doesn't show up
          // on the first fetch. Retry once with a small delay before opening so active_thread can
          // resolve correctly; if it's still missing after the second fetch we open anyway and let
          // the websocket userJoinedThread event populate it.
          await this.getObjectThreads(false);
          if (!this.all_threads_by_thread_id?.[newThreadId]) {
            await new Promise(resolve => setTimeout(resolve, 500));
            await this.getObjectThreads(false);
          }
          this.openThread(newThreadId);
        }
      }, error => {
        this.start_tac_chat_processing = false;
        console.error('StartTacChat failed:', error);
        this.tac_teams_error = this.$t('messaging.window.errors.startChat');
      });
    },
    getAvatarColorForIndex(index) {
      const colorIndex = index % this.$FUSfusionAvatarColorsHex.length;
      const cycle = Math.floor(index / this.$FUSfusionAvatarColorsHex.length);
      const baseColor = this.$FUSfusionAvatarColorsHex[colorIndex];
      
      // Apply variation on subsequent cycles
      if(cycle === 0)
        return baseColor;
      else {
        // If color is being reused, change it slightly
        let hex = baseColor;
        hex = hex.replace('#', '');
        
        // Parse RGB values
        let r = parseInt(hex.substring(0, 2), 16);
        let g = parseInt(hex.substring(2, 4), 16);
        let b = parseInt(hex.substring(4, 6), 16);
        
        // Adjust brightness - alternate between lighter and darker
        const adjustment = cycle % 2 === 1 ? 30 : -30;
        r = Math.max(0, Math.min(255, r + adjustment * cycle));
        g = Math.max(0, Math.min(255, g + adjustment * cycle));
        b = Math.max(0, Math.min(255, b + adjustment * cycle));
        
        // Convert back to hex
        return '#' + [r, g, b].map(x => {
          const hex = x.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        }).join('');
      }
    },
  },
  watch: {
    internal_messaging_state(newValue, oldVal) {
      // Leaving the New Thread picker by any path (back button, cancel, or opening the created
      // thread) ends the pending vehicle-thread flow so the next compose starts clean.
      if(newValue !== oldVal && newValue !== 'NewThread' && this.pending_vehicle_thread) {
        this.pending_vehicle_thread = false;
        this.pending_vehicle_thread_context = null;
      }
      if(this.page_type === 'InternalMessaging' && newValue !== oldVal && newValue === null)
        this.backToMessages();
      // Returning to a thread view (e.g. backing out of Chat Details, the contact profile, or the
      // New Message screen — none of which actually close the still-open thread) must re-show the
      // conversation's name. The message_header watcher only fires when message_header CHANGES, so a
      // header_override cleared by an intervening back hop (NewMessage → contact profile clears it)
      // is never restored on its own when selected_thread hasn't changed. Re-sync it here.
      if(this.page_type === 'InternalMessaging' && newValue !== oldVal && this.selected_thread
         && ['UseMessage','UseChannel','UseThread'].indexOf(newValue) >= 0)
        this.header_override = this.message_header || null;
    },
    message_header(newValue, oldVal) {
      // Only InternalMessaging owns header_override — the Spark instance must not
      // overwrite it, or reopening the AI chat clears/replaces the thread title that
      // the InternalMessaging tab was correctly showing.
      if (this.page_type !== 'InternalMessaging') return;
      this.header_override = newValue || null;
    },
    current_cif(newVal) {
      this.context_cif = newVal || null;
      // The host page switched dealers — reset to its single-dealer scope so the visible
      // filter always reflects the page the user is looking at.
      this.context_group = null;
    },
    selected_thread(newVal) {
      // Mirror the open thread into the store so other subviews (e.g. the contact profile's Shared
      // Chats list) can recognize and disable the chat you're already in. A watcher covers every
      // path that changes selected_thread (open/back/leave/delete) from one place.
      //
      // Each instance into its own mirror. The toolbar mounts both, and with one shared field the
      // Spark instance and the Internal Messaging one overwrote each other — the Rey list then
      // highlighted whichever internal chat was open, and missed that a deleted conversation was the
      // one behind it. See active_thread_id and spark_active_thread_id in the store.
      if(this.page_type === 'Spark')
        this.spark_active_thread_id = newVal || null;
      else
        this.active_thread_id = newVal || null;
      // Remember it as where this user was, so their next open of the day comes back here. The
      // watcher for the same reason as above: every way into a conversation ends here — the drawer,
      // a brief landing, a fork, the first send of a draft — and wiring each of them individually
      // is how one of them gets missed.
      //
      // The placeholder is remembered too, and that is deliberate: a New Topic draft IS somewhere
      // the user was, and skipping it left the previous real thread in place — so clicking New Topic
      // and refreshing snapped them back into a conversation they had just left. landOnStartupThread
      // reads the zero GUID back as "reopen New Topic" rather than looking it up as a thread. The
      // first send replaces it with the real id through this same watcher.
      //
      // Spark only. The store no-ops when the value has not changed, so re-entering the remembered
      // thread (or starting a second draft from a draft) costs nothing.
      //
      // And only where the open thread is the user's choice: wherever FusionWeb owns it
      // (host_supports_rey_conversations), or on a page nothing frames. A blade host that still re-pins
      // its own thread id — a WingCommander extension from before ?rey_conversations=1 — opens that pin
      // on every load, and remembering it wrote the host's thread over the user's own position, so the
      // next standalone or toolbar open came back to the extension's thread instead of theirs.
      if(this.page_type === 'Spark' && newVal && (this.host_supports_rey_conversations || !this.is_embedded))
        this.rememberReyLastThread(newVal);
      // Per-thread, like the group and dealer scopes below: leaving the Scheduled tasks thread with
      // its older results unfolded and coming back should find them folded again, not half-open from
      // a visit the user has stopped thinking about.
      this.earlier_expanded = false;
      // The header's counts, fetched only when that thread is actually opened — a user who never
      // opens it should not pay for the request, and the counts go stale as soon as a task is paused
      // somewhere else anyway.
      if (this.active_thread_is_scheduled) this.loadScheduledTaskCounts();
      // The two scope resets below are skipped for the one switch that is not a move to another
      // conversation: a New Topic's first send turning the draft into this thread
      // (materializeDraftConversation). The scope picked on the draft is the one the question is being
      // asked under, and the thread has no turns yet for it to disagree with. Cleared on every run, so
      // it only ever covers the switch it was set for.
      const keepDraftScope = !!newVal && newVal === this.keep_scope_for_thread_id;
      this.keep_scope_for_thread_id = null;
      if (keepDraftScope) return;
      // A group selection is per-thread intent: without this, "All rooftops" picked in one thread
      // follows the user into the next and silently sends DealerType='Group' — a multi-store
      // fan-out they never asked for there (PR #976 review). Same reasoning as the current_cif
      // watcher above: reset to the single-dealer scope the new thread's context implies.
      this.context_group = null;
      // The single-dealer scope was bleeding the same way, and costing more than a wrong label when
      // it did. A conversation's persisted history is keyed by the dealership its turns were stamped
      // with (ConversationRepository.GetConversationHistoryAsync narrows the window to that
      // dealership and excludes unstamped rows), and the thread id does not change when the dealer
      // does. So carrying the PREVIOUS conversation's dealer into this one does not merely mislabel
      // the picker — it stamps the next turn with a dealership this conversation has never used,
      // which makes its own prior turns invisible to the model and answers out of the other store's
      // data. Restoring the conversation's own dealer is what puts the thread and its history scope
      // back in agreement.
      this.context_cif = this.threadDealerCif(newVal) || this.current_cif || null;
    },
    internal_messages_unread_count(newCount) {
      // Bridge for the embedded (Chrome-extension) host, which renders the Internal Messaging unread
      // count on its native tab icon. Only emit when actually embedded — standalone (top-level)
      // FusionWeb shows its own in-app pill, and emitting would drive a duplicate native badge via
      // the extension content script that also injects on the standalone page. Only the
      // InternalMessaging surface emits — the Spark surface's store may not have internal threads
      // loaded, so it would post a spurious 0 and race with the real count. The host treats this
      // numeric count as authoritative over the legacy boolean dot.
      if (!this.is_embedded || this.page_type === 'Spark') return;
      this.$FUSpostFusionMessage('INTERNAL-MESSAGES-COUNT', newCount || 0);
    },
    spark_view(view) {
      // Back from the conversation list, by a row or by the hamburger, to the chat that stayed open behind
      // it. Nothing reloads on that trip, so a message that arrived meanwhile can sit below the fold where
      // the seen observer never reports it, and its unread dot outlives the visit. Jump to the newest only
      // when something is unread, so the reading position otherwise survives the trip as
      // Views/Messaging.vue intends. setTimeout, like the other scrolls here: the chat is still hidden
      // until this change renders.
      if (this.page_type !== 'Spark' || view !== 'Chat') return;
      if ((this.unreads_by_thread_id[this.selected_thread] || 0) > 0)
        setTimeout(() => this.scrollToLastMessageIfClose(true, false), 50);
    },
  }
}
</script>
